/**
 * Pricing API
 * Version: 1.0
 * Build: Manual
 */

import {
	CountInfo,
	FieldInfo,
	File,
	DataValidationError,
	Comment,
	ListInfo,
	AuditInfo,
	MonetaryAmount,
	ClassRate,
	AcctId,
	Code,
	CrmAccount,
	CrmAccountOwner,
	Address,
	Weight,
	Distance,
	Volume,
	EmployeeId,
	PartyBasic,
	Density,
	Document,
	Dimensions,
	AccessorialOverride,
	NmfcItemOverride,
	ContactInfo,
	TransitTimeDirectionTypeCd
} from '@xpo-ltl/sdk-common';

export class CreateSalesRfpAutoGriRqst {
	/**
	 * GRI information
	 * Required: true
	 * Array: false
	 */
	gri:SalesRfpGri = undefined;
}
export class CreateSalesRfpAutoGriResp {
	/**
	 * GRI information
	 * Required: false
	 * Array: false
	 */
	gri:SalesRfpGri = undefined;
}
/**
 * This request will create a new SalesRFP by providing the RFP header information
 */
export class CreateSalesRfpRqst {
	/**
	 * Key Sales RFP information
	 * Required: false
	 * Array: false
	 */
	salesRfp:SalesRfp = undefined;
	/**
	 * Sales Rfp Id of the source from where to copy the sales rfp details
	 * Required: false
	 * Array: false
	 */
	sourceSalesRfpId:string = undefined;
	/**
	 * source from where to copy the sales rfp account location information. Eg. PA (Pricing Agreement), RFP(Sales Rfp) or ALL
	 * Required: false
	 * Array: false
	 */
	salesRfpAcctSource:string = undefined;
}
/**
 * Contains basic information related to a place where our customer does business with us. 
 */
export class CreateSalesRfpResp {
	/**
	 * The database identifier of this SalesRFP
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Request message to reassign sales rfps from one user to another
 */
export class ReassignSalesRfpRqst {
	/**
	 * List of Sales Rfp Ids to be assigned to new owner
	 * Required: true
	 * Array: true
	 */
	rfpOwner:RfpOwner[] = undefined;
	/**
	 * This is the from user role  (Sales/Pricing Analyst/Nass/Dos/PricingManger) to transfer
	 * Required: false
	 * Array: false
	 */
	fromOwnerRole:string = undefined;
	/**
	 * Employee id of the new owner of the Sales Rfps
	 * Required: true
	 * Array: false
	 */
	toOwner:string = undefined;
	/**
	 * Employee id of the new Sales Agent of the Sales Rfps
	 * Required: false
	 * Array: false
	 */
	toSalesAgentId:string = undefined;
}
export class ReassignSalesRfpResp {
	/**
	 * Count of the Sales Rfps reassigned
	 * Required: false
	 * Array: false
	 */
	reassignedCount:CountInfo = undefined;
}
/**
 * Gets the unmapped shipment file from sales rfp shipment file that is stored in DMS
 */
export class GetUnmappedSupportingDocRqst {
	/**
	 * unique id of the Sales Rfp Supporting document
	 * Required: true
	 * Array: false
	 */
	rfpSupportingDocId:string = undefined;
	/**
	 * unique id of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
export class GetUnmappedSupportingDocResp {
	/**
	 * Required: false
	 * Array: true
	 */
	fields:FieldInfo[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	records:Record[] = undefined;
}
/**
 * The service will create a csv file with data from various Sales Rfp tables to create an audit file that will be downloaded by Sales force for auditing the Sales Rfps.
 */
export class GetSalesRfpAuditFileResp {
	/**
	 * Required: false
	 * Array: false
	 */
	rfpAuditFile:File = undefined;
}
/**
 * Request message to valdate a Sales Rfp before submitting to Pricing
 */
export class ValidateSalesRfpRqst {
	/**
	 * Unique Identifier of Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Response 
 */
export class ValidateSalesRfpResp {
	/**
	 * list of warning messages
	 * Required: false
	 * Array: true
	 */
	warningMsgs:DataValidationError[] = undefined;
	/**
	 * list of error messages
	 * Required: false
	 * Array: true
	 */
	errorMsgs:DataValidationError[] = undefined;
}
/**
 * Get sales rfp counts that meet certain criteria.
 */
export class GetSalesRfpCountsRqst {
	/**
	 * identifies the level of detail to be included in the response counts
	 * Required: false
	 * Array: false
	 */
	levelOfDetail:string = undefined;
	/**
	 * Identifier of the Customer with whom the agreement is being or was negotiated. This will uniquely identify the customer in the CIS component.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustInstId:string = undefined;
	/**
	 * This is the business key at the time of negotiation for this customer. Note that it will not be kept in synch with the master customer database.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustMadCd:string = undefined;
	/**
	 * The date the Sales RFP is due from Pricing back to Sales
	 * Required: false
	 * Array: false
	 */
	dueDate:Date = undefined;
	/**
	 * Requested Effective Date for the Agreement if the request is approved.
	 * Required: false
	 * Array: false
	 */
	proposedEffDate:Date = undefined;
	/**
	 * Proposed Expiration Date for the Agreement if the request is approved.
	 * Required: false
	 * Array: false
	 */
	proposedExpDate:Date = undefined;
	/**
	 * The ID of the Pricing Analyst
	 * Required: false
	 * Array: false
	 */
	pricingAnalystId:string = undefined;
	/**
	 * The ID of the owner of sales RFP.
	 * Required: false
	 * Array: false
	 */
	ownerId:string = undefined;
	/**
	 * The ID of the owner of sales RFP, the owner transfers to the party that is actively working on the RFP.
	 * Required: false
	 * Array: false
	 */
	currentOwnerId:string = undefined;
	/**
	 * Start date  to filter the Sales RFP
	 * Required: false
	 * Array: false
	 */
	startDate:Date = undefined;
	/**
	 * End date to filter the Sales RFP
	 * Required: false
	 * Array: false
	 */
	endDate:Date = undefined;
	/**
	 * Sales Region code of the Sales RFP
	 * Required: false
	 * Array: false
	 */
	salesRegionCd:string = undefined;
}
/**
 * Gets sales rfp counts that meet certain criteria.
 */
export class GetSalesRfpCountsResp {
	/**
	 * Pricing Agreement Monitor record counts
	 * Required: false
	 * Array: true
	 */
	counts:CountInfo[] = undefined;
}
/**
 * Updates the previous agreement details of a given sales rfp
 */
export class UpdateSalesRfpAgreementDtlRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique identifier of the proposal
	 * Required: true
	 * Array: false
	 */
	proposalId:string = undefined;
}
/**
 * Gets sales rfp counts that meet certain criteria.
 */
export class UpdateSalesRfpAgreementDtlResp {
	/**
	 * Pricing Agreement Monitor record counts
	 * Required: false
	 * Array: false
	 */
	salesRfpDetail:SalesRfpHdr = undefined;
}
/**
 * The operation creates the Shipment rfp supporting document from dms and then maps the field names and records from the document to return the records.
 */
export class CreateSalesRfpShipmentSupportingDocsRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique identifier of the supporting document
	 * Required: true
	 * Array: false
	 */
	rfpSupportingDocId:string = undefined;
	/**
	 * List of column fields to be mapped
	 * Required: true
	 * Array: true
	 */
	fieldMapping:FieldInfo[] = undefined;
}
export class DeleteSalesRfpCommentsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	commentId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	commentTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	commentSubTypeCd:string = undefined;
}
/**
 * This request will create a Sales Rfp comment for an input Sales Rfp Id
 */
export class CreateSalesRfpCommentsRqst {
	/**
	 * unique identifier for Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique identifier for Pricing Proposal
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	comment:Comment = undefined;
}
export class CreateSalesRfpCommentsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique identifier of the comment created.
	 * Required: false
	 * Array: false
	 */
	commentId:string = undefined;
}
/**
 * Retrieves all the comments associated to a Sales Rfp
 */
export class ListSalesRfpCommentsRqst {
	/**
	 * unique id of the sales rfp record
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique identifier of Pricing Proposal
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	commentTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	commentSubTypeCd:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListSalesRfpCommentsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	comments:SalesRfpComment[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieves all the comments associated to a Pricing Proposal
 */
export class ListPricingProposalCommentsRqst {
	/**
	 * unique id of the pricing proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	commentTypeCd:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingProposalCommentsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	comments:SalesRfpComment[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * This request will update an existing Sales Rfp comment for an input Sales Rfp Id
 */
export class UpdateSalesRfpCommentsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	commentId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	comment:Comment = undefined;
}
export class CreateSalesRfpAuditLogResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
export class ListSalesRfpAuditLogsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	auditLogDetails:RfpAuditLog[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to retrieve all the audit log information for a given sales rfp
 */
export class ListSalesRfpAuditLogsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message for creating a Sales Rfp Audit log record for an input SalesRfpId
 */
export class CreateSalesRfpAuditLogRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	auditLog:RfpAuditLog[] = undefined;
}
/**
 * This request will update an existing Sales Rfp. Updates include create/update/delete of children records.
 */
export class UpdateSalesRfpRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfp:SalesRfp = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Updates the Status of a given sales rfp
 */
export class UpdateSalesRfpStatusRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Status code of the Sales Rfp to be updated
	 * Required: true
	 * Array: false
	 */
	rfpStatusCd:string = undefined;
	/**
	 * Rfp header data to be updated
	 * Required: false
	 * Array: false
	 */
	salesRfpHdr:SalesRfpHdr = undefined;
}
/**
 * Creates Sales Rfp Lanes for input Sales Rfp Id with input Lane data
 */
export class CreateSalesRfpLanesRqst {
	/**
	 * The database identifier of this SalesRFP
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	salesRfpLanes:PricingLane[] = undefined;
}
export class CreateSalesRfpLanesResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * List sales rfp requests that meet certain criteria.
 */
export class ListSalesRfpAcctsRqst {
	/**
	 * The ID of a sales RFP
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique id of the parent account
	 * Required: false
	 * Array: false
	 */
	parentAcctInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List sales rfp requests that meet certain criteria. Returns 1 or more sales rfp headers
 */
export class ListSalesRfpAcctsResp {
	/**
	 * The ID of a customer associated with the RFP
	 * Required: false
	 * Array: true
	 */
	rfpAccounts:RfpAcct[] = undefined;
	/**
	 * The total number of rows in the list.
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Create references in the SalesRFP for the customer locations associated with it.
 */
export class CreateSalesRfpAcctsRqst {
	/**
	 * The ID of a sales RFP
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * The sales RFP Account references
	 * Required: true
	 * Array: true
	 */
	salesRfpAccts:RfpAcct[] = undefined;
}
/**
 * The primary keys for the sales rfp 
 */
export class CreateSalesRfpAcctsResp {
	/**
	 * The database identifier of this SalesRFP
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Deletes Sales Rfp Accounts rulesets for input Sales Rfp Id
 */
export class DeleteSalesRfpAcctsRulesetRqst {
	/**
	 * Unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
export class DeleteSalesRfpAcctsRulesetResp {
	/**
	 * Unique identifier of sales rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Retrieves a list of Rulesets for each of the Account associated with input sales rfp
 */
export class ListSalesRfpAcctsRulesetRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:string = undefined;
}
export class ListSalesRfpAcctsRulesetResp {
	/**
	 * Required: false
	 * Array: true
	 */
	rfpAcctRuleset:RfpAcctRuleset[] = undefined;
	/**
	 * The total number of rows in the list.
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Creates the rulesets for each of the accounts associated to the input Sales RFP id
 */
export class CreateSalesRfpAcctsRulesetRqst {
	/**
	 * Unqiue identifier of Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	agreementDetail:PricingAgreement = undefined;
}
/**
 * Creates the rulesets for each of the accounts associated to the input Sales RFP id
 */
export class CreateSalesRfpAcctsRulesetResp {
	/**
	 * Unqiue identifier of Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique identifier of the Rfp Audit Header record
	 * Required: false
	 * Array: false
	 */
	rfpAuditHeaderId:string = undefined;
}
/**
 * Request Message to update the sales rfp audit header
 */
export class UpdateSalesRfpAuditHeaderRqst {
	/**
	 * Unique identifier of sales rfp record
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Unique identifier of the Sales Rfp Audit Header record
	 * Required: true
	 * Array: false
	 */
	rfpAuditHeaderId:string = undefined;
	/**
	 * Valid values are true and false. If not provided then no email notification will be sent.
	 * Required: false
	 * Array: false
	 */
	emailNotify:boolean = undefined;
	/**
	 * Rfp Audit Header data to be updated
	 * Required: true
	 * Array: false
	 */
	rfpAuditHeader:SalesRfpAuditHeader = undefined;
}
/**
 * Request Message to get the sales rfp audit header
 */
export class GetSalesRfpAuditHeaderRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Respond Message for sales rfp audit header
 */
export class GetSalesRfpAuditHeaderResp {
	/**
	 * Required: false
	 * Array: false
	 */
	rfpAuditHeader:SalesRfpAuditHeader = undefined;
}
/**
 * Creates Contact details for a given sales rfp
 */
export class CreateSalesRfpContactDetailsRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	rfpContactDetails:SalesRfpContact = undefined;
}
/**
 * Creates Contact details for a given sales rfp
 */
export class CreateSalesRfpContactDetailsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	rfpContactDetails:SalesRfpContact = undefined;
}
/**
 * Updates Contact details for a given sales rfp and Rfp Contact id
 */
export class UpdateSalesRfpContactDetailsRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique identifier of Sales Rfp Contact
	 * Required: true
	 * Array: false
	 */
	rfpContactId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	rfpContactDetails:SalesRfpContact = undefined;
}
/**
 * Retrieves Contact details for a given sales rfp
 */
export class GetSalesRfpContactDetailsRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
export class GetSalesRfpContactDetailsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	rfpContactDetails:SalesRfpContact = undefined;
}
/**
 * Print Cancel Pricing Agreement Letter Rqst
 */
export class PrintCancelPricingAgreementLetterRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: true
	 */
	salesRfpId:string[] = undefined;
	/**
	 * unique identifier of contactrfp
	 * Required: false
	 * Array: true
	 */
	contactId:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	actionType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	docId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	letterDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	noticeDate:Date = undefined;
}
/**
 * Print Cancel Pricing Agreement Letter response
 */
export class PrintCancelPricingAgreementLetterResp {
	/**
	 * Required: true
	 * Array: false
	 */
	cancelPricingAgreementLetter:File = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	docId:string = undefined;
}
/**
 * Deletes one or all Sales Rfp Support Documents. If Sales Rfp Id is input then all supporting documents can be deleted if a supporting document id is input then 1 document is deleted
 */
export class DeleteSalesRfpSupportingDocsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rfpSupportingDocId:string = undefined;
}
/**
 * Request message to retrieve a list of Sales Rfp Support documents based on input Sales Rfp Id
 */
export class ListSalesRfpSupportingDocsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message to retrieve a list of Sales Rfp Support documents based on input Sales Rfp Id
 */
export class ListSalesRfpSupportingDocsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	supportingDocs:ListSalesRfpSupportingDocsResp_SalesRfpSupportingDocument[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to retrieve a l Sales Rfp Support document based on input Sales Rfp Id and document id
 */
export class GetSalesRfpSupportingDocRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	rfpSupportingDocId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Response message to retrieve a list of Sales Rfp Support documents based on input Sales Rfp Id
 */
export class GetSalesRfpSupportingDocResp {
	/**
	 * Required: false
	 * Array: false
	 */
	supportingDoc:SalesRfpSupportingDocument = undefined;
}
/**
 * Adds Sales Rfp support documents to DMS and inserts rows in Sales Rfp Supporting documents
 */
export class CreateSalesRfpSupportingDocsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * indicates if the document is restricted or not
	 * Required: true
	 * Array: true
	 */
	restrictedInd:boolean[] = undefined;
	/**
	 * type of document to be uploaded
	 * Required: true
	 * Array: true
	 */
	docTypeCd:string[] = undefined;
	/**
	 * list of files to be uploaded
	 * Required: true
	 * Array: true
	 */
	docFiles:File[] = undefined;
}
export class CreateSalesRfpSupportingDocsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	rfpDocument:SalesRfpSupportingDocument[] = undefined;
}
/**
 * Retrieves the Document file for input Sales Rfp Identifier and the supporting doc identifier
 */
export class GetSalesRfpSupportingDocFileRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	rfpSupportingDocId:string = undefined;
}
export class GetSalesRfpSupportingDocFileResp {
	/**
	 * Required: false
	 * Array: false
	 */
	supportingDocFile:File = undefined;
}
/**
 * Retrieves a zip file containing multiple Document files for input Sales Rfp Identifier and the supporting doc identifiers
 */
export class GetSalesRfpSupportingDocsFileRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	rfpSupportingDocId:string[] = undefined;
}
export class GetSalesRfpSupportingDocsFileResp {
	/**
	 * Required: false
	 * Array: false
	 */
	supportingDocsFile:File = undefined;
}
/**
 * Inserts the Geo Pricing Lanes for a given Sales Rfp and Geo Pricing Group
 */
export class CreateSalesRfpGeoAreaPricingLanesRqst {
	/**
	 * unique id of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	geoAreaPricingLanes:GeoAreaPricingLane[] = undefined;
}
/**
 * Inserts the Geo Pricing Lanes for a given Sales Rfp and Geo Pricing Group
 */
export class CreateSalesRfpGeoAreaPricingLanesResp {
	/**
	 * unique id of the Pricing lane id
	 * Required: true
	 * Array: false
	 */
	geoAreaPrcLaneId:string = undefined;
}
/**
 * Updates the Geo Pricing Lanes for a given Sales Rfp and Geo Pricing Group
 */
export class UpdateSalesRfpGeoAreaPricingLanesRqst {
	/**
	 * unique id of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique id of Geo Area Pricing Lane record
	 * Required: true
	 * Array: false
	 */
	geoAreaPrcLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoAreaPricingLanes:GeoAreaPricingLane = undefined;
}
/**
 * Retrieves the Geo Pricing Lanes for a given Sales Rfp and Geo Pricing Group
 */
export class ListSalesRfpGeoAreaPricingLanesRqst {
	/**
	 * unique id of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieves the Geo Pricing Lanes for a given Sales Rfp and Geo Pricing Group
 */
export class ListSalesRfpGeoAreaPricingLanesResp {
	/**
	 * unique id of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	geoAreaPricingLanes:GeoAreaPricingLane[] = undefined;
	/**
	 * pagination information for list retrieval
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Deletes the Geo Pricing Lanes for a given Sales Rfp and Geo Pricing Group
 */
export class DeleteSalesRfpGeoAreaPricingLanesRqst {
	/**
	 * unique id of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique id of geo area pricing lane record
	 * Required: false
	 * Array: false
	 */
	geoAreaPrcLaneId:string = undefined;
}
/**
 * Creates the Sales Rfp Obi plan data for a given Sales Rfp id.
 */
export class CreateSalesRfpObiPlansRqst {
	/**
	 * unique id of Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	obiPlans:RfpObi[] = undefined;
}
/**
 * Creates the Sales Rfp Obi plan data for a given Sales Rfp id.
 */
export class CreateSalesRfpObiPlansResp {
	/**
	 * unique id of Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	obiPlans:RfpObi[] = undefined;
}
/**
 * Deletes one or all the Sales Rfp Obi plan data for a given Sales Rfp id
 */
export class DeleteSalesRfpObiPlansRqst {
	/**
	 * unique id of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique id of the sales rfp obi plan
	 * Required: false
	 * Array: false
	 */
	rfpObiPlanId:string = undefined;
}
/**
 * Retrieves the Sales Rfp Obi plan data for a given Sales Rfp id
 */
export class ListSalesRfpObiPlansRqst {
	/**
	 * unique id of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Updates the Sales Rfp Obi plan data for a given Sales Rfp id and Obi plan id
 */
export class ListSalesRfpObiPlansResp {
	/**
	 * unique id of Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	obiPlans:RfpObi[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Updates the Sales Rfp Obi plan data for a given Sales Rfp id and Obi plan id
 */
export class UpdateSalesRfpObiPlansRqst {
	/**
	 * unique id of Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique id of the sales rfp obi plan
	 * Required: true
	 * Array: false
	 */
	rfpObiPlanId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	obiPlan:RfpObi = undefined;
}
/**
 * Updates the Sales Rfp Obi plan data for a given Sales Rfp id and Obi plan id
 */
export class UpdateSalesRfpObiPlansResp {
	/**
	 * unique id of Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	obiPlan:RfpObi = undefined;
}
/**
 * Creates sales rfp commodity record for a given sales rfp
 */
export class CreateSalesRfpCommodityRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Sales Rfp Commodity details
	 * Required: true
	 * Array: true
	 */
	rfpCommodity:PricingCommodity[] = undefined;
}
/**
 * Creates sales rfp commodity record for a given sales rfp
 */
export class CreateSalesRfpCommodityResp {
	/**
	 * unique identifier of sales rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Retrieves a list of sales rfp commodities for a given sales rfp
 */
export class ListSalesRfpCommoditiesRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListSalesRfpCommoditiesResp {
	/**
	 * unique identifier of sales rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * sales rfp commodity detail
	 * Required: false
	 * Array: true
	 */
	rfpCommodity:PricingCommodity[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Given a Sales Rfp Identifier the service will create Lading Dimensions record based on input data
 */
export class CreateSalesRfpLadingDimensionsRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	rfpLadingDimensions:PricingLadingDimension[] = undefined;
}
export class CreateSalesRfpLadingDimensionsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Retrieves a list of Lading Dimensions for an input Sales Rfp Id
 */
export class ListSalesRfpLadingDimensionsRqst {
	/**
	 * Unique identifier of Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListSalesRfpLadingDimensionsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	rfpLadingDimensions:PricingLadingDimension[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Creates Sales Rfp Pricing Analysis for a given Sales Rfp
 */
export class CreateSalesRfpAgrmntPerformanceRqst {
	/**
	 * unique identifier of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Pricing Analysis data to be persisted
	 * Required: true
	 * Array: false
	 */
	rfpAgrmntPerformance:RfpAgrmntPerformance = undefined;
}
export class CreateSalesRfpAgrmntPerformanceResp {
	/**
	 * unique identifier of the Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rfpAgrmntPerformance:RfpAgrmntPerformance = undefined;
}
/**
 * Updates Sales Rfp Pricing Analysis data for a given Sales Rfp and Pricing Analysis
 */
export class UpdateSalesRfpAgrmntPerformanceRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique identifier of Sales Rfp Pricing Analysis
	 * Required: true
	 * Array: false
	 */
	rfpRequestAnalysisId:string = undefined;
	/**
	 * Details about the Pricing Analysis
	 * Required: true
	 * Array: false
	 */
	agrmntPeformanceDetail:RfpAgrmntPerformance = undefined;
}
/**
 * Retrieves Sales Rfp Pricing Analysis for a given Sales Rfp and optional Pricing Analysis Id
 */
export class GetSalesRfpAgrmntPerformanceRqst {
	/**
	 * unique identifier of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
export class GetSalesRfpAgrmntPerformanceResp {
	/**
	 * Pricing Analysis data to be persisted
	 * Required: true
	 * Array: false
	 */
	rfpAgrmntPeformance:RfpAgrmntPerformance = undefined;
}
/**
 * Deletes Sales Rfp Pricing Analysis for a given Sales Rfp and optional Pricing Analysis Id
 */
export class DeleteSalesRfpAgrmntPerformanceRqst {
	/**
	 * unique identifier of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Retrieve the Pricing Analyst assigned to the Sales Rfp
 */
export class GetPricingAnalystAssignmentRqst {
	/**
	 * Pricing Analyst Assignment Id
	 * Required: true
	 * Array: false
	 */
	analystAssignmentId:string = undefined;
}
export class GetPricingAnalystAssignmentResp {
	/**
	 * Details of the Pricing Analyst assigned to the Sales Rfp
	 * Required: false
	 * Array: false
	 */
	assignedAnalyst:PricingAnalystAssignment = undefined;
}
/**
 * Assigns a PricingAnalyst to a given AE/SAM,  District and Region
 */
export class CreatePricingAnalystAssignmentRqst {
	/**
	 * Required: true
	 * Array: true
	 */
	pricingAnalystAssignments:PricingAnalystAssignment[] = undefined;
}
export class CreatePricingAnalystAssignmentResp {
	/**
	 * Required: true
	 * Array: true
	 */
	pricingAnalystAssignments:PricingAnalystAssignment[] = undefined;
}
/**
 * Retrieves a list of Pricing Analyst Assignments based on input criteria
 */
export class ListPricingAnalystAssignmentsRqst {
	/**
	 * Identifies the type of assignment for the pricing analyst
	 * Required: false
	 * Array: false
	 */
	assignmentTypeCd:string = undefined;
	/**
	 * The employee ID of the Account Executive (Local or National)
	 * Required: false
	 * Array: false
	 */
	acctExecEmployeeId:string = undefined;
	/**
	 * The employee ID of the National Account Sales Supervisor
	 * Required: false
	 * Array: false
	 */
	nasSupervisorEmployeeId:string = undefined;
	/**
	 * The employee ID of the National Account Sales Specialist
	 * Required: false
	 * Array: false
	 */
	nasSpecialistEmployeeId:string = undefined;
	/**
	 * A geographical region (now called District) for which an account executive is responsible of (i.e. RCA)
	 * Required: false
	 * Array: false
	 */
	salesRegionCode:string = undefined;
	/**
	 * A geographical area for a location (now called Region) (i.e. CCA)
	 * Required: false
	 * Array: false
	 */
	areaCode:string = undefined;
	/**
	 * The type of pricing agreement (i.e. P360, In-Cycle)
	 * Required: false
	 * Array: false
	 */
	agreementTypeCode:string = undefined;
	/**
	 * The type of request for pricing (i.e. Renegotiation, New Opportunity)
	 * Required: false
	 * Array: false
	 */
	rfpTypeCode:string = undefined;
	/**
	 * The date the assignment record was effective
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * The date when the assignment record expired
	 * Required: false
	 * Array: false
	 */
	expirationDate:Date = undefined;
	/**
	 * Pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAnalystAssignmentsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingAnalystAssignments:PricingAnalystAssignment[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Updates the PricingAnalystAssignment record for an input PricingAnalystAssignment Id 
 */
export class UpdatePricingAnalystAssignmentRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	analystAssignmentId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	analystAssignmentDetail:PricingAnalystAssignment = undefined;
}
/**
 * Deletes a PricingAnalystAssignment record for an input PricingAnalystAssignmentId
 */
export class DeletePricingAnalystAssignmentRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	analystAssignmentId:string = undefined;
}
/**
 * Inserts or updates Pricing Analysis Header record based on input data
 */
export class CreatePricingProposalHeaderRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingProposalHdrInfo:PricingProposalHdr = undefined;
	/**
	 * Indicator to trigger the Pricing Proposal Analysis Flow
	 * Required: false
	 * Array: false
	 */
	triggerProposalAnlysFlow:boolean = undefined;
	/**
	 * unique id of the Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Returns Pricing Proposal header identifier
 */
export class CreatePricingProposalHeaderResp {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingProposalInstId:string = undefined;
}
/**
 * Inserts or updates Pricing Analysis Header record based on input data
 */
export class UpdatePricingProposalHeaderRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingProposalHdrInfo:PricingProposalHdr = undefined;
}
export class UpdatePricingProposalRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Override indicator for update
	 * Required: false
	 * Array: false
	 */
	overrideInd:boolean = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	pricingProposal:PricingProposal = undefined;
}
/**
 * Input message for getting the Pricing Analyst detail
 */
export class GetPricingAnalystDetailsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	pricingAnalystEmplId:string = undefined;
}
/**
 * Pricing Analyst Details
 */
export class GetPricingAnalystDetailsResp {
	/**
	 * Required: true
	 * Array: false
	 */
	pricingAnalystDetails:PricingAdminPersonnel = undefined;
}
/**
 * Input message for getting the list of Pricing Analysts
 */
export class ListPricingAnalystsRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	managerEmplId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	employeeRoleType:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of Pricing Analysts matching the criteria
 */
export class ListPricingAnalystsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingAnalysts:PricingAdminPersonnel[] = undefined;
}
/**
 * Inserts Pricing Proposal Accessorial records for a given Pricing Proposal Id
 */
export class CreatePricingProposalAccessorialRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	accessorialInfo:PricingAccessorial[] = undefined;
}
/**
 * Returns Pricing Proposal header identifier
 */
export class CreatePricingProposalAccessorialResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Creates the Pricing Proposal Geo Pricing records for an input Pricing Proposal Id
 */
export class CreatePricingProposalGeoPricingRqst {
	/**
	 * unique identifier of Pricing Proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	geoPricingGroup:GeoAreaPricingGroup[] = undefined;
}
/**
 * Creates the Pricing Proposal Geo Pricing records for an input Pricing Proposal Id
 */
export class CreatePricingProposalGeoPricingResp {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	geoPricingGroup:GeoAreaPricingGroup[] = undefined;
}
/**
 * Updates the Pricing Proposal Geo Pricing records for an input Pricing Proposal Id and Geo Pricing Id
 */
export class UpdatePricingProposalGeoPricingRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	geoPricingGroup:GeoAreaPricingGroup[] = undefined;
}
/**
 * Updates the Pricing Proposal Geo Pricing records for an input Pricing Proposal Id and Geo Pricing Id
 */
export class UpdatePricingProposalGeoPricingResp {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	geoPricingGroup:GeoAreaPricingGroup[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Retrieves a list of Pricing Proposal Geo Pricing records for an input Pricing Proposal Id
 */
export class ListPricingProposalGeoPricingRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieves a list of Pricing Proposal Geo Pricing records for an input Pricing Proposal Id
 */
export class ListPricingProposalGeoPricingResp {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	geoPricings:GeoAreaPricingGroup[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Deletes on or all Pricing Proposal Geo Pricing records for an input Pricing Proposal Id and optional Geo Pricing Id
 */
export class DeletePricingProposalGeoPricingRqst {
	/**
	 * unique identifier of Pricing Proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of GeoAreaPricing group record
	 * Required: false
	 * Array: false
	 */
	geoAreaPrcId:string = undefined;
}
export class CreateSalesRfpFileRequestRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	rfpFileRequest:RfpFileRequest = undefined;
}
export class SubmitTransitTimeFileRequestRqst {
	/**
	 * Email address of person making the request.
	 * Required: true
	 * Array: false
	 */
	requestorEmailId:string = undefined;
	/**
	 * Valid Values: Inbound, Outbound, Both
	 * Required: false
	 * Array: false
	 */
	directionCode:TransitTimeDirectionTypeCd = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	postalCode:string[] = undefined;
	/**
	 * CountryCode: US or CA. By Default - US
	 * Required: false
	 * Array: false
	 */
	countryCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fileFormat:string = undefined;
}
export class SubmitTransitTimeFileRequestResp {
	/**
	 * Response message
	 * Required: false
	 * Array: false
	 */
	message:string = undefined;
}
/**
 * Creates discounts for the Geo Pricing Lanes baesd on the input data.
 */
export class CreateGeoPricingLaneDiscountsRqst {
	/**
	 * unique db identifier of the Geo Pricing Lane record. Discounts are associated with a Geo Pricing Lane
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	discountDetails:ProposalDiscount[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Response message of CreateGeoPricingLaneDiscounts
 */
export class CreateGeoPricingLaneDiscountsResp {
	/**
	 * unique db identifier of the Geo Pricing Lane record. Discounts are associated with a Geo Pricing Lane
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	discountDetails:ProposalDiscount[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Deletes 1 or all geo pricing lane discounts for an input geo pricing lane
 */
export class DeleteGeoPricingLaneDiscountsRqst {
	/**
	 * unique db identifier of the GeoPricingLane record
	 * Required: true
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique db identifier of the GeoPricingLaneDiscount record
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneDiscountId:string = undefined;
}
/**
 * Creates discounts for the Geo Pricing Lanes baesd on the input data.
 */
export class UpdateGeoPricingLaneDiscountsRqst {
	/**
	 * unique db identifier of the Geo Pricing Lane record. Discounts are associated with a Geo Pricing Lane
	 * Required: true
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique db identifier of the Geo Pricing Lane Discount record. Discounts are associated with a Geo Pricing Lane
	 * Required: true
	 * Array: false
	 */
	geoPricingLaneDiscountId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	discountDetails:ProposalDiscount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Response message of CreateGeoPricingLaneDiscounts
 */
export class UpdateGeoPricingLaneDiscountsResp {
	/**
	 * unique db identifier of the Geo Pricing Lane record. Discounts are associated with a Geo Pricing Lane
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	discountDetails:ProposalDiscount[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Creates discounts for the Geo Pricing Lanes baesd on the input data.
 */
export class ListGeoPricingLaneDiscountsRqst {
	/**
	 * unique db identifier of the Geo Pricing Lane record. Discounts are associated with a Geo Pricing Lane
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message of CreateGeoPricingLaneDiscounts
 */
export class ListGeoPricingLaneDiscountsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	discountDetails:ProposalDiscount[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to create the Geo Pricing Discount and Absolute Minimum charge amount record
 */
export class CreateGeoPricingDiscountAmcSummaryRqst {
	/**
	 * unique identifier of the Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoDiscountAmcInfo:GeoDiscountAmcSummary = undefined;
}
/**
 * Response message to create the Geo Pricing Discount and Absolute Minimum charge amount record
 */
export class CreateGeoPricingDiscountAmcSummaryResp {
	/**
	 * unique identifier of the Pricing Proposal
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoDiscountAmcInfo:GeoDiscountAmcSummary = undefined;
}
/**
 * Input message to create the Geo Pricing Discount and Absolute Minimum charge amount record
 */
export class UpdateGeoPricingDiscountAmcSummaryRqst {
	/**
	 * unique identifier of the Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of the geo pricing discount and amc summary record
	 * Required: true
	 * Array: false
	 */
	geoDiscAmcSummaryId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoDiscountAmcInfo:GeoDiscountAmcSummary = undefined;
}
/**
 * Input message to retrieve the Geo Pricing Discount and Absolute Minimum charge amount record
 */
export class ListGeoPricingDiscountAmcSummaryRqst {
	/**
	 * unique identifier of the Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message to retrieve the Geo Pricing Discount and Absolute Minimum charge amount record
 */
export class ListGeoPricingDiscountAmcSummaryResp {
	/**
	 * unique identifier of the Pricing Proposal
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	geoDiscountAmcInfo:GeoDiscountAmcSummary[] = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to delete the Geo Pricing Discount and Absolute Minimum charge amount record
 */
export class DeleteGeoPricingDiscountAmcSummaryRqst {
	/**
	 * unique identifier of the Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of the geo pricing discount and amc summary record
	 * Required: false
	 * Array: false
	 */
	geoDiscAmcSummaryId:string = undefined;
}
/**
 * Request message to create the GeoPricingLaneAMC record
 */
export class CreateGeoPricingLaneAmcsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	amcDetails:PricingAmc[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Response message
 */
export class CreateGeoPricingLaneAmcsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	amcDetails:GeoPricingLaneAmc[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Request message to create the GeoPricingLaneAMC record
 */
export class UpdateGeoPricingLaneAmcsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	amcDetails:GeoPricingLaneAmc = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Response message
 */
export class UpdateGeoPricingLaneAmcsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	amcDetails:GeoPricingLaneAmc = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Retrieve a list of Absolute Minimum Charge records for a given geo pricing lane
 */
export class ListGeoPricingLaneAmcsRqst {
	/**
	 * unique db identifier of the geo pricing lane record
	 * Required: true
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieve a list of Absolute Minimum Charge records for a given geo pricing lane
 */
export class ListGeoPricingLaneAmcsResp {
	/**
	 * unique db identifier of the geo pricing lane record
	 * Required: true
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	amcDetails:GeoPricingLaneAmc[] = undefined;
	/**
	 * pagination information
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Deletes one all all Geo Pricing Lane Amc records for a given Geo Pricing Lane id
 */
export class DeleteGeoPricingLaneAmcsRqst {
	/**
	 * unique db identifier of geo pricing lane
	 * Required: true
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique db identifier of geo pricing lane amc
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneAmcId:string = undefined;
}
/**
 * Retreive the FAK Adjustment percent based on input Actual NMFC Class, FAK Class, effective date and expiry date
 */
export class GetPricingAnalysisFakAdjustmentRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	actualNmfcClass:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	fakNmfcClass:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	effDate:Date = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	expDate:Date = undefined;
}
export class GetPricingAnalysisFakAdjustmentResp {
	/**
	 * Required: false
	 * Array: false
	 */
	fakAdjPct:number = undefined;
}
/**
 * Retrieves FSC discount percent based on input fuel price per gallon and effective date
 */
export class GetPricingAnalysisFscDiscountRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	fuelPricePerGallon:MonetaryAmount = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	effDate:Date = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	expDate:Date = undefined;
}
export class GetPricingAnalysisFscDiscountResp {
	/**
	 * Required: false
	 * Array: false
	 */
	fscDiscountPct:number = undefined;
}
/**
 * Retrieves the fuel price per gallon based on input date.
 */
export class GetFuelPriceAmountRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	effDate:Date = undefined;
}
export class GetFuelPriceAmountResp {
	/**
	 * Required: false
	 * Array: false
	 */
	fuelPricePerGallon:MonetaryAmount = undefined;
}
/**
 * This service returns the LOWER_BOUND, UPPER_BOUND, OPTIMAL CDI from ADMIN_Analysis_CDI_Factor based on the input sales volume, naics, region. One row will be returned based on the input data.
 */
export class GetPricingAnalysisCdiFactorRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	custSalesVolCd:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	naicsCd:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	salesRegionCd:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	effDate:Date = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	expDate:Date = undefined;
}
export class GetPricingAnalysisCdiFactorResp {
	/**
	 * Required: false
	 * Array: false
	 */
	cdiFactor:AnalysisFactorCdi = undefined;
}
/**
 * Input message to retrieve sales rfp details
 */
export class GetSalesRfpRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	levelOfDetail:string = undefined;
}
/**
 * Retrieves Sales Rfp details based on input Sales Rfp Id
 */
export class GetSalesRfpResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfp:SalesRfp = undefined;
}
/**
 * Request message to return the Class Rates based on input Pricing Agreement Instance Id and Ruleset sequence number
 */
export class GetClassRatesByPricingAgrmtAndRulesetRqst {
	/**
	 * Pricing Agreement Instance Id
	 * Required: true
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * Ruleset Sequence Number
	 * Required: true
	 * Array: false
	 */
	rulesetNbr:number = undefined;
	/**
	 * Shipment Information required for retrieving the class rates
	 * Required: true
	 * Array: false
	 */
	shipmentInfo:RulesetShipment = undefined;
	/**
	 * Party role: Values are S (Shipper), C(Consignee), B(bill to)
	 * Required: true
	 * Array: false
	 */
	partyTypeCode:string = undefined;
}
export class GetClassRatesByPricingAgrmtAndRulesetResp {
	/**
	 * Required: false
	 * Array: false
	 */
	classRate:ClassRate = undefined;
}
/**
 * Request message to return the Class Rates based on input Customer Id and Shipment data
 */
export class GetClassRatesByCustomerIdRqst {
	/**
	 * Customer Account Instance Id for which to find the Pricing Agreement and class rates
	 * Required: true
	 * Array: false
	 */
	acctInstId:string = undefined;
	/**
	 * Party role: Values are S (Shipper), C(Consignee), B(bill to)
	 * Required: true
	 * Array: false
	 */
	partyTypeCode:string = undefined;
	/**
	 * Shipment Information required for retrieving the class rates
	 * Required: true
	 * Array: false
	 */
	shipmentInfo:RulesetShipment = undefined;
}
export class GetClassRatesByCustomerIdResp {
	/**
	 * Information about the class rates from the customer specific pricing agreement ruleset
	 * Required: false
	 * Array: false
	 */
	classRate:ClassRate = undefined;
}
/**
 * List of Customer Pricing Pricing Agreements for input Customer Identifier
 */
export class ListAcctPricingAgreementRqst {
	/**
	 * Acct Id of the customer
	 * Required: false
	 * Array: true
	 */
	acctInstId:string[] = undefined;
	/**
	 * Acct Id of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	leadAcctId:string = undefined;
	/**
	 * pagination information for list retrieval
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of Customer Accounts with their Pricing Agreement Identifier Information
 */
export class ListAcctPricingAgreementResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingAgreementsByAcct:PricingAgreementByAcct[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAgreementAcctsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	agreementAccts:AcctId[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieves a list of Customer locations (Accounts) associated to an input Pricing Agreement
 */
export class ListPricingAgreementAcctsRqst {
	/**
	 * Customer Account Id
	 * Required: false
	 * Array: false
	 */
	leadAcctId:string = undefined;
	/**
	 * Version number of the pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementAcctVersion:string = undefined;
	/**
	 * The sequence number within the version of pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementSeqNbr:number = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to get details of input pricing codes
 */
export class ListPricingCodesByTypeRqst {
	/**
	 * Required: false
	 * Array: true
	 */
	categoryCode:string[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	subCategoryCode:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message containing details of each of the input pricing code
 */
export class ListPricingCodesByTypeResp {
	/**
	 * Required: false
	 * Array: true
	 */
	codeDetails:Code[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to create a Pricing Code
 */
export class CreatePricingCodeRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	codeDetails:Code = undefined;
}
/**
 * Input message to delete a Pricing Code
 */
export class DeletePricingCodeRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	pricingCodeId:string = undefined;
}
/**
 * Input message to create a Pricing Code
 */
export class CreatePricingCodeResp {
	/**
	 * unique db identifier of the pricing code
	 * Required: false
	 * Array: false
	 */
	pricingCodeId:string = undefined;
}
/**
 * Input message to create a Pricing Code
 */
export class UpdatePricingCodeRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	codeDetails:Code = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	pricingCodeId:string = undefined;
}
/**
 * List of All LTL Accessorials 
 */
export class ListGlobalAccessorialsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	globalAccessorials:AccessorialService[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of All LTL Accessorials 
 */
export class ListGlobalAccessorialsRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message for list of pricing analysts for a given pricing manager id
 */
export class ListPricingAnalystsByMgrIdRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingMgrEmplId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ListInfo:ListInfo = undefined;
}
/**
 * Request message to update an existing leaf shipment based on proposal instance id
 */
export class UpdatePricingLeafShipmentRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	leafShipment:PricingLeafShipment = undefined;
}
/**
 * Request message to update Pricing eaf shipments in bulk
 */
export class BulkUpdatePricingLeafShipmentRqst {
	/**
	 * Required: true
	 * Array: true
	 */
	leafShipments:PricingLeafShipment[] = undefined;
}
/**
 * Input message to retrieve a list of Pricing Analysis Leaf Shipments based on input Pricing Proposal Id
 */
export class ListPricingLeafShipmentsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Creates PricingLeafShipments by reading the s and running them thru the segmentation model
 */
export class SegmentPricingLeafShipmentsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Enriches the Pricing Leaf Shipments with Geo and NTD data from BI Extract. Input is Pricing proposal id
 */
export class EnrichPricingLeafShipmentsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:object = undefined;
}
/**
 * Creates PricingLeafShipments by reading the PricingAnalysisShipments and running them thru the segmentation model
 */
export class SegmentPricingLeafShipmentsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * List of Pricing Leaf shipments for a given Pricing Proposal Id
 */
export class ListPricingLeafShipmentsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	leafShipments:PricingLeafShipment[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAnalystsByMgrIdResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingAnalystDetails:PricingAdminPersonnel[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to retrieve 1 or more Pricing Proposal Headers
 */
export class GetPricingProposalHeaderRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Retrieves a list of Pricing Proposal Header records based on input 
 */
export class GetPricingProposalHeaderResp {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingProposalHeader:PricingProposalHdr = undefined;
}
/**
 * Retrieves a list of Pricing Proposal Header records based on input 
 */
export class ListPricingProposalHeadersResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingProposalHeaders:PricingProposalHdr[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to retrieve 1 or more Pricing Proposal Headers
 */
export class ListPricingProposalHeadersRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	analysisTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposalStatCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnalystId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	employeeRoleType:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to retrieve Pricing Proposal Details for a given Pricing Proposal Id
 */
export class GetPricingProposalRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * identifies the level of detail to be included in the response
	 * Required: false
	 * Array: true
	 */
	levelOfDetail:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	excludeHdr:boolean = undefined;
}
/**
 * Retrieves a list of Pricing Proposal Header records based on input 
 */
export class GetPricingProposalResp {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingProposal:PricingProposal = undefined;
}
/**
 * Returns the pricing agreements either associated with the negotiating customer, which is a corporate account, or for the list of one or more accounts which are P2D or Bil2 accounts. The negotiating customer will be used to find pricing agreement for which it is the owner. The list of accounts will be used to search the rulesets where they are referenced and find the pricing agreements to which those rulesets belong.		
 */
export class GetPricingAgreementLiabilityDetailsRqst {
	/**
	 * Acct Id of the customer
	 * Required: false
	 * Array: false
	 */
	acctInstId:string = undefined;
	/**
	 * Sequence number of the agreement
	 * Required: false
	 * Array: false
	 */
	seqNbr:string = undefined;
	/**
	 * Version number of the agreement
	 * Required: false
	 * Array: false
	 */
	versionNbr:string = undefined;
	/**
	 * Agreement instance id
	 * Required: false
	 * Array: false
	 */
	agreementId:string = undefined;
}
/**
 * Returns the pricing agreements either associated with the negotiating customer, which is a corporate account, or for the list of one or more accounts which are P2D or Bil2 accounts. The negotiating customer will be used to find pricing agreement for which it is the owner. The list of accounts will be used to search the rulesets where they are referenced and find the pricing agreements to which those rulesets belong.
 * List of Customer Accounts with their Pricing Agreement Identifier Information
 */
export class GetPricingAgreementLiabilityDetailsResp {
	/**
	 * The liability pricing agreement details.
	 * Required: false
	 * Array: false
	 */
	liabilityPricingAgreement:PricingAgreement = undefined;
}
/**
 * Sales Rfp Id returned after successful creation
 */
export class CreateSalesRfpAccessorialsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Input message to create Sales Rfp accessorials for a given sales rfp
 */
export class CreateSalesRfpAccessorialsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	salesRfpAccessorials:PricingAccessorial[] = undefined;
}
/**
 * List of Sales Rfp Accessorials for a given Sales Rfp Identifier
 */
export class ListSalesRfpAccessorialsRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of Sales Rfp Accessorials for a given Sales Rfp Identifier
 */
export class ListSalesRfpAccessorialsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	salesRfpAccessorials:PricingAccessorial[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List sales rfp requests that meet certain criteria.
 */
export class ListSalesRfpRqst {
	/**
	 * Identifier of the Customer with whom the agreement is being or was negotiated. This will uniquely identify the customer in the CIS component.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustInstId:string = undefined;
	/**
	 * This is the business key at the time of negotiation for this customer. Note that it will not be kept in synch with the master customer database.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustMadCd:string = undefined;
	/**
	 * The date the Sales RFP is due from Pricing back to Sales
	 * Required: false
	 * Array: false
	 */
	dueDate:Date = undefined;
	/**
	 * Requested Effective Date for the Agreement if the request is approved.
	 * Required: false
	 * Array: false
	 */
	proposedEffDate:Date = undefined;
	/**
	 * Proposed Expiration Date for the Agreement if the request is approved.
	 * Required: false
	 * Array: false
	 */
	proposedExpDate:Date = undefined;
	/**
	 * The ID of the Pricing Analyst
	 * Required: false
	 * Array: false
	 */
	pricingAnalystId:string = undefined;
	/**
	 * The ID of the owner of sales RFP.
	 * Required: false
	 * Array: false
	 */
	ownerId:string = undefined;
	/**
	 * The ID of the owner of sales RFP, the owner transfers to the party that is actively working on the RFP.
	 * Required: false
	 * Array: false
	 */
	currentOwnerId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Start date  to filter the Sales RFP
	 * Required: false
	 * Array: false
	 */
	startDate:Date = undefined;
	/**
	 * End date to filter the Sales RFP
	 * Required: false
	 * Array: false
	 */
	endDate:Date = undefined;
	/**
	 * Sales Region code of the Sales RFP
	 * Required: false
	 * Array: false
	 */
	salesRegionCd:string = undefined;
	/**
	 * Sales District code of the Sales RFP
	 * Required: false
	 * Array: false
	 */
	salesDistrictCd:string = undefined;
}
/**
 * List sales rfp requests that meet certain criteria. Returns 1 or more sales rfp headers
 */
export class ListSalesRfpResp {
	/**
	 * The Sales RFP header info that meets the input criteria.
	 * Required: false
	 * Array: true
	 */
	salesRfp:SalesRfp[] = undefined;
	/**
	 * The total number of rows in the list.
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListSalesRfpLifeCycleRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	acctId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	dateCriteria:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	startDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	endDate:Date = undefined;
	/**
	 * The sales region code based on the sales agent's region code at RFP creation time
	 * Required: false
	 * Array: false
	 */
	salesRegionCd:string = undefined;
	/**
	 * Area Code of sales rfp owner
	 * Required: false
	 * Array: false
	 */
	areaCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	reasonCd:string = undefined;
	/**
	 * Current status of the request (Sales or Pricing). Sample values: Approved, Canceled, NewRFP, InSales, InPricing, InPricingAnalysis
	 * Required: false
	 * Array: false
	 */
	rfpStatusCd:string = undefined;
	/**
	 * Current status of the pricing proposal (from 'not started' thru 'accepted'. Sample values (not complete): Not Started, Analysis in Progress, Under Review, Submitted to Sales, On-Hold, Cancelled, Accepted, Rejected, Countered
	 * Required: false
	 * Array: false
	 */
	proposalStatCd:string = undefined;
	/**
	 * audit status for the sales rfp once agreement is created
	 * Required: false
	 * Array: false
	 */
	auditStatus:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rulesetBuildStatus:string = undefined;
}
export class ListSalesRfpLifeCycleResp {
	/**
	 * Required: false
	 * Array: true
	 */
	salesRfp:SalesRfp[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Build sales rfp customer shipments details for a given sales rfp id and create customer shipments using sales rfp constraints and constraint end points
 */
export class BuildSalesRfpCustomerShipmentsRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Build sales rfp customer shipments details for a given sales rfp id and create customer shipments using sales rfp constraints and constraint end points
 */
export class BuildSalesRfpCustomerShipmentsResp {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Updates sales rfp customer shipment details for a given sales rfp id and customer shipment file id
 */
export class UpdateSalesRfpCustomerShipmentsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	custShipmentFileId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	rfpCustomerShipment:CustomerShipmentsFile = undefined;
}
/**
 * Build input hierarchy shipment file for a given sales rfp id.
 */
export class BuildInputHierarchyShipmentFileRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
export class BuildInputHierarchyShipmentFileResp {
	/**
	 * Required: false
	 * Array: false
	 */
	inputHierarchyFile:InputHierarchyFile = undefined;
}
/**
 * Retrieves a list of sales rfp constraints for a given Sales Rfp Id and type (optional)
 */
export class ListSalesRfpConstraintsRqst {
	/**
	 * Unique Identifier of Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	sourceType:string = undefined;
	/**
	 * Pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListSalesRfpConstraintsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	salesRfpConstraints:SalesRfpConstraint[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Deletes an existing Sales Rfp Constraint for a given Sales Rfp Id and Rfp Constraint Id
 */
export class DeleteSalesRfpConstraintRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	rfpConstraintId:string = undefined;
}
/**
 * Updates an existing Sales Rfp Constraint for an input Sales Rfp Id and Rfp Constraint id
 */
export class UpdateSalesRfpConstraintRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	rfpConstraintId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpConstraint:SalesRfpConstraint = undefined;
}
/**
 * Creates a Sales Rfp Constraint record for a given Sales Rfp Id
 */
export class CreateSalesRfpConstraintRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	salesRfpConstraint:SalesRfpConstraint = undefined;
}
export class CreateSalesRfpConstraintResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpConstraint:SalesRfpConstraint = undefined;
}
/**
 * Request message to update User Preference
 */
export class UpdateUserPreferenceRqst {
	/**
	 * employeeId for User Preference
	 * Required: true
	 * Array: false
	 */
	employeeId:string = undefined;
	/**
	 * Application Key for User Preference
	 * Required: true
	 * Array: false
	 */
	appKey:string = undefined;
	/**
	 * User Preference
	 * Required: true
	 * Array: false
	 */
	userPreference:UserPreference = undefined;
}
/**
 * Request message to retrieve User Preference
 */
export class GetUserPreferenceRqst {
	/**
	 * employeeId for User Preference
	 * Required: true
	 * Array: false
	 */
	employeeId:string = undefined;
	/**
	 * Application Key for User Preference
	 * Required: true
	 * Array: false
	 */
	appKey:string = undefined;
}
export class GetUserPreferenceResp {
	/**
	 * Required: false
	 * Array: false
	 */
	userPreference:UserPreference = undefined;
}
/**
 * Request message to get the account information from the CRM System
 */
export class GetCrmAccountInfoRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	accountId:string = undefined;
}
export class GetCrmAccountInfoResp {
	/**
	 * Required: false
	 * Array: false
	 */
	accountInfo:CrmAccount = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	accountOwner:CrmAccountOwner[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	parentAccount:CrmAccount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ultimateAccountParent:CrmAccount = undefined;
}
/**
 * Request message to get the account information from the CRM System
 */
export class ListCrmAccountInfoRqst {
	/**
	 * Required: true
	 * Array: true
	 */
	accountId:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListCrmAccountInfoResp {
	/**
	 * Required: false
	 * Array: true
	 */
	accountInfo:ListCrmAccountInfoResp_CrmAccount[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message
 */
export class CreatePricingAnalysisShipmentResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:object = undefined;
}
/**
 * Input message to create Pricing Analysis Shipments records
 */
export class CreatePricingAnalysisShipmentRqst {
	/**
	 * Required: true
	 * Array: true
	 */
	analysisShipments:PricingAnalysisShipmentHdr[] = undefined;
}
/**
 * Input message to updae Pricing Analysis Shipments records
 */
export class UpdatePricingAnalysisShipmentRqst {
	/**
	 * Required: true
	 * Array: true
	 */
	analysisShipments:PricingAnalysisShipment[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	intraAmc:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	interAmc:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	intlAmc:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	enableAnalysis:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	adjustPricingType:string = undefined;
}
/**
 * Input message to retrieve a list of Pricing Analysis Shipments for a given Pricing Proposal Id
 */
export class ListPricingAnalysisShipmentsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to retrieve a list of Pricing Analysis Shipments for a given Pricing Proposal Id
 */
export class ListPricingAnalysisShipmentsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	analysisShipments:PricingAnalysisShipment[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to delete one or more Analysis shipments for a given Pricing Proposal Id
 */
export class DeletePricingAnalysisShipmentsRqst {
	/**
	 * Unique identifier of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	prcAnalysisShipmentId:string[] = undefined;
}
/**
 * Updates Analysis Shipments from a CSV file
 */
export class UpdatePricingAnalysisShipmentFromCsvRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	docFiles:File[] = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	docMappingInfo:string = undefined;
}
/**
 * Input message to create Shipment Accessorial records for a given Pricing Analysis Shipment record
 */
export class CreatePricingAnalysisShipmentAccessorialsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcAnlysShipmentId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	shipmentAccessorials:PricingAnalysisShipmentAccessorial[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	enableAnalysis:boolean = undefined;
}
export class CreatePricingAnalysisShipmentAccessorialsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysShipmentId:string = undefined;
}
/**
 * Input message to retrieve a list of Shipment Accessorial records for a given Pricing Analysis Shipment and Pricing Proposal Id
 */
export class ListPricingAnalysisShipmentAccessorialsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcAnlysShipmentId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAnalysisShipmentAccessorialsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysShipmentId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	shipmentAccessorials:PricingAnalysisShipmentAccessorial[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to update Shipment Accessorial records for a given Pricing Analysis Shipment record
 */
export class UpdatePricingAnalysisShipmentAccessorialsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	enableAnalysis:boolean = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	shipmentAccessorials:PricingAnalysisShipmentAccessorial[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	groupShipmentAccsrl:PricingGroupShipmentAccsrl = undefined;
}
/**
 * Input message to update Shipment Accessorial records for a given Pricing Analysis Shipment record
 */
export class DeletePricingAnalysisShipmentAccessorialsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcAnlysShipmentId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysShipmentAccsrlId:string = undefined;
}
/**
 * Input message to create Shipment Accessorial records for a given Pricing Analysis Shipment record
 */
export class CreatePricingProposalAccessorialsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	proposalAccessorial:PricingAccessorial[] = undefined;
}
export class CreatePricingProposalAccessorialsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Input message to retrieve a list of Shipment Accessorial records for a given Pricing Analysis Shipment and Pricing Proposal Id
 */
export class ListPricingProposalAccessorialsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingProposalAccessorialsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	proposalAccessorials:PricingProposalAccessorial[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to update Shipment Accessorial records for a given Pricing Analysis Shipment record
 */
export class UpdatePricingProposalAccessorialsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	pricingProposalAccessorial:PricingProposalAccessorial[] = undefined;
}
/**
 * Input message to update Shipment Accessorial records for a given Pricing Analysis Shipment record
 */
export class DeletePricingProposalAccessorialsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalAccsrlId:string = undefined;
}
/**
 * Input message to create the Pricing Proposal Customer Hierarchy record for a given Pricing Proposal Identifier
 */
export class CreatePricingProposalCustHierarchyRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	updateShipment:boolean = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	custHierarchyInfo:PricingProposalCustomerHierarchy[] = undefined;
}
export class CreatePricingProposalCustHierarchyResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Input message to update the Pricing Proposal Customer Hierarchy record for an input Pricing Proposal Identifier and input Prc Proposal Cust Hierarchy Identifier
 */
export class UpdatePricingProposalCustHierarchyRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalCustHierarchyId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	custHierarchyInfo:PricingProposalCustomerHierarchy = undefined;
}
export class UpdatePricingProposalCustHierarchiesRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	updateShipment:boolean = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	custHierarchyInfo:PricingProposalCustomerHierarchy[] = undefined;
}
/**
 * Input message to delete the Pricing Proposal Customer Hierarchy record for an input Pricing Proposal Identifier and input Prc Proposal Cust Hierarchy Identifier
 */
export class DeletePricingProposalCustHierarchyRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalCustHierarchyId:string = undefined;
}
/**
 * Retrieves a list of all the Customer hierarchy records for input Pricing Proposal Id
 */
export class ListPricingProposalCustHierarchyRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to create the Pricing Proposal Customer Hierarchy record for a given Pricing Proposal Identifier
 */
export class ListPricingProposalCustHierarchyResp {
	/**
	 * Required: false
	 * Array: true
	 */
	custHierarchyInfo:PricingProposalCustomerHierarchy[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Downloadable PDF file response that includes data from Sales Rfp Pricing Agreement Expectaton. Includes the projected and actual OR for each of the pricing agreement
 */
export class GetPricingAgreementMonitorFileResp {
	/**
	 * Required: false
	 * Array: false
	 */
	agreementMonitorFile:File = undefined;
}
/**
 * Retrieves a list of Pricing Agreements based on input Account Id or Pricing Agreement Id
 */
export class ListPricingAgreementsRqst {
	/**
	 * Customer location Account Identifier
	 * Required: false
	 * Array: false
	 */
	acctInstId:string = undefined;
	/**
	 * unique identifier of the Pricing Agreement
	 * Required: false
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAgreementsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingAgreement:PricingAgreement[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Creates a Pricing Agreement for a Customer Account
 */
export class CreatePricingAgreementRqst {
	/**
	 * Details of Pricing Agreement to be created
	 * Required: true
	 * Array: false
	 */
	pricingAgreement:PricingAgreement = undefined;
}
export class CreatePricingAgreementResp {
	/**
	 * unique identifier of the Pricing Agreement
	 * Required: false
	 * Array: false
	 */
	agreementId:string = undefined;
}
/**
 * Updates a Pricing Agreement for a Customer Account
 */
export class UpdatePricingAgreementRqst {
	/**
	 * unique identifier of Pricing agreement
	 * Required: true
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * Details of Pricing Agreement to be updated
	 * Required: true
	 * Array: false
	 */
	pricingAgreement:PricingAgreement = undefined;
}
/**
 * Creates Pricing Agreement Expectations data for input Pricing Agreement Id
 */
export class CreatePricingAgeementExpectationRqst {
	/**
	 * unique identifier of Pricing Agreement
	 * Required: true
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	pricingAgreementExpectation:PricingAgreementExpectation = undefined;
}
export class CreatePricingAgreementExpectationResp {
	/**
	 * Unique identifier of Pricing Agreement Expectation
	 * Required: false
	 * Array: false
	 */
	agreementExpectationId:string = undefined;
}
export class ListPricingAgreementExpectationsRqst {
	/**
	 * unique identifier of Pricing agreement
	 * Required: true
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * The instance ID of the customer that negotiated this pricing agreement
	 * Required: false
	 * Array: false
	 */
	leadAcctId:string = undefined;
	/**
	 * The sequence number within the version of pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementSeqNbr:string = undefined;
	/**
	 * The version of the  pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementCustVersion:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieves a list of Pricing Agreement Expectations for a given Pricing Agreement Id
 */
export class ListPricingAgreementExpectationsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingAgreementExpectation:PricingAgreementExpectation[] = undefined;
	/**
	 * pagination information
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to retrieve details about Pricing Agreement Expectation based on its Id
 */
export class GetPricingAgreementExpectationRqst {
	/**
	 * unique identifier of the Pricing Agreement Expectation
	 * Required: true
	 * Array: false
	 */
	agreementExpectationId:string = undefined;
	/**
	 * unique identifier of Pricing agreement
	 * Required: true
	 * Array: false
	 */
	agreementId:string = undefined;
}
export class GetPricingAgreementExpectationResp {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAgreementExpectation:PricingAgreementExpectation = undefined;
}
/**
 * Retrieves a list of Pricing Agreement Shipment summary records for a given Pricing agreement id
 */
export class ListPricingAgreementShipmentSummRqst {
	/**
	 * unique identifier of Pricing agreement
	 * Required: true
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * account id of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	leadAcctId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAgreementShipmentSummResp {
	/**
	 * Required: false
	 * Array: true
	 */
	agreementShipmentSumm:AgreementShipmentSummary[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * This service returns the count of shipments specified for a given customer or sequence or version
 */
export class GetPricingAgreementShipmentSummRqst {
	/**
	 * The instance ID of the customer that negotiated this pricing agreement
	 * Required: false
	 * Array: false
	 */
	leadAcctId:string = undefined;
	/**
	 * The sequence number within the version of pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementSeqNbr:string = undefined;
	/**
	 * The version of the  pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementCustVersion:string = undefined;
	/**
	 * This captures the count of number of months to look back for getting the shipment summary
	 * Required: false
	 * Array: false
	 */
	prevMonthCount:string = undefined;
}
/**
 * This service returns the count of shipments specified for a given customer or sequence or version
 */
export class GetPricingAgreementShipmentSummResp {
	/**
	 * Required: false
	 * Array: false
	 */
	agreementShipmentSumm:ShipmentSummary = undefined;
}
/**
 * Request message to retrieve Pricing Agreement details as a PDF file
 */
export class GetPricingAgreementReportFileRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	leadAcctId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	agreementAcctVersion:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	agreementSeqNbr:string = undefined;
	/**
	 * eg. PDF
	 * Required: false
	 * Array: false
	 */
	reportType:string = undefined;
}
export class GetPricingAgreementReportFileResp {
	/**
	 * Required: false
	 * Array: false
	 */
	reportFile:File = undefined;
}
/**
 * Request message to get a list of Pricing Analysis Workflow details
 */
export class ListPricingAnalysisWorkflowProgressRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	extractTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	extractStatusCd:string = undefined;
	/**
	 * retrieve latest flow indicator
	 * Required: false
	 * Array: false
	 */
	latestFlow:boolean = undefined;
	/**
	 * Active Indicator
	 * Required: false
	 * Array: false
	 */
	active:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to get a list of Pricing Analysis Workflow details
 */
export class ListPricingAnalysisWorkflowProgressResp {
	/**
	 * Required: false
	 * Array: true
	 */
	workflowsByProposalId:PricingAnalysisFlow[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Creates Pricing Analysis flow records based on input data for a given Pricing Proposal Id
 */
export class CreatePricingAnalysisWorkFlowRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	analysisFlowInfo:PricingAnalysisFlow = undefined;
}
/**
 * Creates Pricing Analysis flow records based on input data for a given Pricing Proposal Id
 */
export class CreatePricingAnalysisWorkFlowResp {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Updates Pricing Analysis flow records based on input data for a given Pricing Proposal Id and AnalysisFlowInstId
 */
export class UpdatePricingAnalysisWorkflowRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	analysisFlowInfo:PricingAnalysisFlow = undefined;
}
/**
 * Deletes one or all Pricing Analysis workflow records based on input Pricing Proposal Id and optional Analysis flow instance id
 */
export class DeletePricingAnalysisWorkflowRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Workflow
	 * Required: false
	 * Array: false
	 */
	analysisFlowInstId:string = undefined;
}
/**
 * Retrieve a single result based on a unique Workflow name
 */
export class GetPricingExtractWorkflowRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	workflowNm:string = undefined;
}
/**
 * Retrieve a single result based on a unique Workflow name
 */
export class GetPricingExtractWorkflowResp {
	/**
	 * Required: true
	 * Array: false
	 */
	Workflow:Workflow = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Retrieve a single result based on a ExtractWorkflowId and StepSeqNbr
 */
export class GetPricingExtractWorkflowStepRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcExtractWorkflowId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	stepSeqNbr:string = undefined;
}
/**
 * Retrieve a single result based on a ExtractWorkflowId and StepSeqNbr
 */
export class GetPricingExtractWorkflowStepResp {
	/**
	 * Required: true
	 * Array: false
	 */
	workflowStep:WorkflowStep = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Retrieve a list of all rating tariffs along with their allowed weight breaks. List can be filtered by passing one or more tariff codes
 */
export class ListRatingTariffWeightBreaksRqst {
	/**
	 * Rating tariff code
	 * Required: false
	 * Array: true
	 */
	tariffCd:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieve a list of all rating tariffs along with their allowed weight breaks. List can be filtered by passing one or more tariff codes
 */
export class ListRatingTariffWeightBreaksResp {
	/**
	 * Rating tariff code
	 * Required: false
	 * Array: true
	 */
	ratingTariffWeightBreaks:RatingTariffWgtBreak[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Returns the summary of shipments for a requested pricing group. The inputs are:  a valid Pricing Proposal ID, at least one pricing group category indicators and pricing group category priority. Service can accept more than one group category indicators/priority.
 */
export class CalculatePricingGroupSummaryShipmentsRqst {
	/**
	 * Unique identifier of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Indicates the pricing grouping category. Valid values are:
	 *  'GROUP' - group by Group Name, Orig Group, Dest Group of the shipments
	 *  'TRAFFIC' - group by Traffic Type (International, Interstate, Intrastate) of the shipments
	 *  'BILLING' - group by Application/Billing Type (Inbound, Outbound, 3rd Party) of the shipments
	 *  'WEIGHT' - group by Weight Breaks (L5C, 5C, 1M, 2M, etc) of the shipments
	 *  'CLASS' - group by Commodity Class (50, 55, 60, 65, etc) of the shipments
	 *  'SHIPMENT' - group by shipment id/pro number of the shipments
	 * Required: true
	 * Array: true
	 */
	categoryType:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	newAmc:string = undefined;
	/**
	 * Input for Pricing Adjustments
	 * Required: false
	 * Array: false
	 */
	discAdjustment:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	adjustmentData:PricingAdjustment = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class CalculatePricingGroupSummaryShipmentsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingGroupSummaryDetails:PricingGroupSummaryShipment[] = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class CalculatePricingGroupShipmentAccsrlRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	adjustmentData:PricingAdjustment = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class CalculatePricingGroupShipmentAccsrlResp {
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	pricingGroupSummaryDetails:PricingGroupShipmentAccsrl[] = undefined;
}
/**
 * Creates a GeoPricingGroup record. The Geo Pricing Group contains a list of members that are included and optionally, a list of members that are to be excluded from the Pricing Group.
 */
export class CreateGeoPricingGroupsRqst {
	/**
	 * Details about the GeoPricingGroup to be created along with its Included and Excluded members
	 * Required: true
	 * Array: true
	 */
	geoPricingGroupInfo:GeoPricingGroup[] = undefined;
}
export class CreateGeoPricingGroupsResp {
	/**
	 * Unique Identifier of the GeoPricingGroup
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupId:string = undefined;
}
/**
 * Updates a GeoPricingGroup record. The Geo Pricing Group contains a list of members that are included and optionally, a list of members that are to be excluded from the Pricing Group.
 */
export class UpdateGeoPricingGroupRqst {
	/**
	 * Unique id of the Geo Pricing Group Record
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupInfo:GeoPricingGroup = undefined;
}
/**
 * Retrieves a list of all the GeoPricingGroups and, depending upon the level of detail, their members
 */
export class ListGeoPricingGroupsRqst {
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * unique db identifier of Geo Pricing Group record
	 * Required: false
	 * Array: true
	 */
	geoPricingGroupId:string[] = undefined;
	/**
	 * Unique id of the Customer Record or Customer MAD Code
	 * Required: false
	 * Array: false
	 */
	custAcctId:string = undefined;
}
export class ListGeoPricingGroupsResp {
	/**
	 * pagination information
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * list of geo pricing groups and their member details
	 * Required: false
	 * Array: true
	 */
	geoPricingGroups:GeoPricingGroup[] = undefined;
}
/**
 * Retrieves a list of all GeoPricingGroupMembers for a given GeoPricingGroupIdentifier
 */
export class ListGeoPricingGroupMembersRqst {
	/**
	 * unique identifier of GeoPricingGroup
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListGeoPricingGroupMembersResp {
	/**
	 * pagination information
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * GeoPricingGroup detail
	 * Required: false
	 * Array: false
	 */
	geoPricingGroup:GeoPricingGroup = undefined;
}
/**
 * Creates 1 or more GeoPricingGroupMember records for input GeoPricingGroupIdentifier
 */
export class CreateGeoPricingGroupMembersRqst {
	/**
	 * unique identifier of the GeoPricingGroup record
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupId:object = undefined;
	/**
	 * GeoPricingGroupMembers to be included in the group
	 * Required: true
	 * Array: true
	 */
	geoPricingGroupMembers:GeoPricingGroupMember[] = undefined;
	/**
	 * GeoPricingGroupMembers to be excluded from the group
	 * Required: false
	 * Array: true
	 */
	geoPricingGroupExclMbr:GeoPricingGroupMember[] = undefined;
}
/**
 * Creates 1 or more GeoPricingGroupMember records for input GeoPricingGroupIdentifier
 */
export class CreateGeoPricingGroupMembersResp {
	/**
	 * unique identifier of the GeoPricingGroup record
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupId:object = undefined;
}
/**
 * Deletes 1 GeoPricingGroup and all its members for input GeoPricingGroupIdentifier
 */
export class DeleteGeoPricingGroupRqst {
	/**
	 * unique identifier of the GeoPricingGroup record
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupId:string = undefined;
}
/**
 * Deletes 1 or all GeoPricingGroupMembers for input GeoPricingGroupIdentifier and optional GeoPricingGroupMemberId
 */
export class DeleteGeoPricingGroupMembersRqst {
	/**
	 * unique identifier of the GeoPricingGroup record
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupId:string = undefined;
	/**
	 * unique identifier of GeoPricingGroupMember
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupMemberId:string = undefined;
}
/**
 * Deletes 1 or all GeoPricingGroupExcludedMembers for input GeoPricingGroupIdentifier and optional GeoPricingGroupExclMbrId
 */
export class DeleteGeoPricingGroupExclMbrRqst {
	/**
	 * unique identifier of the GeoPricingGroup record
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupId:string = undefined;
	/**
	 * unique identifier of GeoPricingGroupExcludedMember
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupExclMbrId:string = undefined;
}
/**
 * Creates a geo pricing group lane
 */
export class CreateGeoPricingGroupLanesRqst {
	/**
	 * Geo Pricing Group Lane information
	 * Required: true
	 * Array: true
	 */
	geoPricingGroupLane:GeoPricingGroupLane[] = undefined;
}
export class CreateGeoPricingGroupLanesResp {
	/**
	 * Required: false
	 * Array: true
	 */
	geoPricingGroupLane:GeoPricingGroupLane[] = undefined;
}
/**
 * Retrieves a list of Geo Pricing Group Lane records
 */
export class ListGeoPricingGroupLanesRqst {
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * customer account number
	 * Required: false
	 * Array: false
	 */
	acctInstId:string = undefined;
	/**
	 * customer account Mad code
	 * Required: false
	 * Array: false
	 */
	acctMadCd:string = undefined;
	/**
	 * unique identifier of the Geo Pricing Lanes
	 * Required: false
	 * Array: true
	 */
	geoLaneIds:string[] = undefined;
}
export class ListGeoPricingGroupLanesResp {
	/**
	 * Required: false
	 * Array: true
	 */
	geoPricingGroupLanes:GeoPricingGroupLane[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Updates a geo pricing group lane record based on the input data
 */
export class UpdateGeoPricingGroupLanesRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupLaneId:string = undefined;
	/**
	 * Geo Pricing Group Lane information
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupLane:GeoPricingGroupLane = undefined;
}
/**
 * Deletes a geo pricing group lane and its members
 */
export class DeleteGeoPricingGroupLanesRqst {
	/**
	 * unique identifier of the Geo Pricing Group Lane record
	 * Required: true
	 * Array: false
	 */
	geoPricingGroupLaneId:string = undefined;
	/**
	 * unique identifier of the geo pricing group lane member record
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneMbrId:string = undefined;
}
export class CalculatePricingGroupShipmentAmcRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	intraAmc:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	interAmc:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	intlAmc:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class CalculatePricingGroupShipmentAmcResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingGroupSummaryDetails:PricingGroupShipmentAmc[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Creates Pricing Analysis Geo Group Header record for a given Pricing Proposal Id
 */
export class CreatePricingAnlysGeoGroupHeadersRqst {
	/**
	 * unique identifier of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * analysis geo group header data to be persisted
	 * Required: true
	 * Array: true
	 */
	pricingAnlysGeoGroup:PricingAnalysisGeoGroupHdr[] = undefined;
}
export class CreatePricingAnlysGeoGroupHeadersResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Updates Pricing Analysis Geo Group Header record for a given Pricing Proposal Id
 */
export class UpdatePricingAnlysGeoGroupHeaderRqst {
	/**
	 * unique identifier of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of Pricing Analysis Geo Group Header record
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * analysis geo group header data to be persisted
	 * Required: true
	 * Array: false
	 */
	pricingAnlysGeoGroup:PricingAnalysisGeoGroupHdr = undefined;
}
/**
 * Deletes Pricing Analysis Geo Group Header record for a given Pricing Proposal Id
 */
export class DeletePricingAnlysGeoGroupHeaderRqst {
	/**
	 * unique identifier of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of Pricing Analysis Geo Group Header record
	 * Required: false
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
}
/**
 * Retrieves Pricing Analysis Geo Group Header record for a given Pricing Proposal Id
 */
export class ListPricingAnlysGeoGroupHeadersRqst {
	/**
	 * unique identifier of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAnlysGeoGroupHeadersResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingAnlysGeoGroupHdr:PricingAnalysisGeoGroupHdr[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class CreatePricingAnlysGeoGroupBiAmcsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	biAmcDetails:GeoGroupAmcTariff = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class CreatePricingAnlysGeoGroupBiAmcsResp {
	/**
	 * unique id of the Geo Group BI Amc record
	 * Required: false
	 * Array: false
	 */
	geoGroupBiAmcId:string = undefined;
}
/**
 * Deletes Pricing Analysis Geo Group BI AMC records for input pricing proposal and geo group bi amc id
 */
export class DeletePricingAnlysGeoGroupBiAmcsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group BI AMC record
	 * Required: false
	 * Array: false
	 */
	geoGroupBiAmcId:string = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class UpdatePricingAnlysGeoGroupBiAmcsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group BI AMC record
	 * Required: true
	 * Array: false
	 */
	geoGroupBiAmcId:string = undefined;
	/**
	 * details of the BI AMC data to be persisted
	 * Required: true
	 * Array: false
	 */
	biAmcDetails:GeoGroupAmcTariff = undefined;
}
/**
 * Retrieve a list of Pricing Analysis Geo Group BI AMC records for a given pricing proposal id and pricing analysis geo group id
 */
export class ListPricingAnlysGeoGroupBiAmcsRqst {
	/**
	 * unique Id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class ListPricingAnlysGeoGroupBiAmcsResp {
	/**
	 * unique id of Pricing Analysis geo group BI AMC record
	 * Required: true
	 * Array: false
	 */
	geoGroupId:string = undefined;
	/**
	 * details of the BI AMC data to be persisted
	 * Required: false
	 * Array: true
	 */
	biAmcDetails:GeoGroupBiAmc[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class CreatePricingAnlysGeoGroupCmAmcsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	cmAmcDetails:GeoGroupAmcTariff = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class CreatePricingAnlysGeoGroupCmAmcsResp {
	/**
	 * unique id of the Geo Group BI Amc record
	 * Required: false
	 * Array: false
	 */
	geoGroupCmAmcId:string = undefined;
}
/**
 * Deletes Pricing Analysis Geo Group BI AMC records for input pricing proposal and geo group bi amc id
 */
export class DeletePricingAnlysGeoGroupCmAmcsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group Cost Model AMC record
	 * Required: false
	 * Array: false
	 */
	geoGroupCmAmcId:string = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class UpdatePricingAnlysGeoGroupCmAmcsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group BI AMC record
	 * Required: true
	 * Array: false
	 */
	geoGroupCmAmcId:string = undefined;
	/**
	 * details of the CM AMC data to be persisted
	 * Required: true
	 * Array: false
	 */
	cmAmcDetails:GeoGroupAmcTariff = undefined;
}
/**
 * Retrieve a list of Pricing Analysis Geo Group BI AMC records for a given pricing proposal id and pricing analysis geo group id
 */
export class ListPricingAnlysGeoGroupCmAmcsRqst {
	/**
	 * unique Id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class ListPricingAnlysGeoGroupCmAmcsResp {
	/**
	 * unique id of Pricing Analysis geo group BI AMC record
	 * Required: true
	 * Array: false
	 */
	geoGroupId:string = undefined;
	/**
	 * details of the BI AMC data to be persisted
	 * Required: false
	 * Array: true
	 */
	cmAmcDetails:GeoGroupCmAmc[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * creates the pricing analysis  porter shipment-cost model  not rated with absolute minimum charge record. Based on the input pricing proposal inst id, pricing analysis geo group id, pricing analysis geo group porter shipment-cost model tariff record. 
 */
export class CreatePricingAnlysGeoGroupCmTariffsRqst {
	/**
	 * unique identifier of the Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Unique identifier for Pricing Analysis Group-Level record
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	cmTariffDetails:GeoGroupAmcTariff = undefined;
}
export class CreatePricingAnlysGeoGroupCmTariffsResp {
	/**
	 * unique identifier of the Pricing Analysis Geo Group Cost Model Tariff
	 * Required: false
	 * Array: false
	 */
	geoGroupCmTariffId:string = undefined;
}
/**
 * Deletes Pricing Analysis Geo Group Cost Model Tariff records for input pricing proposal and geo group bi amc id
 */
export class DeletePricingAnlysGeoGroupCmTariffsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group Cost Model Tariff record
	 * Required: false
	 * Array: false
	 */
	geoGroupCmTariffId:string = undefined;
}
/**
 * Retrieve a list of Pricing Analysis Geo Group Cost Model Tariff records for a given pricing proposal id and pricing analysis geo group id
 */
export class ListPricingAnlysGeoGroupCmTariffsRqst {
	/**
	 * unique Id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAnlysGeoGroupCmTariffsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	cmTariffDetails:GeoGroupCmTariff[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Updates pricing analyis geo group cost model shipments tariff record
 */
export class UpdatePricingAnlysGeoGroupCmTariffRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group Cost Model tariff record
	 * Required: true
	 * Array: false
	 */
	geoGroupCmTariffId:string = undefined;
	/**
	 * details of the BI AMC data to be persisted
	 * Required: true
	 * Array: false
	 */
	cmTariffDetails:GeoGroupAmcTariff = undefined;
}
/**
 * This service creates the pricing analysis historical not rated with absolute minimum charge record. Based on the input, PRC_ANLYS_GEO_GROUP_BI_TARIFF table is persisted
 */
export class CreatePricingAnlysGeoGroupBiTariffsRqst {
	/**
	 * unique identifier of the Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Unique identifier for Pricing Analysis Group-Level record
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	biTariffDetails:GeoGroupAmcTariff = undefined;
}
export class CreatePricingAnlysGeoGroupBiTariffsResp {
	/**
	 * unique identifier of the Pricing Analysis Geo Group Cost Model Tariff
	 * Required: false
	 * Array: false
	 */
	geoGroupBiTariffId:string = undefined;
}
/**
 * Deletes Pricing Analysis Geo Group Cost Model Tariff records for input pricing proposal and geo group bi amc id
 */
export class DeletePricingAnlysGeoGroupBiTariffsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group Cost Model Tariff record
	 * Required: false
	 * Array: false
	 */
	geoGroupBiTariffId:string = undefined;
}
/**
 * Retrieve a list of Pricing Analysis Geo Group Cost Model Tariff records for a given pricing proposal id and pricing analysis geo group id
 */
export class ListPricingAnlysGeoGroupBiTariffsRqst {
	/**
	 * unique Id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAnlysGeoGroupBiTariffsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	biTariffDetails:GeoGroupBiTariff[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Updates pricing analyis geo group cost model shipments tariff record
 */
export class UpdatePricingAnlysGeoGroupBiTariffRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group Cost Model tariff record
	 * Required: true
	 * Array: false
	 */
	geoGroupBiTariffId:string = undefined;
	/**
	 * details of the BI AMC data to be persisted
	 * Required: true
	 * Array: false
	 */
	biTariffDetails:GeoGroupAmcTariff = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class CreatePricingAnlysGeoGroupDiscAmcsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	discAmcDetails:GeoGroupDiscountAmc = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class CreatePricingAnlysGeoGroupDiscAmcsResp {
	/**
	 * unique id of the Geo Group BI Amc record
	 * Required: false
	 * Array: false
	 */
	geoGroupDiscAmcId:string = undefined;
}
/**
 * Deletes Pricing Analysis Geo Group Discount AMC records for input pricing proposal and geo group bi amc id
 */
export class DeletePricingAnlysGeoGroupDiscAmcsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group Discount AMC record
	 * Required: false
	 * Array: false
	 */
	geoGroupDiscAmcId:string = undefined;
}
/**
 * Retrieve a list of Pricing Analysis Geo Group BI AMC records for a given pricing proposal id and pricing analysis geo group id
 */
export class ListPricingAnlysGeoGroupDiscAmcsRqst {
	/**
	 * unique Id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class ListPricingAnlysGeoGroupDiscAmcsResp {
	/**
	 * details of the BI AMC data to be persisted
	 * Required: false
	 * Array: true
	 */
	discAmcDetails:GeoGroupDiscountAmc[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Create pricing analyis geo group historical shipments absolute minimum charge record
 */
export class UpdatePricingAnlysGeoGroupDiscAmcsRqst {
	/**
	 * unique id of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique id of Pricing Analysis Geo Group
	 * Required: true
	 * Array: false
	 */
	prcAnalysGeoGroupId:string = undefined;
	/**
	 * unique id of Pricing Analysis geo group DiscountAMC record
	 * Required: true
	 * Array: false
	 */
	geoGroupDiscAmcId:string = undefined;
	/**
	 * details of the Duscount AMC data to be persisted
	 * Required: true
	 * Array: false
	 */
	discAmcDetails:GeoGroupDiscountAmc = undefined;
}
/**
 * Input message to create Pricing Proposal FAK (class override)  records for a given Geo Area Pricing Identifier
 */
export class CreateGeoPricingLaneFaksRqst {
	/**
	 * unique identifier of Pricing Proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of Geo Area Pricing record
	 * Required: true
	 * Array: false
	 */
	geoPrcLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	proposalFak:PricingFak = undefined;
}
export class CreateGeoPricingLaneFaksResp {
	/**
	 * unique identifier of Geo Pricing Lane record
	 * Required: false
	 * Array: false
	 */
	geoPrcLaneId:string = undefined;
}
/**
 * Input message to update Pricing Proposal FAK (class override)  records for a given Geo Area Pricing Identifier and Pricing Proposal Fak identifier
 */
export class UpdateGeoPricingLaneFaksRqst {
	/**
	 * unique identifier of Pricing Proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of Geo Area Pricing record
	 * Required: true
	 * Array: false
	 */
	geoPrcLaneId:string = undefined;
	/**
	 * unique identifier of pricing proposal fak override record
	 * Required: true
	 * Array: false
	 */
	pricingProposalFakId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	proposalFak:PricingFak = undefined;
}
/**
 * Input message to delete Pricing Proposal FAK (class override)  records for a given Geo Area Pricing Identifier and Pricing Proposal Fak identifier
 */
export class DeleteGeoPricingLaneFaksRqst {
	/**
	 * unique identifier of Pricing Proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of Geo Area Pricing record
	 * Required: true
	 * Array: false
	 */
	geoPrcLaneId:string = undefined;
	/**
	 * unique identifier of pricing proposal fak override record
	 * Required: false
	 * Array: false
	 */
	pricingProposalFakId:string = undefined;
}
export class GetGeoPricingLaneFaksRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoPrcLaneId:string = undefined;
}
export class GetGeoPricingLaneFaksResp {
	/**
	 * Required: false
	 * Array: false
	 */
	geoPrcLaneId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fakOverrides:PricingFak = undefined;
}
/**
 * Deletes FAK override records by IDs.
 */
export class DeleteGeoPricingLaneFakOverridesRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoPrcLaneId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcFakHdrId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	overrideIds:string[] = undefined;
}
/**
 * Input message to create the Pricing Proposal Geo Pricing Lane record
 */
export class CreatePricingProposalGeoPricingLanesRqst {
	/**
	 * unique identifier of Pricing Proposal Instance Id
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * geo area pricing lane records to be persisted
	 * Required: true
	 * Array: true
	 */
	geoPricingLanes:GeoAreaPricingLane[] = undefined;
}
export class CreatePricingProposalGeoPricingLanesResp {
	/**
	 * unique id of Pricing Proposal Instance Id
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Retrieves a list of Pricing Proposal Geo Pricing Lanes for a given Pricing Proposal Identifier
 */
export class ListPricingProposalGeoPricingLanesRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingProposalGeoPricingLanesResp {
	/**
	 * Required: false
	 * Array: true
	 */
	geoPricingLanes:GeoAreaPricingLane[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to update the Pricing Proposal Geo Pricing Lane record
 */
export class DeletePricingProposalGeoPricingLanesRqst {
	/**
	 * unique identifier of Pricing Proposal Instance Id
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	geoAreaPrcLaneId:string = undefined;
}
/**
 * Input message to update the Pricing Proposal Geo Pricing Lane record
 */
export class UpdatePricingProposalGeoPricingLanesRqst {
	/**
	 * unique identifier of Pricing Proposal Instance Id
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoAreaPrcLaneId:string = undefined;
	/**
	 * geo area pricing lane records to be persisted
	 * Required: true
	 * Array: false
	 */
	geoPricingLane:GeoAreaPricingLane = undefined;
}
/**
 * Request message to recalculate the FAK amounts for a given proposal id.
 */
export class CalculatePricingProposalFaksRqst {
	/**
	 * input data
	 * Required: false
	 * Array: false
	 */
	fakData:FakAdjustment = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Response message to recalculate the FAK amounts for a given proposal id.
 */
export class CalculatePricingProposalFaksResp {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingProposal:PricingProposal = undefined;
}
/**
 * Creates the Pricing Proposal Ruleset Matrix record based on the input parameters. It includes Origin and destination state, country, postal code range, amc amounts and tier type etc.
 */
export class CreatePricingProposalRulesetMatrixRqst {
	/**
	 * unique identifier of Pricing Proposal Record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	proposalRulesetMatrix:PricingProposalRulesetMatrix[] = undefined;
}
/**
 * Creates the Pricing Proposal Ruleset Matrix record based on the input parameters. It includes Origin and destination state, country, postal code range, amc amounts and tier type etc.
 */
export class CreatePricingProposalRulesetMatrixResp {
	/**
	 * unique identifier of Pricing Proposal Record
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	proposalRulesetMatrix:PricingProposalRulesetMatrix[] = undefined;
}
/**
 * Retrieves Pricing Proposal Ruleset Matrix records for an input Pricing Proposal Instance Id
 */
export class ListPricingProposalRulesetMatrixRqst {
	/**
	 * unique identifier of Pricing Proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Creates the Pricing Proposal Ruleset Matrix record based on the input parameters. It includes Origin and destination state, country, postal code range, amc amounts and tier type etc.
 */
export class ListPricingProposalRulesetMatrixResp {
	/**
	 * unique identifier of Pricing Proposal Record
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	proposalRulesetMatrix:PricingProposalRulesetMatrix[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Deletes 1 or all Pricing Proposal Ruleset Matrix data based on input mandatory Pricing Proposal Inst Id and optional Pricing proposal ruleset matrix id
 */
export class DeletePricingProposalRulesetMatrixRqst {
	/**
	 * unique identifier of Pricing Proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of Pricing Proposal Ruleset Matrix
	 * Required: false
	 * Array: false
	 */
	prcProposalRulesetMatrixId:string = undefined;
}
/**
 * Updates the Pricing Proposal Ruleset Matrix record based on the input parameters. It includes Origin and destination state, country, postal code range, amc amounts and tier type etc.
 */
export class UpdatePricingProposalRulesetMatrixRqst {
	/**
	 * unique identifier of Pricing Proposal Record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of Pricing Proposal Ruleset Matrix Record
	 * Required: true
	 * Array: false
	 */
	prcProposalRulesetMatrixId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	proposalRulesetMatrix:PricingProposalRulesetMatrix = undefined;
}
/**
 * Retrieves a list of all the Pricing Segment Model records
 */
export class ListPricingModelSegmentsRqst {
	/**
	 * Pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingModelSegmentsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	modelSegments:PricingModelSegment[] = undefined;
	/**
	 * pagination information
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingProposalWorkQueueRqst {
	/**
	 * Employee Id of the Pricing Analyst that is assigned this pricing analysis
	 * Required: false
	 * Array: false
	 */
	pricingAnalystId:string = undefined;
	/**
	 * Current status of the pricing proposal
	 * Required: false
	 * Array: true
	 */
	proposalStatCd:string[] = undefined;
	/**
	 * Identifies the type of pricing analysis
	 * Required: false
	 * Array: false
	 */
	analysisTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	acctId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	dateCriteria:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	startDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	endDate:Date = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	listInfo:ListInfo[] = undefined;
}
export class ListPricingProposalWorkQueueResp {
	/**
	 * Required: false
	 * Array: true
	 */
	pricingProposalWorkQueue:PricingProposalWorkQueue[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Creates the Pricing Proposal TMS File record based on the input parameters. 
 */
export class CreatePricingProposalTmsFileRqst {
	/**
	 * unique identifier of Pricing Proposal Record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	proposalTmsFile:PricingProposalTms[] = undefined;
}
/**
 * Creates the Pricing Proposal Ruleset Matrix record based on the input parameters. It includes Origin and destination state, country, postal code range, amc amounts and tier type etc.
 */
export class CreatePricingProposalTmsFileResp {
	/**
	 * unique identifier of Pricing Proposal Record
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	proposalTmsFile:PricingProposalTms[] = undefined;
}
/**
 * Retrieves Pricing Proposal Ruleset Matrix records for an input Pricing Proposal Instance Id
 */
export class ListPricingProposalTmsFileRqst {
	/**
	 * unique identifier of Pricing Proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Creates the Pricing Proposal Ruleset Matrix record based on the input parameters. It includes Origin and destination state, country, postal code range, amc amounts and tier type etc.
 */
export class ListPricingProposalTmsFileResp {
	/**
	 * unique identifier of Pricing Proposal Record
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	proposalTmsFile:PricingProposalTms[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Deletes 1 or all Pricing Proposal Ruleset Matrix data based on input mandatory Pricing Proposal Inst Id and optional Pricing proposal ruleset matrix id
 */
export class DeletePricingProposalTmsFileRqst {
	/**
	 * unique identifier of Pricing Proposal record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of Pricing Proposal Tms file record
	 * Required: false
	 * Array: false
	 */
	prcProposalTmsFileId:string = undefined;
}
/**
 * Updates the Pricing Proposal Ruleset Matrix record based on the input parameters. It includes Origin and destination state, country, postal code range, amc amounts and tier type etc.
 */
export class UpdatePricingProposalTmsFileRqst {
	/**
	 * unique identifier of Pricing Proposal Record
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * unique identifier of Pricing Proposal Ruleset Matrix Record
	 * Required: true
	 * Array: false
	 */
	prcProposalTmsFileId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	proposalTmsFile:PricingProposalTms = undefined;
}
/**
 * Given a unique Sales Rfp Id, build and return a sales rfp pdf file
 */
export class GetSalesRfpReportFileRqst {
	/**
	 * Unique id of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Report type requested. Eg. PDF
	 * Required: false
	 * Array: false
	 */
	reportType:string = undefined;
}
export class GetSalesRfpReportFileResp {
	/**
	 * Required: false
	 * Array: false
	 */
	reportFile:File = undefined;
}
/**
 * Create a sales rfp customer shipments details for a given sales rfp id
 */
export class CreateSalesRfpCustomerShipmentsRqst {
	/**
	 * unique identifier of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	rfpCustomerShipmentHdr:CustomerShipmentsFileHdr = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	rfpCustomerShipments:CustomerShipmentsFile[] = undefined;
}
export class CreateSalesRfpCustomerShipmentsResp {
	/**
	 * unique identifier of sales rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Deletes sales rfp customer shipments for the specified sales rfp id
 */
export class DeleteSalesRfpCustomerShipmentsRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
}
/**
 * Gets list of Sales Rfp Customer Shipment for a given sales rfp id
 */
export class ListSalesRfpCustomerShipmentsRqst {
	/**
	 * unique identifier of sales rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListSalesRfpCustomerShipmentsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	rfpCustomerShipments:CustomerShipmentsFile[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to retrieve list of Proposal AMCs and Discounts for a given Pricing Proposal Id
 */
export class ListPricingProposalAmcDiscountsRqst {
	/**
	 * unique identifier of Pricing Proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Pagination Information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of Pricing Proposal AMCs and Discounts
 */
export class ListPricingProposalAmcDiscountsResp {
	/**
	 * Details about Pricing Proposal AMCs and Discounts
	 * Required: false
	 * Array: true
	 */
	proposalAmcDiscount:ProposalAmcDiscount[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * This service will store user details in pricing system. If the details are not there it will create otherwise it will update the existing details
 */
export class CreatePricingAdminPersonnelRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	pricingAdminPersonnelDetails:PricingAdminPersonnel = undefined;
}
/**
 * This service will store user details in pricing system. If the details are not there it will create otherwise it will update the existing details
 */
export class CreatePricingAdminPersonnelResp {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAdminPersonnelDetails:PricingAdminPersonnel = undefined;
}
export class DeletePricingAdminPersonnelRqst {
	/**
	 * unique identifier of the Pricing Admin Personnel record
	 * Required: true
	 * Array: true
	 */
	adminPersonnelId:string[] = undefined;
}
/**
 * Request message to retrieve a list of all the Aggregate Model Constraint Templates
 */
export class ListAggregateModelConstraintTemplatesRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	aggrModelTemplateId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message 
 */
export class ListAggregateModelConstraintTemplatesResp {
	/**
	 * Required: false
	 * Array: true
	 */
	aggrModelConstraintTemplates:AggregateModelConstraintTemplate[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to create the Aggregate Model Geo Staging Record
 */
export class CreatePricingProposalAggrModelGeoStagingsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	aggrModelGeoStagings:AggregateModelGeoStaging[] = undefined;
}
export class CreatePricingProposalAggrModelGeoStagingsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Request message to update the Aggregate Model Geo Staging Record
 */
export class UpdatePricingProposalAggrModelGeoStagingsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	aggrModelGeoStagings:AggregateModelGeoStaging[] = undefined;
}
/**
 * Request message to retrieve list of Aggregate Model Geo Staging data based on input Pricing Proposal Id or Geo Pricing Group Lane Id
 */
export class ListPricingProposalAggrModelGeoStagingsRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingProposalAggrModelGeoStagingsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	aggrModelGeoStagings:AggregateModelGeoStaging[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieves a list of Geo Pricing Group Lanes for a given Pricing Proposal Identifier
 */
export class ListGeoPricingGroupLanesForProposalRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListGeoPricingGroupLanesForProposalResp {
	/**
	 * Required: false
	 * Array: true
	 */
	geoPricingGroupLanes:GeoPricingGroupLane[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieves a list of Geo Pricing Groups for a given Pricing Proposal Identifier
 */
export class ListGeoPricingGroupsForProposalRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListGeoPricingGroupsForProposalResp {
	/**
	 * Required: false
	 * Array: true
	 */
	geoPricingGroups:GeoPricingGroup[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieves a list of Geo Pricing Group Members for a given Pricing Proposal Identifier
 */
export class ListGeoPricingGroupMembersForProposalRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListGeoPricingGroupMembersForProposalResp {
	/**
	 * Required: false
	 * Array: true
	 */
	geoPricingGroupMembers:GeoPricingGroupMember[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Retrieves a list of Excluded Geo Pricing Group Members for a given Pricing Proposal Identifier
 */
export class ListGeoPricingGroupExclMembersForProposalRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListGeoPricingGroupExclMembersForProposalResp {
	/**
	 * Required: false
	 * Array: true
	 */
	geoPricingGroupExclMbrs:GeoPricingGroupMember[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to create the Proposed Strategy Records
 */
export class CreatePricingAdminProposedStrategyRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	proposedStrategy:PricingAdminProposedStrategy = undefined;
}
/**
 * Response message to create the Proposed Strategy Records
 */
export class CreatePricingAdminProposedStrategyResp {
	/**
	 * Required: true
	 * Array: false
	 */
	proposedStrategy:PricingAdminProposedStrategy = undefined;
}
/**
 * Input message to update and existing Proposed Strategy Record
 */
export class UpdatePricingAdminProposedStrategyRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	proposedStrategy:PricingAdminProposedStrategy = undefined;
}
/**
 * Input message to retrieve list of all Proposed Strategy Records
 */
export class ListPricingAdminProposedStrategyRqst {
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message to retrieve list of all Proposed Strategy Records
 */
export class ListPricingAdminProposedStrategyResp {
	/**
	 * Required: false
	 * Array: true
	 */
	proposedStrategies:PricingAdminProposedStrategy[] = undefined;
}
/**
 * Input message to delete an existing Proposed Strategy record
 */
export class DeletePricingAdminProposedStrategyRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	adminProposedStrategyPctId:string = undefined;
}
/**
 * Input message to create the Proposed Strategy Records
 */
export class CreatePricingAdminProposedIncreaseLevelRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	proposedIncreaseLevel:PricingAdminProposedIncrLevel = undefined;
}
/**
 * Response message to create the Proposed Strategy Records
 */
export class CreatePricingAdminProposedIncreaseLevelResp {
	/**
	 * Required: false
	 * Array: false
	 */
	proposedIncreaseLevel:PricingAdminProposedIncrLevel = undefined;
}
/**
 * Input message to update and existing Proposed Strategy Record
 */
export class UpdatePricingAdminProposedIncreaseLevelRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	proposedIncreaseLevel:PricingAdminProposedIncrLevel = undefined;
}
/**
 * Input message to retrieve list of all Proposed Strategy Records
 */
export class ListPricingAdminProposedIncreaseLevelRqst {
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message to retrieve list of all Proposed Strategy Records
 */
export class ListPricingAdminProposedIncreaseLevelResp {
	/**
	 * Required: false
	 * Array: true
	 */
	proposedIncreaseLevel:PricingAdminProposedIncrLevel[] = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to delete an existing Proposed Strategy record
 */
export class DeletePricingAdminProposedIncreaseLevelRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	adminProposedIncrLevelId:string = undefined;
}
/**
 * Input message to create the Proposed Strategy Records
 */
export class CreatePricingAdminFscPctLookupRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	fscPctLookup:PricingAdminFscPctLookup = undefined;
}
/**
 * Response message to create the Proposed Strategy Records
 */
export class CreatePricingAdminFscPctLookupResp {
	/**
	 * Required: true
	 * Array: false
	 */
	fscPctLookup:PricingAdminFscPctLookup = undefined;
}
/**
 * Input message to update and existing Proposed Strategy Record
 */
export class UpdatePricingAdminFscPctLookupRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	fscPctLookup:PricingAdminFscPctLookup = undefined;
}
/**
 * Input message to retrieve list of all Proposed Strategy Records
 */
export class ListPricingAdminFscPctLookupRqst {
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message to retrieve list of all Proposed Strategy Records
 */
export class ListPricingAdminFscPctLookupResp {
	/**
	 * Required: false
	 * Array: true
	 */
	fscPctLookup:PricingAdminFscPctLookup[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to delete an existing Proposed Strategy record
 */
export class DeletePricingAdminFscPctLookupRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	adminFscPctLookupId:string = undefined;
}
/**
 * Input message to create the Proposed Strategy Records
 */
export class CreatePricingAdminFakAdjustPctRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	fakAdjustPct:PricingAdminFakAdjustmentPct = undefined;
}
/**
 * Response message to create the Proposed Strategy Records
 */
export class CreatePricingAdminFakAdjustPctResp {
	/**
	 * Required: true
	 * Array: false
	 */
	fakAdjustPct:PricingAdminFakAdjustmentPct = undefined;
}
/**
 * Input message to update and existing Proposed Strategy Record
 */
export class UpdatePricingAdminFakAdjustPctRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	fakAdjustPct:PricingAdminFakAdjustmentPct = undefined;
}
/**
 * Input message to retrieve list of all Proposed Strategy Records
 */
export class ListPricingAdminFakAdjustPctRqst {
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Response message to retrieve list of all Proposed Strategy Records
 */
export class ListPricingAdminFakAdjustPctResp {
	/**
	 * Required: false
	 * Array: true
	 */
	fakAdjustPct:PricingAdminFakAdjustmentPct[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Input message to delete an existing Proposed Strategy record
 */
export class DeletePricingAdminFakAdjustPctRqst {
	/**
	 * unique identifier of Pricing Admin Fak Adjustment Pct record
	 * Required: false
	 * Array: false
	 */
	adminFakAdjustPctId:string = undefined;
}
/**
 * Request message to create the Pricing Agreement Monitor Parameter Record
 */
export class CreatePricingAgreementMonitorParameterRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	paMonitorParameters:PricingAgreementMonitorParameter = undefined;
}
/**
 * Request message to retrieve monitor parameter details by its Identifier
 */
export class CreatePricingAgreementMonitorParameterResp {
	/**
	 * Unique identifier of the Pricing Agreement Monitor Parameter record
	 * Required: true
	 * Array: false
	 */
	agreementMonitorParameterId:string = undefined;
}
/**
 * Request message to update an existing Pricing Agreement Monitor Parameter record
 */
export class UpdatePricingAgreementMonitorParameterRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	paMonitorParameter:PricingAgreementMonitorParameter = undefined;
}
/**
 * Request message to retrieve monitor parameter details by its Identifier
 */
export class GetPricingAgreementMonitorParameterRqst {
	/**
	 * Unique identifier of the Pricing Agreement Monitor Parameter record
	 * Required: true
	 * Array: false
	 */
	agreementMonitorParameterId:string = undefined;
}
/**
 * Request message to get an existing Pricing Agreement Monitor Parameter record
 */
export class GetPricingAgreementMonitorParameterResp {
	/**
	 * Pricing Agreement Monitor Parameter
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorParameter:PricingAgreementMonitorParameter = undefined;
}
/**
 * Request message to retrieve the list of all Pricing Agreement Monitor Parameter records
 */
export class ListPricingAgreementMonitorParameterRqst {
	/**
	 * pagination information to retrieve the list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAgreementMonitorParameterResp {
	/**
	 * pagination information to retrieve the list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Details about sales Pricing Agreement Monitor Parameters
	 * Required: false
	 * Array: true
	 */
	paMonitorParameters:PricingAgreementMonitorParameter[] = undefined;
}
/**
 * Request message to delete monitor parameter details by its Identifier
 */
export class DeletePricingAgreementMonitorParameterRqst {
	/**
	 * Unique identifier of the Pricing Agreement Monitor Parameter record
	 * Required: true
	 * Array: false
	 */
	agreementMonitorParameterId:string = undefined;
}
/**
 * Request message to retrieve Pricing Agreement Monitor Counts
 */
export class GetPricingAgreementMonitorCountsRqst {
	/**
	 * identifies the level of detail to be included in the response counts
	 * Required: false
	 * Array: false
	 */
	levelOfDetail:string = undefined;
	/**
	 * employee id of the PAM owner
	 * Required: false
	 * Array: false
	 */
	ownerId:string = undefined;
}
export class GetPricingAgreementMonitorCountsResp {
	/**
	 * Pricing Agreement Monitor record counts
	 * Required: false
	 * Array: true
	 */
	counts:CountInfo[] = undefined;
}
/**
 * Request message to create the Pricing Agreement Monitor Action Record
 */
export class CreatePricingAgreementMonitorActionRqst {
	/**
	 * Unique identifier of the Pricing Agreement Monitor record
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	paMonitorAction:PricingAgreementMonitorAction = undefined;
}
/**
 * Response message
 */
export class CreatePricingAgreementMonitorActionResp {
	/**
	 * Unique identifier of the Pricing Agreement Monitor Action record
	 * Required: true
	 * Array: false
	 */
	agreementMonitorActionId:string = undefined;
	/**
	 * Sales Rfp id of the rfp created for the specified action taken
	 * Required: true
	 * Array: false
	 */
	salesRfpId:object = undefined;
}
/**
 * Request message for the list of all Valid Actions for a Pricing Agreement Monitor Records
 */
export class ListPAMValidActionsRqst {
	/**
	 * Unique identifier of the Pricing Agreement Monitor record
	 * Required: false
	 * Array: false
	 */
	agreementMonitorId:string = undefined;
	/**
	 * pagination information to retrieve the list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	actionSource:string = undefined;
}
export class ListPAMValidActionsResp {
	/**
	 * pagination information to retrieve the list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Valid Actions for Pricing Agreement Monitor
	 * Required: false
	 * Array: true
	 */
	agreementMonitorValidActions:PricingAgreementMonitorValidAction[] = undefined;
}
/**
 * Request to get a list of Audit Next Revision Agreements
 */
export class ListPricingAgreementNextRevisionAuditRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	leadAcctId:number = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	rfpReqDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	period:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to get a list of recent Pricing AgreementAudit records
 */
export class ListPricingAgreementRecentAuditRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	loginUserId:string = undefined;
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to get a list of Pricing AgreementAudit records
 */
export class ListPricingAgreementAuditRqst {
	/**
	 * pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Start date of the date range to search.
	 * Required: false
	 * Array: false
	 */
	fromDate:Date = undefined;
	/**
	 * End date of the date range to search.
	 * Required: false
	 * Array: false
	 */
	toDate:Date = undefined;
	/**
	 * Current status of the Pricing Agreement Audit record
	 * Required: false
	 * Array: false
	 */
	auditStatus:string = undefined;
	/**
	 * Unique identifier of the Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Customer Account Id for which to search the Pricing Audit records
	 * Required: false
	 * Array: false
	 */
	custAcctId:string = undefined;
	/**
	 * Employee Id for whom the list details are get
	 * Required: true
	 * Array: false
	 */
	employeeId:string = undefined;
}
export class ListPricingAgreementAuditResp {
	/**
	 * Pagination information to retrieve list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	agreementAuditList:PricingAgreementAudit[] = undefined;
}
/**
 * Request message to update the Pricing Agreement Audit Header record
 */
export class UpdatePricingAgreementAuditHeaderRqst {
	/**
	 * Required: true
	 * Array: true
	 */
	auditHeader:PricingAgreementAuditHdr[] = undefined;
	/**
	 * Type of update to be performed. Eg. Reassignment
	 * Required: false
	 * Array: false
	 */
	updateType:string = undefined;
}
/**
 * Request message to update the Pricing Agreement Audit Header record
 */
export class UpdatePricingAgreementAuditHeaderResp {
	/**
	 * List of Agreement Audit Headers that were not updated
	 * Required: false
	 * Array: true
	 */
	errorList:AgreementAuditId[] = undefined;
}
/**
 * Request message to get the details of the Pricing Agreement Audit based on its Identifier
 */
export class GetPricingAgreementAuditDetailRqst {
	/**
	 * Unique identifier of the Pricing Agreement Audit Header record
	 * Required: true
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
}
export class GetPricingAgreementAuditDetailResp {
	/**
	 * Required: false
	 * Array: false
	 */
	auditDetail:PricingAgreementAudit = undefined;
}
export class CreatePricingAgreementAuditCommentRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	auditComment:PricingAgreementAuditComment = undefined;
	/**
	 * type of document to be uploaded
	 * Required: false
	 * Array: true
	 */
	docTypeCd:string[] = undefined;
	/**
	 * list of files to be uploaded
	 * Required: false
	 * Array: true
	 */
	docFiles:File[] = undefined;
}
export class CreatePricingAgreementAuditCommentResp {
	/**
	 * Required: true
	 * Array: false
	 */
	auditComment:PricingAgreementAuditComment = undefined;
}
export class CreatePricingAgreementAuditIssueRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	auditIssue:PricingAgreementAuditIssue = undefined;
}
export class CreatePricingAgreementAuditIssueResp {
	/**
	 * Required: false
	 * Array: false
	 */
	auditIssue:PricingAgreementAuditIssue = undefined;
}
export class UpdatePricingAgreementAuditIssueRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	auditIssue:PricingAgreementAuditIssue = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditIssueComment:PricingAgreementAuditComment = undefined;
	/**
	 * Audit event log id to update the status
	 * Required: false
	 * Array: false
	 */
	agreementAuditEventLogId:string = undefined;
}
export class UpdatePricingAgreementAuditIssueResp {
	/**
	 * Required: false
	 * Array: false
	 */
	auditIssue:PricingAgreementAuditIssue = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditIssueComment:PricingAgreementAuditComment = undefined;
}
export class DeletePricingAgreementAuditIssueRqst {
	/**
	 * Unique identifier of the Agreement Audit Issue record
	 * Required: false
	 * Array: false
	 */
	agreementAuditIssueId:string = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Audit Header record
	 * Required: false
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
}
export class DeletePricingAgreementAuditIssueResp {
	/**
	 * Unique identifier of the Agreement Audit Issue record
	 * Required: false
	 * Array: false
	 */
	agreementAuditIssueId:string = undefined;
	/**
	 * This is status allowed, based on this status the button will be displayed
	 * Required: false
	 * Array: false
	 */
	statusAllowed:string = undefined;
}
export class DeletePricingAgreementAuditEventLogRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	agreementAuditEventLogId:string = undefined;
}
export class CreatePricingAgreementAuditDocumentRqst {
	/**
	 * type of document to be uploaded
	 * Required: false
	 * Array: true
	 */
	docTypeCd:string[] = undefined;
	/**
	 * list of files to be uploaded
	 * Required: false
	 * Array: true
	 */
	docFiles:File[] = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Audit Header record
	 * Required: true
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
	/**
	 * Unique identifier of the Agreement Audit Comment
	 * Required: false
	 * Array: false
	 */
	agreementAuditCommentId:string = undefined;
	/**
	 * Unique identifier of the Agreement Audit Issue record
	 * Required: false
	 * Array: false
	 */
	agreementAuditIssueId:string = undefined;
}
export class CreatePricingAgreementAuditDocumentResp {
	/**
	 * Required: false
	 * Array: false
	 */
	auditIssue:PricingAgreementAuditIssue = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditIssueComment:PricingAgreementAuditComment = undefined;
}
export class GetPricingAgreementAuditDocumentRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	agreementAuditCommentId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementAuditIssueId:string = undefined;
}
export class GetPricingAgreementAuditDocumentResp {
	/**
	 * Required: false
	 * Array: false
	 */
	agreementAuditDocFile:File = undefined;
}
export class ListPricingAccessorialsByTypeRqst {
	/**
	 * Accessorial Type. Eg. Ratequote
	 * Required: false
	 * Array: false
	 */
	typeCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAccessorialsByTypeResp {
	/**
	 * Required: false
	 * Array: true
	 */
	accessorials:AccessorialService[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAccessorialsApplIndRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	typeCode:string = undefined;
}
export class ListPricingAccessorialsApplIndResp {
	/**
	 * Required: false
	 * Array: true
	 */
	accessorials:AccessorialService[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to create the Pricing Agreement Audit Parameter Record
 */
export class CreatePricingAgreementAuditParameterRqst {
	/**
	 * Required: true
	 * Array: true
	 */
	paAuditParameters:PricingAgreementAuditParameter[] = undefined;
}
/**
 * Request message to retrieve audit parameter details by its Identifier
 */
export class CreatePricingAgreementAuditParameterResp {
	/**
	 * Unique identifier of the Pricing Agreement Audit Parameter record
	 * Required: true
	 * Array: false
	 */
	agreementAuditParameterId:string = undefined;
}
/**
 * Request message to update an existing Pricing Agreement Audit Parameter record
 */
export class UpdatePricingAgreementAuditParameterRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	paAuditParameter:PricingAgreementAuditParameter = undefined;
}
/**
 * Request message to retrieve audit parameter details by its Identifier
 */
export class GetPricingAgreementAuditParameterRqst {
	/**
	 * Unique identifier of the Pricing Agreement Audit Parameter record
	 * Required: true
	 * Array: false
	 */
	agreementAuditParameterId:string = undefined;
}
/**
 * Request message to get an existing Pricing Agreement Audit Parameter record
 */
export class GetPricingAgreementAuditParameterResp {
	/**
	 * Pricing Agreement Audit Parameter
	 * Required: false
	 * Array: false
	 */
	pricingAgreementAuditParameter:PricingAgreementAuditParameter = undefined;
}
/**
 * Request message to retrieve the list of all Pricing Agreement Audit Parameter records
 */
export class ListPricingAgreementAuditParameterRqst {
	/**
	 * pagination information to retrieve the list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Start Effective Date for Active Audit Parameters
	 * Required: false
	 * Array: false
	 */
	startEffDate:Date = undefined;
	/**
	 * EndEffective Date for Active Audit Parameters
	 * Required: false
	 * Array: false
	 */
	endEffDate:Date = undefined;
}
export class ListPricingAgreementAuditParameterResp {
	/**
	 * pagination information to retrieve the list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Details about sales Pricing Agreement Audit Parameters
	 * Required: false
	 * Array: true
	 */
	paAuditParameters:PricingAgreementAuditParameter[] = undefined;
}
/**
 * Request message to delete audit parameter details by its Identifier
 */
export class DeletePricingAgreementAuditParameterRqst {
	/**
	 * Unique identifier of the Pricing Agreement Audit Parameter record
	 * Required: true
	 * Array: false
	 */
	agreementAuditParameterId:string = undefined;
}
export class ListPricingAgreementMonitorRunDateRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
export class ListPricingAgreementMonitorRunDateResp {
	/**
	 * List of dates when the PAM was run
	 * Required: false
	 * Array: true
	 */
	pamRunDate:Date[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request message to re-assign the PAM record
 */
export class ReassignPricingAgreementMonitorRqst {
	/**
	 * Employee id of the Current Owner of the PAM record
	 * Required: false
	 * Array: true
	 */
	pamOwner:PAMOwner[] = undefined;
	/**
	 * This is the from user role  (Sales/Pricing Analyst/Nass/Dos/PricingManger) to transfer
	 * Required: false
	 * Array: false
	 */
	fromOwnerRole:string = undefined;
	/**
	 * Employee id of the new Owner of the PAM record
	 * Required: true
	 * Array: false
	 */
	toOwnerId:string = undefined;
}
export class ReassignPricingAgreementMonitorResp {
	/**
	 * Count of PAM records reassigned
	 * Required: false
	 * Array: false
	 */
	reassignedCount:CountInfo = undefined;
}
/**
 * Request message to create the Pricing Agreement Monitor Record
 */
export class CreatePricingAgreementMonitorRqst {
	/**
	 * Required: true
	 * Array: true
	 */
	paMonitors:PricingAgreementMonitor[] = undefined;
}
/**
 * Request message to retrieve monitor details by its Identifier
 */
export class CreatePricingAgreementMonitorResp {
	/**
	 * Pricing Agreement Monitor Create results
	 * Required: false
	 * Array: true
	 */
	pricingAgreementMonitorResults:CreatePricingAgreementMonitorResult[] = undefined;
}
/**
 * Result from Create Pricing Agreement Monitor Request
 */
export class CreatePricingAgreementMonitorResult {
	/**
	 * Unique identifier of the Pricing Agreement Monitor record
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorId:string = undefined;
	/**
	 * Result message for the Pricing Agreement Monitor Create
	 * Required: false
	 * Array: false
	 */
	message:string = undefined;
}
/**
 * Request message to update an existing Pricing Agreement Monitor record
 */
export class UpdatePricingAgreementMonitorRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	paMonitor:PricingAgreementMonitor = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	pricingAgreementMonitorId:string = undefined;
}
/**
 * Request message to retrieve monitor details by its Identifier
 */
export class GetPricingAgreementMonitorRqst {
	/**
	 * Unique identifier of the Pricing Agreement Monitor record
	 * Required: true
	 * Array: false
	 */
	pricingAgreementMonitorId:string = undefined;
}
/**
 * Request message to get an existing Pricing Agreement Monitor record
 */
export class GetPricingAgreementMonitorResp {
	/**
	 * Pricing Agreement Monitor
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitor:PricingAgreementMonitor = undefined;
}
/**
 * Request message to retrieve the list of all Pricing Agreement Monitor records
 */
export class ListPricingAgreementMonitorRqst {
	/**
	 * pagination information to retrieve the list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * the year and month the PAM record was ran for
	 * Required: false
	 * Array: false
	 */
	startYearMonth:Date = undefined;
	/**
	 * pricing type code: Local or National
	 * Required: false
	 * Array: false
	 */
	pricingTypeCd:string = undefined;
	/**
	 * The instance ID of the customer that negotiated the pricing agreement for this PAM record
	 * Required: false
	 * Array: false
	 */
	leadAcctId:string = undefined;
	/**
	 * The version of the pricing agreement for this PAM record
	 * Required: false
	 * Array: false
	 */
	agreementCustVersion:string = undefined;
	/**
	 * The sequence number within the version of pricing agreement for this PAM record
	 * Required: false
	 * Array: false
	 */
	agreementSeqNbr:string = undefined;
	/**
	 * Sales District
	 * Required: false
	 * Array: false
	 */
	salesDistrict:string = undefined;
	/**
	 * Sales Region
	 * Required: false
	 * Array: false
	 */
	salesRegion:string = undefined;
	/**
	 * Employee Id of the Sales person who owns this Monitor record
	 * Required: false
	 * Array: false
	 */
	salesOwnerEmployeeId:string = undefined;
	/**
	 * Status of the Pricing Agreement Monitor record
	 * Required: false
	 * Array: false
	 */
	statusCd:string = undefined;
	/**
	 * Pricing Agreement Monitor Type
	 * Required: false
	 * Array: false
	 */
	typeCd:string = undefined;
	/**
	 * the year and month the PAM record was ran for
	 * Required: false
	 * Array: false
	 */
	endYearMonth:Date = undefined;
}
export class ListPricingAgreementMonitorResp {
	/**
	 * pagination information to retrieve the list
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Details about sales Pricing Agreement Monitor records
	 * Required: false
	 * Array: true
	 */
	paMonitors:PricingAgreementMonitor[] = undefined;
	/**
	 * Action allowed for this list
	 * Required: false
	 * Array: false
	 */
	actionAllowed:string = undefined;
}
/**
 * Request message to retrieve the list of all Pricing Agreement Monitor Groups
 */
export class GetPricingAgreementMonitorRegionRollupReportRqst {
	/**
	 * the year and month the PAM record was ran for
	 * Required: false
	 * Array: false
	 */
	yearMonth:Date = undefined;
	/**
	 * status: Open or Closed
	 * Required: false
	 * Array: false
	 */
	statusCd:string = undefined;
	/**
	 * Employee Id for the report requester
	 * Required: true
	 * Array: false
	 */
	requesterId:string = undefined;
}
export class GetPricingAgreementMonitorRegionRollupReportResp {
	/**
	 * Pricing Agreement Monitor Region Rollup Report
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorRegionRollupReport:PricingAgreementMonitorRegionRollupReport = undefined;
}
/**
 * Input message to run the Input hierarchy model for a given Sales Rfp Id
 */
export class RunInputHierarchyModelRqst {
	/**
	 * Unique id of the Sales Rfp
	 * Required: true
	 * Array: false
	 */
	salesRfpId:object = undefined;
}
/**
 * Input message to run the Aggregation model for a given Pricing Proposal Id and Aggregate Model Template Id
 */
export class RunAggregationModelRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	aggrModelTemplateId:string = undefined;
	/**
	 * Unique id for the proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Input message to run the Leaf Model for a given Pricing Proposal Id
 */
export class RunLeafModelRqst {
	/**
	 * Unique id for the proposal
	 * Required: true
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
/**
 * Request message to get a list of counts for Pricing Navigation Bar
 */
export class ListPricingNavigationBarCountsRqst {
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
	/**
	 * Employee Id of the owner
	 * Required: false
	 * Array: false
	 */
	ownerId:string = undefined;
}
export class ListPricingNavigationBarCountsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	navigationBarCount:CountInfo[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of Customer Pricing Agreements Doc for input Customer number
 */
export class ListPricingAgreementDocRqst {
	/**
	 * Acct Id of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	custAcctId:number = undefined;
	/**
	 * Pricing agreement details customer sequence and version
	 * Required: false
	 * Array: false
	 */
	pricingAgreementId:string = undefined;
	/**
	 * Account code of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	custAcctCd:string = undefined;
	/**
	 * pagination information for list retrieval
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of Pricing Agreement Doc details with pricing agreement version details, doc header and doc type Identifier Information
 */
export class ListPricingAgreementDocResp {
	/**
	 * Customer account pricing agreement doc details
	 * Required: false
	 * Array: true
	 */
	pricingAgreementDocHdrDetail:PricingAgreementDocHdr[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	custDetails:AcctId[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Update pricing agreement document and type details
 */
export class UpdatePricingAgreementDocHdrRqst {
	/**
	 * pricing agreement document header details
	 * Required: false
	 * Array: false
	 */
	agreementDocHdrDetail:PricingAgreementDocHdr = undefined;
}
/**
 * Update pricing agreement document header details
 */
export class UpdatePricingAgreementDocHdrResp {
	/**
	 * agreement document header details
	 * Required: false
	 * Array: false
	 */
	agreementDocHdrDetail:PricingAgreementDocHdr = undefined;
}
/**
 * Request for create pricing agreement doc header
 */
export class CreatePricingAgreementDocHdrRqst {
	/**
	 * Pricing agreement doc header details
	 * Required: true
	 * Array: false
	 */
	agreementDocHdrDetail:PricingAgreementDocHdr = undefined;
}
/**
 * Response for create pricing agreement doc header details
 */
export class CreatePricingAgreementDocHdrResp {
	/**
	 * Agreement  document header details
	 * Required: true
	 * Array: false
	 */
	agreementDocHdrDetail:PricingAgreementDocHdr = undefined;
}
/**
 * Request for upload pricing agreement doc type
 */
export class CreatePricingAgreementDocumentRqst {
	/**
	 * Agreement Document ID
	 * Required: true
	 * Array: false
	 */
	agreementDocHdrId:number = undefined;
	/**
	 * Document type
	 * Required: false
	 * Array: true
	 */
	docTypeCd:string[] = undefined;
	/**
	 * Document contents
	 * Required: false
	 * Array: true
	 */
	docFiles:File[] = undefined;
	/**
	 * Agreement Monitor Id
	 * Required: false
	 * Array: false
	 */
	agreementMonitorId:number = undefined;
}
export class CreatePricingAgreementDocumentResp {
	/**
	 * Required: true
	 * Array: false
	 */
	agreementDocHdrDetail:PricingAgreementDocHdr = undefined;
}
/**
 * Request for download pricing agreement document
 */
export class GetPricingAgreementDocumentRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	agreementDocHdrId:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	docTypeId:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	oracleDocId:number = undefined;
}
/**
 * Reaponse to get the down load pricing agreement document
 */
export class GetPricingAgreementDocumentResp {
	/**
	 * Required: false
	 * Array: false
	 */
	agreementDocTyp:PricingAgreementDocType = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementDocFile:File = undefined;
}
/**
 * Request for download pricing agreement documents
 */
export class GetPricingAgreementDocumentsRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	agreementDocHdrId:number = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	docTypeId:number[] = undefined;
}
/**
 * Response to get the down load pricing agreement documents
 */
export class GetPricingAgreementDocumentsResp {
	/**
	 * Required: false
	 * Array: false
	 */
	agreementDocFile:File = undefined;
}
/**
 * Request for getting the pricing agreement document Header Details
 */
export class GetPricingAgreementDocHdrRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	agreementDocHdrId:number = undefined;
	/**
	 * Agreement Monitor Id
	 * Required: false
	 * Array: false
	 */
	agreementMonitorId:number = undefined;
}
/**
 * Response to get pricing agreement document header details
 */
export class GetPricingAgreementDocHdrResp {
	/**
	 * agreement document header details
	 * Required: false
	 * Array: false
	 */
	agreementDocHdrDetail:PricingAgreementDocHdr = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	pricingAgreements:string[] = undefined;
}
/**
 * Request for Delete  pricing agreement documents
 */
export class DeletePricingAgreementDocumentRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	agreementDocHdrId:number = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	docTypeId:number = undefined;
}
/**
 * Request for getting the list of corp Customer  and Pricing Agreements
 */
export class SearchPricingAgreementCustomerRqst {
	/**
	 * Acct Id of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	custAcctId:number = undefined;
	/**
	 * pagination information for list retrieval
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Respose of list of corp customers and agreement for the specified customer ID
 */
export class SearchPricingAgreementCustomerResp {
	/**
	 * Customer account pricing agreements
	 * Required: false
	 * Array: true
	 */
	pricingAgreements:string[] = undefined;
	/**
	 * list of Corp customers
	 * Required: false
	 * Array: true
	 */
	custDetails:AcctId[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Request for Delete  pricing agreement document Header
 */
export class DeletePricingAgreementDocHdrRqst {
	/**
	 * Required: true
	 * Array: false
	 */
	agreementDocHdrId:number = undefined;
}
/**
 * List of Customers (customer hierarchy) for input Customer number/code
 */
export class ListPrcCustomerHierarchyRqst {
	/**
	 * Acct Id of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	custAcctId:number = undefined;
	/**
	 * Account code of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	custAcctCd:string = undefined;
	/**
	 * pagination information for list retrieval
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of Customer (customer hierarchy) details including customenr name, address, credti status, type and primary location
 */
export class ListPrcCustomerHierarchyResp {
	/**
	 * Required: false
	 * Array: true
	 */
	custDetails:AccountDetail[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * This request type will be used to retrieve basic identifying customer location information for the children of a corporate location
 */
export class ListPricingCustomerAccountRqst {
	/**
	 * The database key identifying a customer location record to be retrieved or acted upon
	 * Required: false
	 * Array: true
	 */
	acctInstId:string[] = undefined;
	/**
	 * customer name
	 * Required: false
	 * Array: false
	 */
	acctName:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	acctPartyRole:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	addressLine1:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cityName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	stateCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	countryCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	postalCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of Customer account details including customer name, address, credit status, type and primary location
 */
export class ListPricingCustomerAccountResp {
	/**
	 * Required: false
	 * Array: true
	 */
	custDetails:AccountDetail[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * This request type will be used to retrieve basic identifying customer detail information for a given Customer Id or Customer code
 */
export class ListPricingCustomerDetailsRqst {
	/**
	 * customer id to be retrieved or acted upon
	 * Required: false
	 * Array: true
	 */
	acctId:number[] = undefined;
	/**
	 * customer code to be retrieved or acted upon
	 * Required: false
	 * Array: true
	 */
	acctCd:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * List of Customer account details including customer name, address, credit status, type and primary location
 */
export class ListPricingCustomerDetailsResp {
	/**
	 * Required: false
	 * Array: true
	 */
	custDetails:AccountDetail[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	listInfo:ListInfo = undefined;
}
/**
 * Create renegotiation sales rfp request.
 */
export class CreateRenegotiationSalesRfpsRqst {
	/**
	 * Acct Id of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	acctId:string = undefined;
	/**
	 * option to process request
	 * Required: false
	 * Array: false
	 */
	option:string = undefined;
	/**
	 * This si the status the rfp to be created
	 * Required: false
	 * Array: false
	 */
	status:string = undefined;
	/**
	 * This is remarks to be added on creating RFP
	 * Required: false
	 * Array: false
	 */
	remarks:string = undefined;
}
export class AccessorialCharge {
	/**
	 * The minimum units for which this charge applies.
	 * Required: false
	 * Array: false
	 */
	minUnitCount:number = undefined;
	/**
	 * The maximum units for which this charge applies
	 * Required: false
	 * Array: false
	 */
	maxUnitCount:number = undefined;
	/**
	 * This is the rate at which the accessorial service is charged. This is mutually exclusive with ratePct and will not be specified if ratePct is specified.
	 * Required: false
	 * Array: false
	 */
	rateAmt:MonetaryAmount = undefined;
	/**
	 * This specfies the percentage to be used for calculating the charges for the accessorial service. Mutually exclusive with rateAmt
	 * Required: false
	 * Array: false
	 */
	ratePct:number = undefined;
	/**
	 * sequence number in which the charge should appear
	 * Required: false
	 * Array: false
	 */
	seqNbr:number = undefined;
}
export class AccessorialAppIndicator {
	/**
	 * unique identifier
	 * Required: false
	 * Array: false
	 */
	accessorialSvcApplIndId:string = undefined;
	/**
	 * Name of the application.
	 * Required: false
	 * Array: false
	 */
	applName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	indicatorKey:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	indicatorValue:string = undefined;
}
/**
 * Information about global accessorials and how to calculate their charge amounts
 */
export class AccessorialService {
	/**
	 * This code also identifies the accessorial charge.
	 * Required: false
	 * Array: false
	 */
	accessorialCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialDesc:string = undefined;
	/**
	 * Date the Accessorial charge was effective.
	 * Required: false
	 * Array: false
	 */
	effDate:Date = undefined;
	/**
	 * Date the Accessorial charge was expired.
	 * Required: false
	 * Array: false
	 */
	expDate:Date = undefined;
	/**
	 * Name of the rules tariff applicable to the rule set.
	 * Required: false
	 * Array: false
	 */
	rulesTariffName:string = undefined;
	/**
	 * 1- Flat Rate per UOM
	 * 2 - % of Specified Charge type
	 * Required: false
	 * Array: false
	 */
	chargeTypeCd:string = undefined;
	/**
	 * This is the unit of measure for specifying the maximum and minimum limits for the accessorial service.
	 * Required: false
	 * Array: false
	 */
	accessorialUom:string = undefined;
	/**
	 * Specifies the type of charge which is used for calculating the charges for the accessorial service using the RATE_PCT.
	 * Required: false
	 * Array: false
	 */
	pctChgTypeCd:string = undefined;
	/**
	 * Describes whether the accessorial is applicable at Origin or Destination or Both
	 * Required: false
	 * Array: false
	 */
	termCreditCd:string = undefined;
	/**
	 * This indicates if the charges for the accessorial service are tiered by weight or other units of measure.
	 * Required: false
	 * Array: false
	 */
	tieredInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	negotiableInd:boolean = undefined;
	/**
	 * This indicates if the charges for the accessorial service are tiered by weight or other units of measure.
	 * Required: false
	 * Array: false
	 */
	minChgAmt:MonetaryAmount = undefined;
	/**
	 * This is the maximum charge amount that will be charged if the accessorial service was performed.
	 * Required: false
	 * Array: false
	 */
	maxChgAmt:MonetaryAmount = undefined;
	/**
	 * Indicates whether the charge for the accesorial service is discountable or not.
	 * Required: false
	 * Array: false
	 */
	accessorialDiscInd:boolean = undefined;
	/**
	 * Indicates whether the AC is a literal or not.
	 * Required: false
	 * Array: false
	 */
	literalInd:boolean = undefined;
	/**
	 * Each Accessorial is identified by Item number in CNWY199 guide
	 * Required: false
	 * Array: false
	 */
	itemText:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	accessorialAppInd:AccessorialAppIndicator[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	accessorialChg:AccessorialCharge[] = undefined;
}
/**
 * Basic customer account info
 */
export class AccountDetail {
	/**
	 * Required: false
	 * Array: false
	 */
	acctId:AcctId = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	address:Address = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	Indentation:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	primaryLocation:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	Owner:string = undefined;
}
export class AggregateModelConstraintTemplate {
	/**
	 * Required: false
	 * Array: false
	 */
	aggrModelConstrTemplateId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	aggrModelTemplateId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	constraintDimension:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	constraintSequence:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	constraintType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	dimensionLowerLimit:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	dimensionScope:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	dimensionUpperLimit:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	priority:number = undefined;
}
/**
 * Information about Geo Pricing Group Lane keys that define the set of Geo information to be considered by the Aggregation Model for the current Proposal
 */
export class AggregateModelGeoStaging {
	/**
	 * Identifier of the Aggr Model Geo Staging record
	 * Required: false
	 * Array: false
	 */
	aggrModelGeoStagingId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Identifier of the Geo Pricing Group Lane record
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	isActiveInd:boolean = undefined;
	/**
	 * Identifier of the Pricing Proposal record
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
}
export class AggregateModelTemplate {
	/**
	 * Required: false
	 * Array: false
	 */
	aggrModelConstraintTemplate:AggregateModelConstraintTemplate = undefined;
	/**
	 * unique identifier of aggregate model template
	 * Required: false
	 * Array: false
	 */
	aggrModelTemplateId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	amcType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destinationGeo:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	discountGroup:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	discountType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	originGeo:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingMatchType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	weightBreakPricing:string = undefined;
}
/**
 * Identifier of the Pricing Agreement Audit
 */
export class AgreementAuditId {
	/**
	 * Unique identifier of the Pricing Agreement Audit Header record
	 * Required: false
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
}
/**
 * Information about Shipment summary for a given Pricing Agreement
 */
export class AgreementShipmentSummary {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAgreement:PricingAgreement = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	shipmentSummary:ShipmentSummary[] = undefined;
}
export class AgrmtAcct {
	/**
	 * Required: false
	 * Array: false
	 */
	acctId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	acctPartyRole:string = undefined;
}
/**
 * Factors involved in the calculation of CDI
 */
export class AnalysisFactorCdi {
	/**
	 * Sales Volume code value
	 * Required: false
	 * Array: false
	 */
	custSalesVolCd:string = undefined;
	/**
	 * NAICS code value
	 * Required: false
	 * Array: false
	 */
	naicsCd:string = undefined;
	/**
	 * salesRegionCd
	 * Required: false
	 * Array: false
	 */
	salesRegionCd:string = undefined;
	/**
	 * Recommended lower bound discount percentage for this combination of sales volume, naics and sales region.
	 * Required: false
	 * Array: false
	 */
	lowerBoundCDIPct:number = undefined;
	/**
	 * Recommended upper bound discount percentage for this combination of sales volume, naics and sales region.
	 * Required: false
	 * Array: false
	 */
	upperBoundCDIPct:number = undefined;
	/**
	 * Recommended optimal discount percentage for this combination of sales volume, naics and sales region.
	 * Required: false
	 * Array: false
	 */
	optimalCDIPct:number = undefined;
}
/**
 * Factors involved in the calculation of FAK Discount Impact
 */
export class AnalysisFactorFakDi {
	/**
	 * actual commodity class
	 * Required: false
	 * Array: false
	 */
	actualNmfcClass:string = undefined;
	/**
	 * as rated commodity class
	 * Required: false
	 * Array: false
	 */
	fakNmfcClass:string = undefined;
	/**
	 * Indicates the factor to be used in the calculation of the fak discount impact
	 * Required: false
	 * Array: false
	 */
	fakAdjPct:number = undefined;
}
/**
 * Factors involved in the calculation of FSC Discount Impact
 */
export class AnalysisFactorFscDi {
	/**
	 * FSC price per gallon lower range
	 * Required: false
	 * Array: false
	 */
	fscPerGalLowerBoundAmt:MonetaryAmount = undefined;
	/**
	 * FSC price per gallon lower range
	 * Required: false
	 * Array: false
	 */
	fscPerGalUpperBoundAmt:MonetaryAmount = undefined;
	/**
	 * Indicates the factor to be used in the calculation of the fsc discount impact
	 * Required: false
	 * Array: false
	 */
	fscDiscountPct:number = undefined;
}
/**
 * Commodity class overrides in the Pricing Agreement
 */
export class ClassOverride {
	/**
	 * Required: false
	 * Array: false
	 */
	fakOverrideId:string = undefined;
	/**
	 * Commodity NMFC class
	 * Required: false
	 * Array: false
	 */
	commodityClass:string = undefined;
	/**
	 * NMFC item that along with class uniquely defines the type of Freight
	 * Required: false
	 * Array: false
	 */
	commodityNmfcItem:string = undefined;
	/**
	 * FAK that overrides the NMFC class code and NMFC Item
	 * Required: false
	 * Array: false
	 */
	commodityFak:string = undefined;
	/**
	 * percent of business done by this commodity
	 * Required: false
	 * Array: false
	 */
	pctOfBusiness:number = undefined;
	/**
	 * Discount Impact for shipments with FSC by Class
	 * Required: false
	 * Array: false
	 */
	classDiscountImpactFsc:number = undefined;
	/**
	 * Discount Impact for shipments without FSC by Class
	 * Required: false
	 * Array: false
	 */
	classDiscountImpactNoFsc:number = undefined;
}
/**
 * The variable parameters that are included in the standard Pricing Agreement
 */
export class ContractBoilerPlate {
	/**
	 * This contains the amount per pound of liability this customer is asking for.
	 * Required: false
	 * Array: false
	 */
	liabilityLimit:MonetaryAmount = undefined;
	/**
	 * Payment terms if other than that standard corporate
	 * Required: false
	 * Array: false
	 */
	paymentTerms:string = undefined;
	/**
	 * Length of pricing agreement if other than one year if the contract is signed agreement
	 * Required: false
	 * Array: false
	 */
	pricingTerms:string = undefined;
	/**
	 * Any other comments to be included in the contract
	 * Required: false
	 * Array: false
	 */
	boilerPlateComments:string = undefined;
}
/**
 * File layout  of representative shipments composed of lanes, commodities, weights, density for a customer, maybe based on actual historical shipments or derived shipments. 
 */
export class CustomerShipment {
	/**
	 * Type of Billing - B (Both), S(Shipper), C(Consignee)
	 * Required: false
	 * Array: false
	 */
	billingTypeCd:string = undefined;
	/**
	 * The commodity class without FAK
	 * Required: false
	 * Array: false
	 */
	shipmentActualClass:string = undefined;
	/**
	 * Delivery date for shipment
	 * Required: false
	 * Array: false
	 */
	shipmentDeliveryDate:Date = undefined;
	/**
	 * Density of shipment
	 * Required: false
	 * Array: false
	 */
	shipmentDensity:number = undefined;
	/**
	 * The unit of measure used to represent the density - eg. kilograms per cubic meter or pounds per cubic foot.
	 * Required: false
	 * Array: false
	 */
	shipmentDensityUom:string = undefined;
	/**
	 * SIC code for destination location
	 * Required: false
	 * Array: false
	 */
	shipmentDestSic:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shipmentDestStateCd:string = undefined;
	/**
	 * Zip code for dest location
	 * Required: false
	 * Array: false
	 */
	shipmentDestZip:string = undefined;
	/**
	 * Identifying number for sample shipment
	 * Required: false
	 * Array: false
	 */
	shipmentId:string = undefined;
	/**
	 * SIC code for originating location
	 * Required: false
	 * Array: false
	 */
	shipmentOrigSic:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shipmentOrigStateCd:string = undefined;
	/**
	 * Zip code for origin location
	 * Required: false
	 * Array: false
	 */
	shipmentOrigZip:string = undefined;
	/**
	 * Number of lading units that are pallets
	 * Required: false
	 * Array: false
	 */
	shipmentPallets:number = undefined;
	/**
	 * The perrcentage of business sharing the same characteristics as this sample shipment
	 * Required: false
	 * Array: false
	 */
	shipmentPctOfBusiness:number = undefined;
	/**
	 * Pickup date for shipment
	 * Required: false
	 * Array: false
	 */
	shipmentPickupDate:Date = undefined;
	/**
	 * Number of lading units
	 * Required: false
	 * Array: false
	 */
	shipmentPieceCount:number = undefined;
	/**
	 * Weight of shipment
	 * Required: false
	 * Array: false
	 */
	shipmentWeight:Weight = undefined;
	/**
	 * Standard tariff revenue for a shipment of this type ???
	 * Required: false
	 * Array: false
	 */
	tariffRevenue:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shipmentDistance:Distance = undefined;
}
/**
 * File of representative shipments composed of lanes, commodities, weights, density for a customer, maybe based on actual historical shipments or derived shipments. 
 */
export class CustomerShipmentsFile {
	/**
	 * Required: false
	 * Array: false
	 */
	custShipmentFileId:string = undefined;
	/**
	 * defines if from BI historical, derived, provided by customer
	 * Required: false
	 * Array: false
	 */
	shipmentFileSrc:string = undefined;
	/**
	 * customer shipments
	 * Required: false
	 * Array: false
	 */
	custShipments:CustomerShipment = undefined;
}
/**
 * Header information for Customer Shipment file
 */
export class CustomerShipmentsFileHdr {
	/**
	 * unique identifier of the Rfp Customer Shipment file Header record
	 * Required: false
	 * Array: false
	 */
	rfpCustShipmentFileHdrId:string = undefined;
	/**
	 * unique identifier from the source of the customer shipment file
	 * Required: false
	 * Array: false
	 */
	sourceId:string = undefined;
	/**
	 * count of number of records from the source system
	 * Required: false
	 * Array: false
	 */
	sourceRecordCount:number = undefined;
	/**
	 * type of source
	 * Required: false
	 * Array: false
	 */
	sourceType:string = undefined;
}
/**
 * Associates a discount percentage with a particular segment of a Variable discountTypeCd - by Weight Break, by Traffic type or lane-based
 */
export class DiscountPctByVarSegment {
	/**
	 * Code for the variable discount segment - example intrastate, interstate, international, L5C, 5C, 1M, 2M
	 * Required: false
	 * Array: false
	 */
	discountSegmentTypeCd:string = undefined;
	/**
	 * Discount percentage for the particular variable discount segment
	 * Required: false
	 * Array: false
	 */
	discountPct:MonetaryAmount = undefined;
}
export class FakAdjustment {
	/**
	 * Required: false
	 * Array: false
	 */
	requestedFscDiscountPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fscAmountPerGal:number = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	classOverrides:ClassOverride[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	requestedTariffDiscountPct:number = undefined;
}
/**
 * geographic area that shares common pricing elements, including tariffs, discounts, AMCs, FAK
 */
export class GeoAreaPricingGroup {
	/**
	 * unique id of geo area pricing
	 * Required: false
	 * Array: false
	 */
	geoAreaPrcId:string = undefined;
	/**
	 * A textual identification of a grouping of rates and discounts
	 * Required: false
	 * Array: false
	 */
	pricingGroupName:string = undefined;
	/**
	 * The lanes in this pricing
	 * Required: false
	 * Array: false
	 */
	geoPricingLanes:GeoAreaPricingLane = undefined;
	/**
	 * Identifies the rating type for the group of rates and discount - tariff, commodity
	 * Required: false
	 * Array: false
	 */
	rateTypeCd:string = undefined;
	/**
	 * Identifies the RTAR code or RTAR name for the tariff for a group of rates and discounts.
	 * Required: false
	 * Array: false
	 */
	ratingTariffName:string = undefined;
	/**
	 * This field allows the sales gent to enter a text describing the tariff when they do not know the RTAR or tariff name in our system
	 * Required: false
	 * Array: false
	 */
	ratingTariffDesc:string = undefined;
	/**
	 * Name of the rules tariff applicable for this RFP.
	 * Required: false
	 * Array: false
	 */
	rulesTariffName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rulesTariffDesc:string = undefined;
	/**
	 * Name of the tariff that governs how the fuel surcharge will be applied for this customer..
	 * Required: false
	 * Array: false
	 */
	fscTariffName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fscTariffDesc:string = undefined;
	/**
	 * The discount against the fuel surcharge that is being asked for.
	 * Required: false
	 * Array: false
	 */
	requestedFscDiscountPct:number = undefined;
	/**
	 * A code that qualifies a request by service of transportation.
	 * Required: false
	 * Array: false
	 */
	serviceTypeCd:string = undefined;
	/**
	 * Indicator if the rates and discounts apply to third party shipments
	 * Required: false
	 * Array: false
	 */
	thirdPartyInd:boolean = undefined;
	/**
	 * The requested FAKs for the customer
	 * Required: false
	 * Array: true
	 */
	requestedFak:PricingFak[] = undefined;
	/**
	 * Captures the type of Absolute Minimum Charge offered: Single, Variable By Lane Type
	 * Required: false
	 * Array: false
	 */
	amcTypeCd:string = undefined;
	/**
	 * Captures the type of Discount offered. Single, Variable By Weight, Variable By Lane Type
	 * Required: false
	 * Array: false
	 */
	discountTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * geographic area that shares common pricing elements, including tariffs, discounts, AMCs, FAK
 */
export class GeoAreaPricingLane {
	/**
	 * unique id of geo area pricing lane record for sales rfp
	 * Required: false
	 * Array: false
	 */
	geoAreaPrcLaneId:string = undefined;
	/**
	 * unique id of geo area pricing lane record for pricing proposal
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneId:string = undefined;
	/**
	 * geographic scope for the lane
	 * Required: false
	 * Array: false
	 */
	lane:Lane = undefined;
	/**
	 * Proposed pricing factors for some or all of the lading units described in this proosal. The lading units referenced are in the request type and referenced here by id.
	 * Required: false
	 * Array: true
	 */
	ladingUnitBasedPricing:LadingUnitBasedPricing[] = undefined;
	/**
	 * requested discount for this lane
	 * Required: false
	 * Array: true
	 */
	requestedDiscount:PricingDiscount[] = undefined;
	/**
	 * requested AMC for this lane
	 * Required: false
	 * Array: true
	 */
	requestedAmc:PricingAmc[] = undefined;
	/**
	 * parts of the defined area that are excluded from this pricing lane
	 * Required: false
	 * Array: true
	 */
	geoAreaLaneExclusions:Lane[] = undefined;
	/**
	 * textual identification of a grouping of rates and discounts
	 * Required: false
	 * Array: false
	 */
	pricingGroupName:string = undefined;
	/**
	 * identifies the rating type for the group of rates and discount - tariff and commodity
	 * Required: false
	 * Array: false
	 */
	rateTypeCd:string = undefined;
	/**
	 * identifies the RTAR code or RTAR name for the tariff for a group of rates and discounts
	 * Required: false
	 * Array: false
	 */
	ratingTariffName:string = undefined;
	/**
	 * textual description of rating tariff name
	 * Required: false
	 * Array: false
	 */
	ratingTariffDesc:string = undefined;
	/**
	 * name of the rules tariff applicable for this rfp
	 * Required: false
	 * Array: false
	 */
	rulesTariffName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rulesTariffDesc:string = undefined;
	/**
	 * Name of the tariff that governs how the fuel surcharge will be applied for this customer.
	 * Required: false
	 * Array: false
	 */
	fscTariffName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fscTariffDesc:string = undefined;
	/**
	 * the discount against fuel surcharge that is being asked for
	 * Required: false
	 * Array: false
	 */
	requestedFscDiscountPct:number = undefined;
	/**
	 * A code that qualifies a request by service of transportation.
	 * Required: false
	 * Array: false
	 */
	serviceTypeCd:string = undefined;
	/**
	 * Indicator if the rates and discounts apply to third party shipments
	 * Required: false
	 * Array: false
	 */
	thirdPartyInd:boolean = undefined;
	/**
	 * Captures the type of Absolute Minimum Charge offered: Single, Variable By Lane Type
	 * Required: false
	 * Array: false
	 */
	amcTypeCd:string = undefined;
	/**
	 * Captures the type of Discount offered. Single, Variable By Weight, Variable By Lane Type
	 * Required: false
	 * Array: false
	 */
	discountTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	requestedFaks:PricingFak = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	proposedFaks:PricingFak[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Captures the Discount and Absolute Min Charge information for Origin and Destination geo areas
 */
export class GeoDiscountAmcSummary {
	/**
	 * unique identifier of the Geo Discount Amc Summary record
	 * Required: false
	 * Array: false
	 */
	geoDiscAmcSummaryId:string = undefined;
	/**
	 * origin state code
	 * Required: false
	 * Array: false
	 */
	origStateCd:string = undefined;
	/**
	 * unique identifier of the origin group
	 * Required: false
	 * Array: false
	 */
	origGroupId:string = undefined;
	/**
	 * Origin group name
	 * Required: false
	 * Array: false
	 */
	origGroupName:string = undefined;
	/**
	 * destination state code
	 * Required: false
	 * Array: false
	 */
	destStateCd:string = undefined;
	/**
	 * unique identifier of destination group
	 * Required: false
	 * Array: false
	 */
	destGroupId:string = undefined;
	/**
	 * destination group name
	 * Required: false
	 * Array: false
	 */
	destGroupName:string = undefined;
	/**
	 * current discount percentage
	 * Required: false
	 * Array: false
	 */
	currDiscountPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	currAmcAmt:MonetaryAmount = undefined;
	/**
	 * proposed discount percentage
	 * Required: false
	 * Array: false
	 */
	proposedDiscountPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposedAmcAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Geo Group AMC and Tariff data sourced from Cost Model and BI
 */
export class GeoGroupAmcTariff {
	/**
	 * Averaged Accessorial amount of the shipments within the group from BI
	 * Required: false
	 * Array: false
	 */
	accessorialTariffAmt:MonetaryAmount = undefined;
	/**
	 * Averaged Corporate linehaul charge amount of the shipments within the group from BI
	 * Required: false
	 * Array: false
	 */
	corpLnhaulChrg:MonetaryAmount = undefined;
	/**
	 * Averaged freight revenue of shipments within the group  from BI
	 * Required: false
	 * Array: false
	 */
	freightRevenueAmt:MonetaryAmount = undefined;
	/**
	 * Averaged Fuel Surcharge amount of the shipments within the group from BI
	 * Required: false
	 * Array: false
	 */
	fscRevenueAmt:MonetaryAmount = undefined;
	/**
	 * Average Off-bill incentive amount of the shipments within the group from BI
	 * Required: false
	 * Array: false
	 */
	obiTariffAmt:MonetaryAmount = undefined;
	/**
	 * Averaged total miles of the shipments within the group  from BI
	 * Required: false
	 * Array: false
	 */
	shipmentDistance:Distance = undefined;
	/**
	 * Number of shipments not rated with Absolute Minimum Charge within the group  from BI
	 * Required: false
	 * Array: false
	 */
	shipmentTariffCount:number = undefined;
	/**
	 * Averaged tariff amount of the shipments within the group  from BI
	 * Required: false
	 * Array: false
	 */
	shipmentTariffAmt:MonetaryAmount = undefined;
	/**
	 * Total weight of the shipment unit of measurement
	 * Required: false
	 * Array: false
	 */
	shipmentVolume:Volume = undefined;
	/**
	 * Total weight of the shipment
	 * Required: false
	 * Array: false
	 */
	shipmentWeight:Weight = undefined;
	/**
	 * Average total cost amount of the shipments within the group from BI
	 * Required: false
	 * Array: false
	 */
	totalTariffCostAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Geo Group BI AMC information
 */
export class GeoGroupBiAmc {
	/**
	 * unique id of Geo Group BI AMC record
	 * Required: false
	 * Array: false
	 */
	geoGroupBiAmcId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	biAmcDetails:GeoGroupAmcTariff = undefined;
}
/**
 * Geo Group Historical shipments information
 */
export class GeoGroupBiTariff {
	/**
	 * unique id of Geo Group Bi Tariff record
	 * Required: false
	 * Array: false
	 */
	geoGroupBiTariffId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	biTariffDetails:GeoGroupAmcTariff = undefined;
}
/**
 * Geo Group Cost Model rated AMC information
 */
export class GeoGroupCmAmc {
	/**
	 * unique id of Geo Group Cost Model rated AMC record
	 * Required: false
	 * Array: false
	 */
	geoGroupCmAmcId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cmAmcDetails:GeoGroupAmcTariff = undefined;
}
/**
 * Geo Group Cost Model Tariff information
 */
export class GeoGroupCmTariff {
	/**
	 * unique id of Geo Group CM Tariff record
	 * Required: false
	 * Array: false
	 */
	geoGroupCmTariffId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cmTariffDetails:GeoGroupAmcTariff = undefined;
}
export class GeoGroupDiscountAmc {
	/**
	 * 3 char ISO currency Code
	 * Required: false
	 * Array: false
	 */
	amcCurrencyCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	averageCurrentAmcAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	averageCurrentDiscountPct:number = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	geoGroupDiscountAmcBase:GeoGroupDiscountAmcBase = undefined;
	/**
	 * unique identifier of Geo Group Discount AMC
	 * Required: false
	 * Array: false
	 */
	geoGroupDiscAmcId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
export class GeoGroupDiscountAmcBase {
	/**
	 * Indicator to show whether the AMC was increased or decreased
	 * Required: false
	 * Array: false
	 */
	amcIncrDecrInd:string = undefined;
	/**
	 * Percent of discount increase
	 * Required: false
	 * Array: false
	 */
	discountIncrPct:number = undefined;
	/**
	 * New Absolute Minimum Charge amount of shipments within the group on pricing analysis
	 * Required: false
	 * Array: false
	 */
	newAmcAmt:MonetaryAmount = undefined;
	/**
	 * Proposed Discount Amt
	 * Required: false
	 * Array: false
	 */
	newDiscountAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	newDiscountPct:number = undefined;
	/**
	 * New Net revenue amount of shipments rated with AMC within the group based on pricing analysis
	 * Required: false
	 * Array: false
	 */
	newNetRevenueAmcAmt:MonetaryAmount = undefined;
	/**
	 * New Net revenue amount of shipments within the group based on pricing analysis
	 * Required: false
	 * Array: false
	 */
	newNetRevenueAmt:MonetaryAmount = undefined;
	/**
	 * New Net revenue amount of shipments not rated with AMC within the group based on pricing analysis
	 * Required: false
	 * Array: false
	 */
	newNetRevenueTariffAmt:MonetaryAmount = undefined;
	/**
	 * Indicator to show whether the Tariff was increased or decreased
	 * Required: false
	 * Array: false
	 */
	tariffIncrDecrInd:string = undefined;
}
/**
 * Geo Group FFO information sourced from BI and Cost Model
 */
export class GeoGroupFFO {
	/**
	 * Required: false
	 * Array: false
	 */
	biFfoPupCapacityPerDay:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	biFfoShipmentPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	biFfoShipmentCount:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cmFfoPupCapacityPerDay:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cmFfoShipmentPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cmFfoShipmentCount:number = undefined;
}
/**
 * Geo Group Lane Score Data
 */
export class GeoGroupLaneScore {
	/**
	 * Required: false
	 * Array: false
	 */
	cdiPctBetterLocal:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cdiPctBetterNational:number = undefined;
	/**
	 * Overall lane score National value
	 * Required: false
	 * Array: false
	 */
	currOverallLaneScoreNat:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	hssTotalShipmentVolPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	macroScorePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	orPctBetter:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	orPctBetterLocal:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	orPctBetterNational:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	orPctScore:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prpsdCdiPctBetterNational:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prpsdCdiPctBetterLocal:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prpsdOrScorePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prpsdOverallLaneScorePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalShipmentPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	hssLaneScorePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prpsdOrPctBetterNational:number = undefined;
}
/**
 * Information about Geo Pricing group and their included and excluded members
 */
export class GeoPricingGroup {
	/**
	 * unique db identifier of Geo Pricing Group record
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupId:string = undefined;
	/**
	 * name of the geo pricing group
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupName:string = undefined;
	/**
	 * customer account MAD Code
	 * Required: false
	 * Array: false
	 */
	acctMadCd:string = undefined;
	/**
	 * customer account instance id
	 * Required: false
	 * Array: false
	 */
	acctInstId:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	groupMembers:GeoPricingGroupMember[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	exclGroupMembers:GeoPricingGroupMember[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Information about predefined global group lane or a custom customer-specific group lane when user creates a new group lane using the orig/dest group 
 */
export class GeoPricingGroupLane {
	/**
	 * unique identifier of the Geo Pricing Group Lane record
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneId:string = undefined;
	/**
	 * Name of the Geo Pricing Group Lane
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneName:string = undefined;
	/**
	 * Notes captured when creating the lane
	 * Required: false
	 * Array: false
	 */
	note:string = undefined;
	/**
	 * Customer Account Instance Id that this Geo Pricing Group Lane record is applicable for.
	 * Required: false
	 * Array: false
	 */
	acctInstId:string = undefined;
	/**
	 * Customer Account MAD Code that this Geo Pricing Group Lane record is applicable for.
	 * Required: false
	 * Array: false
	 */
	acctMadCd:string = undefined;
	/**
	 * Date when the the Geo Pricing Group Lane record is effective
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * Date when the the Geo Pricing Group Lane record expires
	 * Required: false
	 * Array: false
	 */
	expiryDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	laneMember:GeoPricingGroupLaneMember = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Information about the member groups within predefined lane
 */
export class GeoPricingGroupLaneMember {
	/**
	 * unique identifier of the geo pricing group lane member
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneMbrId:string = undefined;
	/**
	 * identifier of the parent Geo Pricing Lane record
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneId:string = undefined;
	/**
	 * identifier of the Geo Pricing Group record used as the Origin
	 * Required: false
	 * Array: false
	 */
	origGeoPricingGroupId:string = undefined;
	/**
	 * Name of the Geo Pricing Group record used as the Origin
	 * Required: false
	 * Array: false
	 */
	origGeoPricingGroupName:string = undefined;
	/**
	 * identifier of the Geo Pricing Group record used as the destination
	 * Required: false
	 * Array: false
	 */
	destGeoPricingGroupId:string = undefined;
	/**
	 * Name of the Geo Pricing Group record used as the destination
	 * Required: false
	 * Array: false
	 */
	destGeoPricingGroupName:string = undefined;
}
/**
 * Information about the members of the Geo Pricing Group
 */
export class GeoPricingGroupMember {
	/**
	 * unique db identifier of Geo Pricing Group record
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupId:string = undefined;
	/**
	 * unique db identifier of the Geo Pricing Group Member
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupMemberId:string = undefined;
	/**
	 * unique db identifier of the excluded geo pricing group member
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupExclMbrId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	groupTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	locationCd:string = undefined;
	/**
	 * lower range of the postal code
	 * Required: false
	 * Array: false
	 */
	lowZipCd:string = undefined;
	/**
	 * higher range of the postal code
	 * Required: false
	 * Array: false
	 */
	highZipCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	stateCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	countryCd:string = undefined;
}
/**
 * Information about the AMCs for a given Geo Pricing Lane
 */
export class GeoPricingLaneAmc {
	/**
	 * unique db id of geo pricing lane amc record
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneAmcId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	amcInfo:PricingAmc = undefined;
}
export class InputHierarchyFile {
	/**
	 * Required: false
	 * Array: false
	 */
	class:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	custRevenue:string = undefined;
	/**
	 * text field
	 * Required: false
	 * Array: false
	 */
	density:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destGeoType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origGeoType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * text field
	 * Required: false
	 * Array: false
	 */
	weight:string = undefined;
}
/**
 * Lading Unit characteristics
 */
export class LadingUnitBasedPricing {
	/**
	 * An Id that identifies an instance in the  LadingUnitType array. That type will have the characteristics of a particular lading unit.
	 * Required: false
	 * Array: false
	 */
	pricingLadingUnitId:string = undefined;
	/**
	 * What the begin and end range is based on for this pricing - Pallets, Volume, Density, Weight, Piece Count.
	 * Required: false
	 * Array: false
	 */
	rangeTypeCode:string = undefined;
	/**
	 * Unit of measure for the range type. Unit of Measure for the Range Type
	 * if Range type Code = Weight then UOM code = LB or KG
	 * Required: false
	 * Array: false
	 */
	rangeUom:string = undefined;
	/**
	 * If range type is Pallet then this is the Minimum Pallet count value
	 * 
	 * If range type is Weight then this is the Minimum Weight value
	 * 
	 * If range type is Piece Count then this is the Minimum Piece Count value
	 * Required: false
	 * Array: false
	 */
	beginRangeValue:number = undefined;
	/**
	 * If range type is Pallet then this is the Maximum Pallet count value
	 * 
	 * If range type is Weight then this is the Maximum Weight value
	 * 
	 * If range type is Piece Count then this is the Maximum Piece Count value
	 * Required: false
	 * Array: false
	 */
	endRangeValue:number = undefined;
	/**
	 * Minimum charge amount that is applicable for this tier
	 * Required: false
	 * Array: false
	 */
	minChargeAmt:MonetaryAmount = undefined;
	/**
	 * 1. Rate Per Unit
	 * 2. Lumpsum rate for all units
	 * 3. Base Rate
	 * Required: false
	 * Array: false
	 */
	rateTypeCd:string = undefined;
	/**
	 * The rate per unit
	 * Required: false
	 * Array: false
	 */
	ratePerUnitAmt:MonetaryAmount = undefined;
	/**
	 * This will have a value when  rateTypeCd is BaseRate
	 * Required: false
	 * Array: false
	 */
	baseRateAmt:MonetaryAmount = undefined;
	/**
	 * Applies when the rateTypeCd is BaseRate ??? need a better definition
	 * Required: false
	 * Array: false
	 */
	baseRateUnitCount:number = undefined;
	/**
	 * Indicates how the FSC charge should be priced
	 * Required: false
	 * Array: false
	 */
	fscRateTypeCd:string = undefined;
}
/**
 * Defined by an origin area to a destination area. 
 */
export class Lane {
	/**
	 * unique id of the excluded lane record
	 * Required: false
	 * Array: false
	 */
	geoLaneExclId:string = undefined;
	/**
	 * A descriptive designation for the scope of the origin area. Could be a zip, state, country or some group of zips, etc.
	 * Required: false
	 * Array: false
	 */
	origAreaTypeCd:string = undefined;
	/**
	 * A descriptive name of the area if not a single zip, state or country
	 * Required: false
	 * Array: false
	 */
	origArea:string = undefined;
	/**
	 * State code for where the customers shipments originate.
	 * Required: false
	 * Array: false
	 */
	origStateCd:string = undefined;
	/**
	 * Postal code for where the customers shipments originate. This field may accommodate zip3, zip5 or zip6 for Canada
	 * Required: false
	 * Array: false
	 */
	origPostalCd:string = undefined;
	/**
	 * minimum in a range of postal codes
	 * Required: false
	 * Array: false
	 */
	origLowPostalCd:string = undefined;
	/**
	 * maximum in a range of postal codes
	 * Required: false
	 * Array: false
	 */
	origHighPostalCd:string = undefined;
	/**
	 * Counry code for where the customers shipments originate.
	 * Required: false
	 * Array: false
	 */
	origCountryCd:string = undefined;
	/**
	 * A descriptive designation for the scope of the destination area. Could be a zip, state, country or some group of zips, etc.
	 * Required: false
	 * Array: false
	 */
	destAreaTypeCd:string = undefined;
	/**
	 * A descriptive name of the area if not a single zip, state or country
	 * Required: false
	 * Array: false
	 */
	destArea:string = undefined;
	/**
	 * State code for where the customers shipments are destined.
	 * Required: false
	 * Array: false
	 */
	destStateCd:string = undefined;
	/**
	 * This field may accommodate zip3, zip5 or zip6 for Canada shipments for the destination of shipments.
	 * Required: false
	 * Array: false
	 */
	destPostalCd:string = undefined;
	/**
	 * minimum in a range of postal codes
	 * Required: false
	 * Array: false
	 */
	destLowPostalCd:string = undefined;
	/**
	 * maximum in a range of postal codes
	 * Required: false
	 * Array: false
	 */
	destHighPostalCd:string = undefined;
	/**
	 * Country code for where the customers shipments are destined.
	 * Required: false
	 * Array: false
	 */
	destCountryCd:string = undefined;
	/**
	 * unique identifier of the Geo Prigin Group
	 * Required: false
	 * Array: false
	 */
	origGroupId:string = undefined;
	/**
	 * Pricing Group Name used as origin point
	 * Required: false
	 * Array: false
	 */
	origGroupName:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	origGroupMembers:GeoPricingGroupMember[] = undefined;
	/**
	 * unique identifier of the Geo Prigin Group
	 * Required: false
	 * Array: false
	 */
	destGroupId:string = undefined;
	/**
	 * Pricing Group Name used as destination point
	 * Required: false
	 * Array: false
	 */
	destGroupName:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	destGroupMembers:GeoPricingGroupMember[] = undefined;
}
/**
 * Pricing Agreement expectations are stored in this object. Initial expectation for the specific version is created when the corresponding pricing request's rulesets are approved. User has option to modify the expectations during Monitor and adjust process. On each modification new record will be created and old expectations will be expired and the history is maintained in this object itself.
 * 
 * 
 * As an Account Executive / Pricing Analyst, I want to be able to view expectations for the entire agreement (at the suffix level) so that when I monitor the agreement, I will be using complete expectations information for the agreement.
 * As a Sales / Pricing Manager, I want to be able to modify the Projected OR and Projected Revenue for the Pricing Agreement so that I can adjust expectations that were originally defined incorrectly.
 * Expectation records are read-only and visible to all Pricing and all Sales profiles.
 * 
 * 
 * 
 * 
 * 
 */
export class PaePricingAgreementExpectation {
	/**
	 * Identifier of the Customer with whom the agreement is being or was negotiated. This will uniquely identify the customer in the CIS component.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustInstId:string = undefined;
	/**
	 * A pricing agreement is considered to be a contract with a customer that may be renegotiated, changed, and respecified over time. Whenever the contract is renegotiated the Version Number is incremented and the effective date is changed to reflect the current negotiations.
	 * Required: false
	 * Array: false
	 */
	pricingAgrmtCustVer:number = undefined;
	/**
	 * If a customer has more than one pricing agreement at a time, each agreement is assigned a numeric suffix. Usually there is one suffix per customer but some have more. Each customer-suffix combination has its own set of version numbers starting with 1.
	 * Required: false
	 * Array: false
	 */
	pricingAgrmtSeqNbr:number = undefined;
	/**
	 * Expectation details are valid up to this date. Once the details are modified it will be effective from new date.
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * Expectation details are valid up to this date. Once the details are modified it will be effective from new date. Expiration dates for an expectation: a pricing agreement expiration record should have the same expiration date as the Pricing Agreement.
	 * Required: false
	 * Array: false
	 */
	expiryDate:Date = undefined;
	/**
	 * An unique number generated by the source system used to identify a RFP.
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * This is the unique database key of the agreement.
	 * Required: false
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * Revenue prior to the concluded renegotiation.  It will be frozen when the Pricing Analyst begins analysis.
	 * 
	 * It is a snapshot of last 6 months Revenue average before 'Analysis Start Date' the inital date is the anlysis start date. It is referred as Monthly Revenue At Start of Analysis in SFDC.
	 * 
	 * This is copied from SFDC Pricing Request when the expectation is initially created. It is not modified as part of the Expectation, it is read-only.
	 * Required: false
	 * Array: false
	 */
	monthlyInitialRevenueAmt:MonetaryAmount = undefined;
	/**
	 * Current pricing Operating Ratio O/R (%) for this account.
	 * Required: false
	 * Array: false
	 */
	currentOr:number = undefined;
	/**
	 * It is projected target improvement percent based on current Operating ratio entered by user.
	 * Required: false
	 * Array: false
	 */
	profitImprvmntTargetPct:number = undefined;
	/**
	 * Projected O/R (%) in the Expectations section of the Pricing Agreement can only be changed by Pricing Managers/Directors.
	 * 
	 * This can be a calculated amount or manually entered
	 * Required: false
	 * Array: false
	 */
	projectedOr:number = undefined;
	/**
	 * This is the Projected Yield (with Fuel) is used in Integrated Yield Management.
	 * Required: false
	 * Array: false
	 */
	projectedYieldWithFuelAmt:number = undefined;
	/**
	 * This is the expected Percent of shipments rated with AMC based on which the pricing request was approved.
	 * Required: false
	 * Array: false
	 */
	amcShipmentRevAmt:MonetaryAmount = undefined;
	/**
	 * This is an average of the freight class code
	 * Required: false
	 * Array: false
	 */
	avgFreightClassCd:string = undefined;
	/**
	 * This is the expected Average Pickup Stop Time based on which the pricing agreement was created.
	 * Required: false
	 * Array: false
	 */
	avgPickupStopElapsedTime:number = undefined;
	/**
	 * This is the expected Average Delivery Stop Time based on which the pricing agreement was created.
	 * Required: false
	 * Array: false
	 */
	avgDeliveryStopElapsedTime:number = undefined;
	/**
	 * This is the expected average Density of freight, based on which the pricing agreement was created.
	 * Required: false
	 * Array: false
	 */
	avgDensity:number = undefined;
	/**
	 * This is the expected average weight per shipment, based on which the pricing agreement was created.
	 * Required: false
	 * Array: false
	 */
	avgShipmentWeight:Weight = undefined;
	/**
	 * This will indicate whether the pricing is lane based or not: values are Y or N.
	 * Required: false
	 * Array: false
	 */
	laneBasedPricingInd:string = undefined;
	/**
	 * User will enter the comments once the expectation data modified before new version of the pricing agreement
	 * Required: false
	 * Array: false
	 */
	commentText:string = undefined;
}
/**
 * Accessorial information being considered for this RFP
 */
export class PricingAccessorial {
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialId:string = undefined;
	/**
	 * The code representing the accessorial
	 * Required: false
	 * Array: false
	 */
	accessorialCd:string = undefined;
	/**
	 * Description of the accessorial
	 * Required: false
	 * Array: false
	 */
	accessorialDesc:string = undefined;
	/**
	 * The unit for this accessorial
	 * Required: false
	 * Array: false
	 */
	accessorialUom:string = undefined;
	/**
	 * The minimum required based on the UOM for this accessorial type
	 * Required: false
	 * Array: false
	 */
	accessorialMinUnits:number = undefined;
	/**
	 * The minimum required based on the UOM for this accessorial type
	 * Required: false
	 * Array: false
	 */
	accessorialMaxUnits:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialChgAmt:MonetaryAmount = undefined;
	/**
	 * An amount that will override the default amount for this accessorial
	 * Required: false
	 * Array: false
	 */
	accessorialOverrideChg:MonetaryAmount = undefined;
	/**
	 * This field overrides the default percentage used for calculating the accessorial service.
	 * Required: false
	 * Array: false
	 */
	accessorialOverrideChgPct:number = undefined;
	/**
	 * True if this accessorial will not be charged in spite of the circumstances normally requiring it.
	 * Required: false
	 * Array: false
	 */
	accessorialWaivedInd:boolean = undefined;
	/**
	 * The percentage of the customers total shipments that will qualify for this accessorial.
	 * Required: false
	 * Array: false
	 */
	accessorialQualifyPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
export class PricingAdjustment {
	/**
	 * Required: false
	 * Array: true
	 */
	instIds:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	adjustAmt:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	adjustPct:number = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	clusterIds:string[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	uom:string = undefined;
}
/**
 * Information about Fak adjustment percentage
 */
export class PricingAdminFakAdjustmentPct {
	/**
	 * unique identifier of Pricing Admin Fak Adjustment Pct record
	 * Required: false
	 * Array: false
	 */
	adminFakAdjustPctId:string = undefined;
	/**
	 * Fak Class
	 * Required: false
	 * Array: false
	 */
	fakClass:string = undefined;
	/**
	 * actual NMFC class
	 * Required: false
	 * Array: false
	 */
	actualClass:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fakAdjustmentPct:number = undefined;
	/**
	 * effective date of the record
	 * Required: false
	 * Array: false
	 */
	effDate:Date = undefined;
	/**
	 * expiry date of the record
	 * Required: false
	 * Array: false
	 */
	expDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Information about Fuel Surcharge Percentage
 */
export class PricingAdminFscPctLookup {
	/**
	 * Required: false
	 * Array: false
	 */
	adminFscPctLookupId:string = undefined;
	/**
	 * Beginning range of Fuel Price Per Gallon
	 * Required: false
	 * Array: false
	 */
	fuelPerGalBgnRng:MonetaryAmount = undefined;
	/**
	 * Ending range of Fuel Price Per Gallon
	 * Required: false
	 * Array: false
	 */
	fuelPerGalEndRng:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	effDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	expDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Information about the Pricing Analysts and their Managers
 */
export class PricingAdminPersonnel {
	/**
	 * unique identifier of the Pricing Admin Personnel record
	 * Required: false
	 * Array: false
	 */
	adminPersonnelId:string = undefined;
	/**
	 * Alternate Employee Id of the Employee in HR system
	 * Required: false
	 * Array: false
	 */
	alternateEmployeeId:string = undefined;
	/**
	 * Alternate Employee Id of the Manager in HR system
	 * Required: false
	 * Array: false
	 */
	alternateManagerEmployeeId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * employee district code as defined in the HR system
	 * Required: false
	 * Array: false
	 */
	districtCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	emailAddr:string = undefined;
	/**
	 * Employee full name as defined in the HR system
	 * Required: false
	 * Array: false
	 */
	employeeFullName:string = undefined;
	/**
	 * Employee id as defined in the HR system
	 * Required: false
	 * Array: false
	 */
	employeeId:string = undefined;
	/**
	 * Employee Role as defined in the HR system
	 * Required: false
	 * Array: false
	 */
	employeeRoleType:string = undefined;
	/**
	 * Employee Title as defined in the HR system
	 * Required: false
	 * Array: false
	 */
	employeeTitle:string = undefined;
	/**
	 * Indicates user has admin access. If the value is ï¿½Yï¿½ the user have access to  update any screen in the system
	 * Required: false
	 * Array: false
	 */
	isAdminUserInd:string = undefined;
	/**
	 * Employee Id of the Manager in the HR System
	 * Required: false
	 * Array: false
	 */
	managerEmployeeId:string = undefined;
	/**
	 * Full Name of the Manager in the HR System
	 * Required: false
	 * Array: false
	 */
	managerFullName:string = undefined;
	/**
	 * employee region code as defined in the HR system
	 * Required: false
	 * Array: false
	 */
	regionCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	sicCode:string = undefined;
	/**
	 * Indicates if the employee is still active
	 * Required: false
	 * Array: false
	 */
	activeInd:boolean = undefined;
	/**
	 * Employee termination date
	 * Required: false
	 * Array: false
	 */
	terminationDate:Date = undefined;
	/**
	 * Additional Roles performed by this employee (like Pricing analyst does Auditor or Ruleset Builder)
	 * Required: false
	 * Array: false
	 */
	additionalRoleType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	reportsToEmployeeId:string = undefined;
	/**
	 * Netwirk userr Id
	 * Required: false
	 * Array: false
	 */
	ntUserId:string = undefined;
}
/**
 * Information about Proposed Increase Level
 */
export class PricingAdminProposedIncrLevel {
	/**
	 * Required: false
	 * Array: false
	 */
	adminProposedIncrLevelId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prpsIncreaseLevelPctMin:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prpsIncreaseLevelPctMax:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	result:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	effDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	expDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Information about Proposed Strategy
 */
export class PricingAdminProposedStrategy {
	/**
	 * Required: false
	 * Array: false
	 */
	adminProposedStrategyPctId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposedStrategyPctMin:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposedStrategyPctMax:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	result:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	effDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	expDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
export class PricingAgreement {
	/**
	 * Values :
	 * 1 - LTL Pricing
	 * 2 - Spot Quote
	 * 3 - Pallet Pricing
	 * Required: false
	 * Array: false
	 */
	paTypeCd:string = undefined;
	/**
	 * This is the unique database key of the agreement.
	 * Required: false
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * unique id of Sales Rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Type of Sales Rfp associated with the Pricing agreement
	 * Required: false
	 * Array: false
	 */
	salesRfpTypeCd:string = undefined;
	/**
	 * Request date of Sales Rfp associated wih the agreement
	 * Required: false
	 * Array: false
	 */
	salesRfpRequstDate:Date = undefined;
	/**
	 * The version of the  pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementCustVersion:string = undefined;
	/**
	 * The sequence number within the version of pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementSeqNbr:string = undefined;
	/**
	 * The instance ID of the customer that negotiated this pricing agreement
	 * Required: false
	 * Array: false
	 */
	leadAcctId:string = undefined;
	/**
	 * This is the primary location used for the agreement
	 * Required: false
	 * Array: false
	 */
	primaryAcctId:string = undefined;
	/**
	 * Desc: This identifies the type of contract (this pricing agreement) for the customer.
	 * 
	 * Valid Values:
	 * T - Third Party Logistics (3PL) contract
	 * N - National contract
	 * L - Local contract
	 * O - Other contract
	 * Required: false
	 * Array: false
	 */
	paAcctTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementTypeCd:string = undefined;
	/**
	 * Date the Pricing Agreement was effective
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * Date the pricing agreement will expire or expired
	 * Required: false
	 * Array: false
	 */
	expiryDate:Date = undefined;
	/**
	 * Identifies if the current pricing agreement has OBI Program
	 * Required: false
	 * Array: false
	 */
	obiInd:boolean = undefined;
	/**
	 * Format type of Pricing agreement - Internal or External
	 * Required: false
	 * Array: false
	 */
	formatType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	signatureReqdInd:boolean = undefined;
	/**
	 * Renegotiation date of Pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementRenegDate:Date = undefined;
	/**
	 * unique identifier of the Pricing Agreement from the source system
	 * Required: false
	 * Array: false
	 */
	sourceAgrmtId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	thirdPartyLogisticsTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	activeIndicator:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingDcmntLeadPaId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAgreementEventType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAudit:PricingAgreementAuditHdr = undefined;
	/**
	 * Agreement Account primary location owner
	 * Required: false
	 * Array: false
	 */
	agreementAccountOwner:EmployeeId = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	strategyType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	liabilityDetails:PricingAgreementDocLiabHdr = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	acctDetails:AcctId = undefined;
}
/**
 * List of Customer Accounts associated with a Pricing Agreement
 */
export class PricingAgreementAcct {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAgreement:PricingAgreement = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	acctInfo:AcctId[] = undefined;
}
/**
 * Details about the Pricing Agreement Audit
 */
export class PricingAgreementAudit {
	/**
	 * Audit Header details
	 * Required: false
	 * Array: false
	 */
	agreementAuditHdr:PricingAgreementAuditHdr = undefined;
	/**
	 * Sales Rfp details associated with the Pricing Audit
	 * Required: false
	 * Array: false
	 */
	salesRfpHdr:SalesRfpHdr = undefined;
	/**
	 * Basic details about the customer account
	 * Required: false
	 * Array: false
	 */
	accountDetail:AcctId = undefined;
	/**
	 * Pricing Agreement details
	 * Required: false
	 * Array: false
	 */
	agreementDetail:PricingAgreement = undefined;
	/**
	 * Pricing Proposal Header Details
	 * Required: false
	 * Array: false
	 */
	proposalHdr:PricingProposalHdr = undefined;
}
/**
 * Information about the Pricing Agreement Audit
 */
export class PricingAgreementAuditHdr {
	/**
	 * Unique identifier of the Pricing Agreement Audit Header record
	 * Required: false
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
	/**
	 * Unique identifier of the parent Pricing Agreement record to which this audit record belongs
	 * Required: false
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * Employee Id of the Ruleset builder
	 * Required: false
	 * Array: false
	 */
	rulesetBuilderEmpId:EmployeeId = undefined;
	/**
	 * Current status of the Pricing Agreement Audit record
	 * Required: false
	 * Array: false
	 */
	auditStatus:string = undefined;
	/**
	 * Date when the Pricing Agreement Audit was completed
	 * Required: false
	 * Array: false
	 */
	completeDate:Date = undefined;
	/**
	 * Due date for the Current Audit Status to be completed
	 * Required: false
	 * Array: false
	 */
	statusDueDate:Date = undefined;
	/**
	 * Employee Id of the Auditor
	 * Required: false
	 * Array: false
	 */
	auditorEmpId:EmployeeId = undefined;
	/**
	 * Date when the Pricing Agreement was tagged for Audit
	 * Required: false
	 * Array: false
	 */
	tagDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	maxRevenueAmt:MonetaryAmount = undefined;
	/**
	 * Employee Id of the Pricing Analyst responsible for this Pricing Agreement
	 * Required: false
	 * Array: false
	 */
	pricingAnalystEmpId:EmployeeId = undefined;
	/**
	 * Describes the type of audit work
	 * Required: false
	 * Array: false
	 */
	auditWorkTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	comments:PricingAgreementAuditComment[] = undefined;
	/**
	 * Header Event Log
	 * Required: false
	 * Array: true
	 */
	headerEventLog:PricingAgreementAuditEventLog[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	issues:PricingAgreementAuditIssue[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Date when the Pricing Agreement Audit was started
	 * Required: false
	 * Array: false
	 */
	startDate:Date = undefined;
	/**
	 * Priority of the Audit
	 * Required: false
	 * Array: false
	 */
	priority:string = undefined;
	/**
	 * Number of dayes overdue or  outstanding
	 * Required: false
	 * Array: false
	 */
	dueDateText:string = undefined;
	/**
	 * number of document error
	 * Required: false
	 * Array: false
	 */
	documentErrorCount:number = undefined;
	/**
	 * number of ruleset error
	 * Required: false
	 * Array: false
	 */
	rulesetErrorCount:number = undefined;
	/**
	 * number of miscellaneous error
	 * Required: false
	 * Array: false
	 */
	miscErrorCount:number = undefined;
	/**
	 * Ruleset open issue count
	 * Required: false
	 * Array: false
	 */
	rulesetOpenCount:number = undefined;
	/**
	 * Update pf audit header record allowed or not
	 * Required: false
	 * Array: false
	 */
	statusAllowed:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	nextRevisionCount:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnalystMgrId:EmployeeId = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditorMgrId:EmployeeId = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rulesetBuilderMgrId:EmployeeId = undefined;
}
/**
 * Information about the supporting documents for Pricing Agreement Audit
 */
export class PricingAgreementAuditDoc {
	/**
	 * Unique identifier of the Agreement Audit Document record
	 * Required: false
	 * Array: false
	 */
	agreementAuditDocId:string = undefined;
	/**
	 * Unique identifier of the Agreement Audit Comment
	 * Required: false
	 * Array: false
	 */
	agreementAuditCommentId:string = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Audit Header record
	 * Required: false
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
	/**
	 * Type of Document
	 * Required: false
	 * Array: false
	 */
	docTypeCd:string = undefined;
	/**
	 * URL for the document to fetch from DMS system
	 * Required: false
	 * Array: false
	 */
	docUrl:string = undefined;
	/**
	 * Document file name in the DMS system
	 * Required: false
	 * Array: false
	 */
	docFile:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Comments related to Pricing Agreement Audit
 */
export class PricingAgreementAuditComment {
	/**
	 * Unique identifier of the Agreement Audit Comment
	 * Required: false
	 * Array: false
	 */
	agreementAuditCommentId:string = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Audit Header record
	 * Required: false
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
	/**
	 * Unique identifier of the Agreement Audit Issue record
	 * Required: false
	 * Array: false
	 */
	agreementAuditIssueId:string = undefined;
	/**
	 * Source for the Audit Comments - Analyst, Auditor, Internal, Manager, Ruleset Builder etc.
	 * Required: false
	 * Array: false
	 */
	commentSource:string = undefined;
	/**
	 * Actual comment text
	 * Required: false
	 * Array: false
	 */
	commentText:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Document Type Code
	 * Required: false
	 * Array: false
	 */
	docTypeCd:string = undefined;
	/**
	 * Document Url
	 * Required: false
	 * Array: false
	 */
	docUrl:string = undefined;
	/**
	 * Document File
	 * Required: false
	 * Array: false
	 */
	docFile:string = undefined;
	/**
	 * Agreement Audit Event Log Id
	 * Required: false
	 * Array: false
	 */
	agreementAuditEventLogId:string = undefined;
	/**
	 * Name of the employee who created the record
	 * Required: false
	 * Array: false
	 */
	createdBy:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	createdTimestamp:Date = undefined;
}
/**
 * Information about milestone events for Audit
 */
export class PricingAgreementAuditEventLog {
	/**
	 * Unique identifier of the Pricing Agreement Audit Event Log record
	 * Required: false
	 * Array: false
	 */
	agreementAuditEventLogId:string = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Audit Header record
	 * Required: false
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
	/**
	 * Unique identifier of the Agreement Audit Issue record
	 * Required: false
	 * Array: false
	 */
	agreementAuditIssueId:string = undefined;
	/**
	 * Type of event. Eg. HdrStatus or IssueStatus
	 * Required: false
	 * Array: false
	 */
	eventTypeCd:string = undefined;
	/**
	 * Sub Type Code for the event
	 * Required: false
	 * Array: false
	 */
	eventSubTypeCd:string = undefined;
	/**
	 * Value for the eventTypeCd
	 * Required: false
	 * Array: false
	 */
	eventStatus:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventTranCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventData:string = undefined;
	/**
	 * The date and time when the event occurred
	 * Required: false
	 * Array: false
	 */
	eventOccurredTimestamp:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	statusReasonCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventCreatedBy:string = undefined;
	/**
	 * Due date for the event status
	 * Required: false
	 * Array: false
	 */
	eventDueDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Comment  associated to the issue audit log
	 * Required: false
	 * Array: true
	 */
	logComment:PricingAgreementAuditComment[] = undefined;
	/**
	 * previous event status value
	 * Required: false
	 * Array: false
	 */
	prevEventStatus:string = undefined;
}
/**
 * Information about Issues related to Pricing Agreement Audit
 */
export class PricingAgreementAuditIssue {
	/**
	 * Unique identifier of the Agreement Audit Issue record
	 * Required: false
	 * Array: false
	 */
	agreementAuditIssueId:string = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Audit Header record
	 * Required: false
	 * Array: false
	 */
	agreementAuditHdrId:string = undefined;
	/**
	 * Date when the Audit Issue was reported
	 * Required: false
	 * Array: false
	 */
	startDate:Date = undefined;
	/**
	 * Identifies the type of Audit Issue
	 * Required: false
	 * Array: false
	 */
	issueType:string = undefined;
	/**
	 * Date when the Audit Issue was closed
	 * Required: false
	 * Array: false
	 */
	closeDate:Date = undefined;
	/**
	 * Brief description of the issue
	 * Required: false
	 * Array: false
	 */
	description:string = undefined;
	/**
	 * Identifies the category to which the Issue type belongs
	 * Required: false
	 * Array: false
	 */
	issueCategory:string = undefined;
	/**
	 * Ruleset number for which the Audit issue was recorded
	 * Required: false
	 * Array: false
	 */
	rulesetNumber:string = undefined;
	/**
	 * Ruleset type for which the Audit issue was recorded
	 * Required: false
	 * Array: false
	 */
	rulesetType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInto:AuditInfo = undefined;
	/**
	 * Audit Issue Event Log
	 * Required: false
	 * Array: true
	 */
	issueEventLog:PricingAgreementAuditEventLog[] = undefined;
	/**
	 * Audit Issue Detail
	 * Required: false
	 * Array: false
	 */
	issueDetail:string = undefined;
	/**
	 * Audit issue Commemts
	 * Required: false
	 * Array: true
	 */
	issueComments:PricingAgreementAuditComment[] = undefined;
	/**
	 * These are the allowed issue status fo r the current status
	 * Required: false
	 * Array: false
	 */
	allowedIssueStatus:string = undefined;
	/**
	 * Audit Issue current Status
	 * Required: false
	 * Array: false
	 */
	issueStatus:string = undefined;
	/**
	 * Status of audit header allowed
	 * Required: false
	 * Array: false
	 */
	statusAllowed:string = undefined;
}
/**
 * Basic Information about a Pricing Agreement
 */
export class PricingAgreementBasic {
	/**
	 * This is the unique database key of the agreement.
	 * Required: false
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * The version of the  pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementCustVersion:string = undefined;
	/**
	 * The sequence number within the version of pricing agreement
	 * Required: false
	 * Array: false
	 */
	agreementSeqNbr:string = undefined;
	/**
	 * The instance ID of the customer that negotiated this pricing agreement
	 * Required: false
	 * Array: false
	 */
	leadAcctId:string = undefined;
	/**
	 * Date the Pricing Agreement was effective
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * Date the pricing agreement will expire or expired
	 * Required: false
	 * Array: false
	 */
	expiryDate:Date = undefined;
	/**
	 * Renegotiation date of Pricing agreement
	 * Required: false
	 * Array: false
	 */
	renegotiationDate:Date = undefined;
}
/**
 * List of Pricing Agreements associated with a Customer Account
 */
export class PricingAgreementByAcct {
	/**
	 * Required: false
	 * Array: false
	 */
	acctInstId:object = undefined;
	/**
	 * List of Pricing Agreements by input Account Instance Id
	 * Required: false
	 * Array: true
	 */
	acctPricingAgreement:PricingAgreement[] = undefined;
}
export class PricingAgreementDocHdr {
	/**
	 * Required: false
	 * Array: false
	 */
	acctInstId:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	acctMadCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	acctName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	activatedByEmpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	activationDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementCustVersion:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementDocHdrId:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementDocLiabHdr:PricingAgreementDocLiabHdr = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	agreementDocType:PricingAgreementDocType[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	agreementDocInd:PricingAgreementDocInd[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementSeqNbr:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	approvalStep:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	companySignedBy:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	companySignedDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	companySignedTitle:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact1City:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact1Country:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact1Name:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact1PostalCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact1State:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact1Street:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact1Title:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact2City:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact2Country:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact2Name:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact2PostalCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact2State:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact2Street:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contact2Title:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contractDuration:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	currentExecutiveReviewer:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	description:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	dueDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	expiryDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fak:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	legalReviewerEmpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	liabilityLimits:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ltlDamageLiability:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ndaTermEndDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	nonStandardTerms:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	outstandingDays:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	paymentTerm:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	potentialMonthlyRevenueAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	previousStatus:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAgreementEffDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAgreementExpDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAgreementNumber:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnalystEmpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	promisedMonthlyRevenueAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	renegotiationDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	requestDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	requesterEmpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	signatoryEmpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	status:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	statusCategory:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	subCategory:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	terminationDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	terminationNoticeDays:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	terminationReason:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	terminationRequesterId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	terminationRequestStatus:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalAgreementValue:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	type:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	whoSignsFirst:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	pricingAgreement:PricingAgreement = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	buttons:string[] = undefined;
}
export class PricingAgreementDocInd {
	/**
	 * Required: false
	 * Array: false
	 */
	agreementDocIndId:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	indicatorCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
export class PricingAgreementDocType {
	/**
	 * Required: false
	 * Array: false
	 */
	agreementDocTypeId:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	docTypeCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	docFile:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	docUrl:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	docTags:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Information about the Customer expectation in Pricing Agreement
 */
export class PricingAgreementExpectation {
	/**
	 * if (Monthly_Revenue_At_Start_of_Analysis > 0, Projected_Operating_Ratio - Current_Operating_Ratio ,0)
	 * Required: false
	 * Array: false
	 */
	orChangePct:number = undefined;
	/**
	 * unique identifier of the Pricing Agreement Expectation
	 * Required: false
	 * Array: false
	 */
	agreementExpectationId:string = undefined;
	/**
	 * This is the expected Percent of shipments rated with AMC based on which the pricing request was approved.
	 * Required: false
	 * Array: false
	 */
	amcShipmentRevPct:number = undefined;
	/**
	 * Audit Information
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * This is the expected Average Delivery Stop Time based on which the pricing agreement was creaated.
	 * Required: false
	 * Array: false
	 */
	averageDeliveryStopTime:number = undefined;
	/**
	 * This is the expected average Density of freight, based on which the pricing agreement was created.
	 * Required: false
	 * Array: false
	 */
	averageDensity:number = undefined;
	/**
	 * This is the expected Average Pickup Stop Time based on which the pricing agreement was created.
	 * Required: false
	 * Array: false
	 */
	averagePickupStopTime:number = undefined;
	/**
	 * This is the expected average weight per shipment, based on which the pricing agreement was created.
	 * Required: false
	 * Array: false
	 */
	averageWeightPerShipment:Weight = undefined;
	/**
	 * User will enter the comments once the expectation data modified before new version of the pricing agreement
	 * Required: false
	 * Array: false
	 */
	comments:string = undefined;
	/**
	 * Current pricing Operating Ratio of this account.
	 * Required: false
	 * Array: false
	 */
	currentOr:number = undefined;
	/**
	 * Effective date of the pricing agreement expectation
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * Expectation details are valid up to this date. Once the details are modified it will be effective from new date.
	 * Required: false
	 * Array: false
	 */
	expiryDate:Date = undefined;
	/**
	 * This will indicate whether the pricing is lane based or not: Valid values are Y or N
	 * Required: false
	 * Array: false
	 */
	laneBasedPricingInd:string = undefined;
	/**
	 * Revenue prior to the concluded renegotiation. It will be frozen when the Pricing Analyst begins analysis. Snapshot of last 6 months Revenue average before Analysis Start Date
	 * Required: false
	 * Array: false
	 */
	monthlyInitialRevAmt:MonetaryAmount = undefined;
	/**
	 * This is the expected percent price increase from the previous agreement.
	 * Required: false
	 * Array: false
	 */
	priceChangePct:number = undefined;
	/**
	 * Profit improvement target
	 * Required: false
	 * Array: false
	 */
	profitImprvmntTargetPct:number = undefined;
	/**
	 * This represents the expected discount Impact for this customer once the new pricing is applied.
	 * Required: false
	 * Array: false
	 */
	projectedDiscountImpactPct:number = undefined;
	/**
	 * Projected_Profit - Monthly_Revenue_At_Start_of_Analysis(1- Current O/R)
	 * Required: false
	 * Array: false
	 */
	projectedMnthProfitImprvmntAmt:MonetaryAmount = undefined;
	/**
	 * Expected revenue loss as a result of the renegotiation
	 * 
	 * (1) If no change in revenue then 0,
	 * (2) A negative number if growth is expected,
	 * (3) A positive number if a reduction is expected
	 * 
	 * Mo Revenue At Start of Analysis ($) - Projected Monthly Revenue ($)
	 * Required: false
	 * Array: false
	 */
	projectedMnthRevChngAmt:MonetaryAmount = undefined;
	/**
	 * Projected Monthly Revenue($) + Mo Cost Change ($)
	 * Required: false
	 * Array: false
	 */
	projectedMonthlyCost:MonetaryAmount = undefined;
	/**
	 * Cost associated with the revenue that we expect to lose
	 * 
	 * (1) If no change in cost is ï¿½0ï¿½,
	 * (2) A negative number if growth is expected,
	 * (3) A positive number if a reduction is expected
	 * Required: false
	 * Array: false
	 */
	projectedMonthlyCostChange:MonetaryAmount = undefined;
	/**
	 * Expected Promise Amount after the offer, this will be different from the promise amount captured at the sales RFP creation.
	 * Calculated as (Mo Revenue at Start of Analysis ($) ï¿½  Mo Revenue Change ($)) * (1 + Price Change (%)) or can be manually entered.
	 * Required: false
	 * Array: false
	 */
	projectedMonthlyRevAmt:MonetaryAmount = undefined;
	/**
	 * This represents the expected Discount Impact for this customer once the new pricing is applied.
	 * Calculated as Projected Mo Cost ($)/'Projected Mo Revenue ($) or manually enter.
	 * Required: false
	 * Array: false
	 */
	projectedOr:number = undefined;
	/**
	 * Calculated as Projected Mo Revenue ($) - Projected Mo Cost ($)
	 * Required: false
	 * Array: false
	 */
	projectedProfitAmt:MonetaryAmount = undefined;
	/**
	 * Proj_Month_profit_Improvement/ Monthly_Revenue_At_Start_of_Analysis
	 * Required: false
	 * Array: false
	 */
	projectedProfitImprovementPct:number = undefined;
	/**
	 * This represents the Projected Yield (Net Fuel) and is used for integrated yield management
	 * Required: false
	 * Array: false
	 */
	projectedYieldNetFuel:number = undefined;
	/**
	 * This is the Projected Yield (with Fuel) is used in Integrated Yield Management.
	 * Required: false
	 * Array: false
	 */
	projectedYieldWithFuel:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	monthlyPromiseAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	monthlyPotentialAmt:MonetaryAmount = undefined;
}
/**
 * Pricing Agreement Monitor Region Rollup Report
 */
export class PricingAgreementMonitorRegionRollupReport {
	/**
	 * Report requester information
	 * Required: false
	 * Array: false
	 */
	requester:EmployeeId = undefined;
	/**
	 * Sum total of all the PAM record counts for all regions and all districts
	 * Required: false
	 * Array: false
	 */
	total:PricingAgreementMonitorCounts = undefined;
	/**
	 * Pam Regions
	 * Required: false
	 * Array: true
	 */
	regions:PricingAgreementMonitorRegion[] = undefined;
}
export class PricingAgreementMonitorEventLog {
	/**
	 * Required: false
	 * Array: false
	 */
	monitorEventLogId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventSubTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventStatus:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventTranCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventCreatedBy:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventData:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventOccuredTimestamp:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	statusReasonCd:string = undefined;
}
/**
 * Pricing Agreement Monitor basic information
 */
export class PricingAgreementMonitorBasic {
	/**
	 * PAM Name
	 * Required: false
	 * Array: false
	 */
	agreementMonitorName:string = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Monitor record
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorId:string = undefined;
	/**
	 * Agreement Detail
	 * Required: false
	 * Array: false
	 */
	pricingAgreement:PricingAgreementBasic = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accountDetails:PartyBasic = undefined;
}
/**
 * Pricing Agreement Monitor
 */
export class PricingAgreementMonitor {
	/**
	 * Actions taken for this Monitor record
	 * Required: false
	 * Array: true
	 */
	action:PricingMonitorAction[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	actionAllowed:string = undefined;
	/**
	 * PAM Name
	 * Required: false
	 * Array: false
	 */
	agreementMonitorName:string = undefined;
	/**
	 * Monthly Revenue Amount
	 * Required: false
	 * Array: false
	 */
	revenueAmt:MonetaryAmount = undefined;
	/**
	 * Date when the monitor record was closed
	 * Required: false
	 * Array: false
	 */
	closeDate:Date = undefined;
	/**
	 * Cost associated with the customer's shipments
	 * Required: false
	 * Array: false
	 */
	cost:MonetaryAmount = undefined;
	/**
	 * Director Of Sales Employee Id
	 * Required: false
	 * Array: false
	 */
	dosEmployee:EmployeeId = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	eventLogs:PricingMonitorEventLog[] = undefined;
	/**
	 * Off Bill Incentive Amount
	 * Required: false
	 * Array: false
	 */
	offBilIncentiveAmt:MonetaryAmount = undefined;
	/**
	 * Operating Ratio
	 * Required: false
	 * Array: false
	 */
	operatingRatio:number = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Monitor record
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorId:string = undefined;
	/**
	 * Pricing Analyst Id
	 * Required: false
	 * Array: false
	 */
	pricingAnalyst:EmployeeId = undefined;
	/**
	 * Pricing Manager Id
	 * Required: false
	 * Array: false
	 */
	pricingManager:EmployeeId = undefined;
	/**
	 * Profit Amount
	 * Required: false
	 * Array: false
	 */
	profitAmt:MonetaryAmount = undefined;
	/**
	 * Reasons why the Monitor record was created
	 * Required: false
	 * Array: true
	 */
	reason:PricingMonitorReason[] = undefined;
	/**
	 * List of SalesRfps associated with the Monitor record
	 * Required: false
	 * Array: true
	 */
	salesRfps:SalesRfpHdr[] = undefined;
	/**
	 * Sales District
	 * Required: false
	 * Array: false
	 */
	salesDistrict:string = undefined;
	/**
	 * Employee Id of the Sales person who owns this Monitor record
	 * Required: false
	 * Array: false
	 */
	salesOwnerEmployee:EmployeeId = undefined;
	/**
	 * Sales Region
	 * Required: false
	 * Array: false
	 */
	salesRegion:string = undefined;
	/**
	 * Sequence Number of the Pricing Agreement Monitor record
	 * Required: false
	 * Array: false
	 */
	sequenceNbr:number = undefined;
	/**
	 * Status
	 * Required: false
	 * Array: false
	 */
	status:string = undefined;
	/**
	 * Type of Pricing Agreement Monitor record: O/R or Rev or MON
	 * Required: false
	 * Array: false
	 */
	typeCode:string = undefined;
	/**
	 * Version Number of the Pricing Agreement Monitor
	 * Required: false
	 * Array: false
	 */
	versionNbr:string = undefined;
	/**
	 * Year and Month when the Monitor record is created
	 * Required: false
	 * Array: false
	 */
	yearMonth:Date = undefined;
	/**
	 * Agreement Detail
	 * Required: false
	 * Array: false
	 */
	pricingAgreement:PricingAgreement = undefined;
	/**
	 * Pricing Agreement Expectation
	 * Required: false
	 * Array: false
	 */
	expectation:PricingAgreementExpectation = undefined;
	/**
	 * NASS Employee information
	 * Required: false
	 * Array: false
	 */
	nassEmployee:EmployeeId = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	createDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accountDetails:AcctId = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	monitorParameter:PricingAgreementMonitorParameter = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	monitorParameterTier:PricingAgreementMonitorParameterTier = undefined;
}
/**
 * Pricing Agreement Monitor Valid Action
 */
export class PricingAgreementMonitorValidAction {
	/**
	 * Action Source: Pam, Batch, or OneCRM
	 * Required: true
	 * Array: false
	 */
	actionSource:string = undefined;
	/**
	 * Action Type: Pricing, or Sales
	 * Required: true
	 * Array: false
	 */
	actionType:string = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Monitor record
	 * Required: true
	 * Array: false
	 */
	actionName:string = undefined;
}
/**
 * Pricing Agreement Rollup Record
 */
export class PricingAgreementMonitorRollup {
	/**
	 * PAM Name
	 * Required: false
	 * Array: false
	 */
	agreementMonitorName:string = undefined;
	/**
	 * Unique identifier of the Pricing Agreement Monitor record
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorId:string = undefined;
	/**
	 * Pricing Agreement Monitor Type
	 * Required: false
	 * Array: false
	 */
	typeCode:string = undefined;
	/**
	 * Negotiating Custustomer Mad Code from Pricing Agreement related to this PAM
	 * Required: false
	 * Array: false
	 */
	negotiatingCustMadCd:string = undefined;
	/**
	 * Pricing Agreement Monitor Status Code
	 * Required: false
	 * Array: false
	 */
	statusCd:string = undefined;
	/**
	 * Pam Counts
	 * Required: false
	 * Array: false
	 */
	counts:PricingAgreementMonitorCounts = undefined;
	/**
	 * Rfp Status associated to the PAM
	 * Required: false
	 * Array: false
	 */
	rfpStatus:string = undefined;
}
export class PricingMonitorReason {
	/**
	 * unique identifier of the Agreement Monitor Reason record
	 * Required: false
	 * Array: false
	 */
	agreementMonitorReasonId:string = undefined;
	/**
	 * Why the monitor record was created for this Pricing Agreement. One of the following values:
	 * Monthly Revenue Percentage Attainment
	 * O/R - Projected vs. Actual Difference
	 * O/R Maximum
	 * Monthly Profit Loss
	 * Required: false
	 * Array: false
	 */
	createReason:string = undefined;
	/**
	 * Monitor Reason created date
	 * Required: false
	 * Array: false
	 */
	createdDate:Date = undefined;
}
export class PricingMonitorEventLog {
	/**
	 * Required: false
	 * Array: false
	 */
	monitorEventLogId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventSubTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventStatus:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventTranCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventCreatedBy:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventData:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventOccuredTimestamp:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	statusReasonCd:string = undefined;
}
export class PricingMonitorAction {
	/**
	 * unique identifier of the agreement monitor action record
	 * Required: false
	 * Array: false
	 */
	agreementMonitorActionId:string = undefined;
	/**
	 * Action taken by the Pricing Analyst as a result of this monitor record. One of the following values
	 * Seasonal Business
	 * Pricing Request - Cancel Pricing
	 * Pricing Request - Renegotiation
	 * Request Expectations Adjustment
	 * Required: false
	 * Array: false
	 */
	actionTaken:string = undefined;
	/**
	 * Type of Action
	 * Sales or Pricing or SalesForce
	 * Required: false
	 * Array: false
	 */
	actionType:string = undefined;
	/**
	 * Comments added as part of the action taken.
	 * Required: false
	 * Array: false
	 */
	commentText:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	actionSource:string = undefined;
	/**
	 * Set number for the action. This is used for grouping actions belonging to a set
	 * Required: false
	 * Array: false
	 */
	actionSetNbr:number = undefined;
	/**
	 * Date on which this action created
	 * Required: false
	 * Array: false
	 */
	createdDate:Date = undefined;
	/**
	 * User who took this action
	 * Required: false
	 * Array: false
	 */
	CreatedBy:EmployeeId = undefined;
}
export class PricingAgreementDocLiabDtl {
	/**
	 * Required: false
	 * Array: false
	 */
	agreementLiabilityDetailId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementLiabilityHdrId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	commodityClass:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	liabilityPerPound:MonetaryAmount = undefined;
}
export class PricingAgreementDocLiabHdr {
	/**
	 * Required: false
	 * Array: false
	 */
	agreementDocId:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementLiabilityHdrId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	approvalRequiredInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	approvedBy:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	caFlatLiabilityPerLb:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	caMaxLiabilityAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	claimsNotifyMaxDays:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	householdGoodsLiabilityPerLb:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	inboundCollectInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	inboundPrepaidInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	internationalInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	interstateInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	intrastateInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	liabilityComments:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	liabilityTermsCreator:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	maxLiabilityAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	outboundCollectInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	outboundPrepaidInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	specialHandlingInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	specialHandlingMaxAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	specialHandlingMinAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	stdLiabilityInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	thirdPartyInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	usFlatLiabilityPerLb:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	agreementDocLiabDtl:PricingAgreementDocLiabDtl[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposalHdrId:string = undefined;
}
/**
 * Pricing Agreement Monitor Region
 */
export class PricingAgreementMonitorRegion {
	/**
	 * Region Name
	 * Required: false
	 * Array: false
	 */
	name:string = undefined;
	/**
	 * Pam Counts
	 * Required: false
	 * Array: false
	 */
	regionTotal:PricingAgreementMonitorCounts = undefined;
	/**
	 * Pam Districts
	 * Required: false
	 * Array: true
	 */
	districts:PricingAgreementMonitorDistrict[] = undefined;
}
/**
 * Pricing Agreement Monitor District
 */
export class PricingAgreementMonitorDistrict {
	/**
	 * District Name
	 * Required: false
	 * Array: false
	 */
	name:string = undefined;
	/**
	 * Pam Counts
	 * Required: false
	 * Array: false
	 */
	districtTotal:PricingAgreementMonitorCounts = undefined;
	/**
	 * Pam Owners
	 * Required: false
	 * Array: true
	 */
	owners:PricingAgreementMonitorOwner[] = undefined;
}
export class PAMOwner {
	/**
	 * Unique identifier of the Pricing Agreement Monitor record
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorId:string = undefined;
	/**
	 * Employee Id of the owner
	 * Required: false
	 * Array: false
	 */
	ownerId:object = undefined;
}
/**
 * Pricing Agreement Monitor Owner
 */
export class PricingAgreementMonitorOwner {
	/**
	 * Owner
	 * Required: false
	 * Array: false
	 */
	owner:EmployeeId = undefined;
	/**
	 * Pam Counts
	 * Required: false
	 * Array: false
	 */
	counts:PricingAgreementMonitorCounts = undefined;
	/**
	 * Pam Types
	 * Required: false
	 * Array: true
	 */
	pamTypes:PricingAgreementMonitorTypeSummary[] = undefined;
}
/**
 * Pricing Agreement Monitor Summary
 */
export class PricingAgreementMonitorTypeSummary {
	/**
	 * Type of Pricing Agreement Monitor record: O/R or Rev or MON
	 * Required: false
	 * Array: false
	 */
	typeCode:string = undefined;
	/**
	 * Pam Counts
	 * Required: false
	 * Array: false
	 */
	counts:PricingAgreementMonitorCounts = undefined;
	/**
	 * Pam Records
	 * Required: false
	 * Array: true
	 */
	pamRecords:PricingAgreementMonitorRollup[] = undefined;
}
/**
 * Pricing Agreement Monitor Counts
 */
export class PricingAgreementMonitorCounts {
	/**
	 * Total number for Revenue records
	 * Required: false
	 * Array: false
	 */
	revCount:number = undefined;
	/**
	 * Total number for all records
	 * Required: false
	 * Array: false
	 */
	totalCount:number = undefined;
	/**
	 * Total number for Operating Ratio records
	 * Required: false
	 * Array: false
	 */
	orCount:number = undefined;
	/**
	 * Cost associated with the customer's shipments
	 * Required: false
	 * Array: false
	 */
	cost:MonetaryAmount = undefined;
	/**
	 * Monthly Revenue Amount
	 * Required: false
	 * Array: false
	 */
	revenueAmt:MonetaryAmount = undefined;
	/**
	 * Off Bill Incentive Amount
	 * Required: false
	 * Array: false
	 */
	offBillIncentiveAmt:MonetaryAmount = undefined;
	/**
	 * Profit Amount
	 * Required: false
	 * Array: false
	 */
	profitAmt:MonetaryAmount = undefined;
	/**
	 * Operating Ratio
	 * Required: false
	 * Array: false
	 */
	operatingRatio:number = undefined;
	/**
	 * Amount of revenue not attained
	 * Required: false
	 * Array: false
	 */
	revNotAttainedAmt:MonetaryAmount = undefined;
}
/**
 * Information about the parameters for Pricing Agreement Monitor and Expectations
 */
export class PricingAgreementMonitorParameter {
	/**
	 * Unique identifier of the Pricing Agreement Monitor Parameter record
	 * Required: false
	 * Array: false
	 */
	agreementMonitorParameterId:string = undefined;
	/**
	 * information about the user who is creating or updating the record and the date time it was created or updated
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Start effective date for a parameter used to monitor pricing agreement expectations.
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * End effective date for a parameter used to monitor pricing agreement expectations.
	 * Required: false
	 * Array: false
	 */
	expiryDate:Date = undefined;
	/**
	 * Operating ratio value checked. Any agreements with an OR greater than this value will have a monitor record created.
	 * Required: false
	 * Array: false
	 */
	maxOperatingRatio:number = undefined;
	/**
	 * The minimum monthly revenue the Account must have in order to be monitored.
	 * Required: false
	 * Array: false
	 */
	minMonthlyActualRev:MonetaryAmount = undefined;
	/**
	 * The minimum projected monthly revenue the Account must have in order to be monitored.
	 * Required: false
	 * Array: false
	 */
	minMonthlyProjectedRev:MonetaryAmount = undefined;
	/**
	 * The minimum OR the Account must have in order to be monitored.
	 * Required: false
	 * Array: false
	 */
	minOperatingRatio:number = undefined;
	/**
	 * The monthly profit loss the Account must have in order to be monitored.
	 * Required: false
	 * Array: false
	 */
	monthlyProfitLossAmt:MonetaryAmount = undefined;
	/**
	 * The difference between the Projected OR and Actual OR. If the difference is greater than this number a monitor record is created
	 * Required: false
	 * Array: false
	 */
	orChangePct:number = undefined;
	/**
	 * The percentage of projected revenue attained. If the percentage is below this number, a monitor record is created.
	 * Required: false
	 * Array: false
	 */
	projectedRevAttainmentPct:number = undefined;
	/**
	 * Type of Pricing: Local or National
	 * Required: false
	 * Array: false
	 */
	pricingTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	parameterTiers:PricingAgreementMonitorParameterTier[] = undefined;
}
/**
 * Information about the parameters for Pricing Agreement Monitor Tiers
 */
export class PricingAgreementMonitorParameterTier {
	/**
	 * Unique identifier of the Pricing Agreement Monitor Parameter Tier Record
	 * Required: false
	 * Array: false
	 */
	agreementMonitorParameterTierId:string = undefined;
	/**
	 * The minimum monthly projected revenue for this tier
	 * Required: false
	 * Array: false
	 */
	minMonthlyProjectedRev:MonetaryAmount = undefined;
	/**
	 * The maximum monthly projected revenue for this tier
	 * Required: false
	 * Array: false
	 */
	maxMonthlyProjectedRev:MonetaryAmount = undefined;
	/**
	 * Revenue Attainment Percentage for this Tier
	 * Required: false
	 * Array: false
	 */
	revAttainmentPct:number = undefined;
}
/**
 * Pricing Agreement Monitor Action Details
 */
export class PricingAgreementMonitorAction {
	/**
	 * Unique identifier of the Pricing Agreement Monitor Action record
	 * Required: false
	 * Array: false
	 */
	agreementMonitorActionId:string = undefined;
	/**
	 * information about the user who is creating or updating the record and the date time it was created or updated
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Action Type, Either 'Sales' or 'Pricing'
	 * Required: false
	 * Array: false
	 */
	actionType:string = undefined;
	/**
	 * Source the request is coming from, ie. OneCRM
	 * Required: false
	 * Array: false
	 */
	actionSource:string = undefined;
	/**
	 * Which action was taken
	 * Required: false
	 * Array: false
	 */
	actionTaken:string = undefined;
	/**
	 * Any comments related to this action
	 * Required: false
	 * Array: false
	 */
	commentText:string = undefined;
}
/**
 * Information about the parameters for Pricing Agreement Audit
 */
export class PricingAgreementAuditParameter {
	/**
	 * Unique identifier of the Pricing Agreement Audit Parameter record
	 * Required: false
	 * Array: false
	 */
	agreementAuditParameterId:string = undefined;
	/**
	 * information about the user who is creating or updating the record and the date time it was created or updated
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Start effective date for a parameter used to audit pricing agreement expectations.
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * End effective date for a parameter used to audit pricing agreement expectations.
	 * Required: false
	 * Array: false
	 */
	expiryDate:Date = undefined;
	/**
	 * Audit Percent
	 * Required: false
	 * Array: false
	 */
	auditPct:number = undefined;
	/**
	 * Pricing Analyst Id
	 * Required: false
	 * Array: false
	 */
	pricingAnalyst:EmployeeId = undefined;
}
/**
 * The absolute minimum charge with the scope for this AMC
 */
export class PricingAmc {
	/**
	 * unique id of the pricing amc record
	 * Required: false
	 * Array: false
	 */
	pricingAmcId:string = undefined;
	/**
	 * The absolute minimum charge amount
	 * Required: false
	 * Array: false
	 */
	amcAmt:MonetaryAmount = undefined;
	/**
	 * The scope for this amc. Interstate, Intrastate, International
	 * Required: false
	 * Array: false
	 */
	amcScopeIndicators:PricingScope = undefined;
}
/**
 * Shipment information used for the analysis that lead to the proposal. 
 */
export class PricingAnalysisFlow {
	/**
	 * Unique identifier of extract
	 * Required: false
	 * Array: false
	 */
	analysisFlowInstId:string = undefined;
	/**
	 * The workflow to follow for this analysis flow
	 * Required: false
	 * Array: false
	 */
	workflow:Workflow = undefined;
	/**
	 * The type of extract.
	 * Required: false
	 * Array: false
	 */
	extractTypeCd:string = undefined;
	/**
	 * The id of the proposal that is being analyzed.
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Status of the extract
	 * Required: false
	 * Array: false
	 */
	extractStatusCd:string = undefined;
	/**
	 * The current step of the workflow.
	 * Required: false
	 * Array: false
	 */
	analysisFlowCurrentStep:number = undefined;
	/**
	 * Comments related to the extract
	 * Required: false
	 * Array: false
	 */
	extractComment:string = undefined;
	/**
	 * customer account instance id
	 * Required: false
	 * Array: false
	 */
	acctInstId:string = undefined;
	/**
	 * customer account associated with the extract
	 * Required: false
	 * Array: false
	 */
	acctMadCd:string = undefined;
	/**
	 * The begin date for the extract
	 * Required: false
	 * Array: false
	 */
	extractStartDateTime:Date = undefined;
	/**
	 * The end date for the extract
	 * Required: false
	 * Array: false
	 */
	extractEndDateTime:Date = undefined;
	/**
	 * Label name  to be used for input field
	 * Required: false
	 * Array: false
	 */
	prompt1:string = undefined;
	/**
	 * Label name  to be used for input field
	 * Required: false
	 * Array: false
	 */
	prompt2:string = undefined;
	/**
	 * Label name  to be used for input field
	 * Required: false
	 * Array: false
	 */
	prompt3:string = undefined;
	/**
	 * Label name  to be used for input field
	 * Required: false
	 * Array: false
	 */
	prompt4:string = undefined;
	/**
	 * Label name  to be used for input field
	 * Required: false
	 * Array: false
	 */
	prompt5:string = undefined;
	/**
	 * Label name  to be used for input field
	 * Required: false
	 * Array: false
	 */
	prompt6:string = undefined;
	/**
	 * Label name  to be used for input field
	 * Required: false
	 * Array: false
	 */
	prompt7:string = undefined;
	/**
	 * Label name  to be used for input field
	 * Required: false
	 * Array: false
	 */
	prompt8:string = undefined;
	/**
	 * Label name  to be used for input field
	 * Required: false
	 * Array: false
	 */
	prompt9:string = undefined;
	/**
	 * Run Type
	 * Required: false
	 * Array: false
	 */
	runType:string = undefined;
	/**
	 * File Date Type
	 * Required: false
	 * Array: false
	 */
	fileDateType:string = undefined;
	/**
	 * Duration Multiplier
	 * Required: false
	 * Array: false
	 */
	durationMultiplier:number = undefined;
	/**
	 * Customer revenue override
	 * Required: false
	 * Array: false
	 */
	custRevOverride:MonetaryAmount = undefined;
	/**
	 * Calculated revenue
	 * Required: false
	 * Array: false
	 */
	calculatedRevenue:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Pricing Analysis State/Zip Geo Group header information
 */
export class PricingAnalysisGeoGroupHdr {
	/**
	 * Required: false
	 * Array: false
	 */
	amcCostAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	clusterDefinition:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	clusterId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	commentNote:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destCountryCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destGeoPrcGrpLaneMbrId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destGeoPrcGrpLaneMbrNm:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destStateCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	effRevIncrAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneNm:string = undefined;
	/**
	 * Recommended discount output of the leaf model
	 * Required: false
	 * Array: false
	 */
	leafRecommendedDisc:number = undefined;
	/**
	 * Averaged Market Discount Impact percentage output of the leaf model
	 * Required: false
	 * Array: false
	 */
	leafRecommendedMarketCdi:number = undefined;
	/**
	 * Operating Ratio based on proposed pricing for shipments  rated with AMC
	 * Required: false
	 * Array: false
	 */
	newOrAmcPct:number = undefined;
	/**
	 * Operating Ratio based on proposed pricing for shipments not rated with AMC
	 * Required: false
	 * Array: false
	 */
	newOrTariffPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origCountryCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origGeoPrcGrpLaneMbrId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origGeoPrcGrpLaneMbrNm:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origStateCd:string = undefined;
	/**
	 * Proposed Overall percentage increase based on new discount percentage from pricing analysis
	 * Required: false
	 * Array: false
	 */
	overallIncreasePrpsPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupBiAmc:GeoGroupBiAmc = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupBiTariff:GeoGroupBiTariff = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupCmAmc:GeoGroupCmAmc = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupCmTariff:GeoGroupCmTariff = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupDiscAmc:GeoGroupDiscountAmc = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupFfo:GeoGroupFFO = undefined;
	/**
	 * Captures how many months worth of shipment data is used for analysis
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupLaneScore:GeoGroupLaneScore = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnlysGeoGroupHdrBase:PricingAnalysisGeoGroupHdrBase = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prpsdMidIncrRevAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	statusCd:string = undefined;
}
/**
 * Pricing Analysis State/Zip Geo Group header information
 */
export class PricingAnalysisGeoGroupHdrBase {
	/**
	 * Current Operating Ratio percentage of shipments  rated with AMC included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	amcOrPct:number = undefined;
	/**
	 * Current profit amount of shipments  rated with AMC included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	amcProfitAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	averageCurrAmcAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	averageCurrDiscPct:number = undefined;
	/**
	 * Averaged total weight of shipment data included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	averageDensity:Density = undefined;
	/**
	 * Averaged total miles of shipment data included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	averageDist:Distance = undefined;
	/**
	 * Averaged total weight of shipment data included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	averageWeight:Weight = undefined;
	/**
	 * Average CTS599 discount impact of the shipments within the group
	 * Required: false
	 * Array: false
	 */
	cts599ImpactPct:number = undefined;
	/**
	 * Proposed percentage increase upper bound
	 * Required: false
	 * Array: false
	 */
	incrLevelRngPctHighValue:number = undefined;
	/**
	 * Proposed percentage increase lower bound
	 * Required: false
	 * Array: false
	 */
	incrLevelRngPctLowValue:number = undefined;
	/**
	 * Proposed percentage increase mid bound
	 * Required: false
	 * Array: false
	 */
	incrLevelRngPctMidValue:number = undefined;
	/**
	 * Total net revenue amount of shipment data  rated with AMC that are included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	netAmcRevAmt:MonetaryAmount = undefined;
	/**
	 * Total net revenue amount of shipment data included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	netRevAmt:MonetaryAmount = undefined;
	/**
	 * Total net revenue amount of shipment data not rated with AMC that are included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	netTariffRevAmt:MonetaryAmount = undefined;
	/**
	 * New Operating Ratio
	 * Required: false
	 * Array: false
	 */
	newOrPct:number = undefined;
	/**
	 * Current Operating Ratio percentage
	 * Required: false
	 * Array: false
	 */
	orPct:number = undefined;
	/**
	 * Overall Increase Percentage (Current Net Revenue / New Net Revenue)
	 * Required: false
	 * Array: false
	 */
	overallIncrPct:number = undefined;
	/**
	 * Current profit amount of shipments not rated with AMC included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	profitAmt:MonetaryAmount = undefined;
	/**
	 * Market Discount Impact output of the leaf model
	 * Required: false
	 * Array: false
	 */
	recmdMarketCdi:number = undefined;
	/**
	 * Market Discount Pct output of the leaf model
	 * Required: false
	 * Array: false
	 */
	recmdDiscount:number = undefined;
	/**
	 * Total number of shipments rated with AMC included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	shipmentAmcCount:number = undefined;
	/**
	 * Total number of shipments included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	shipmentCount:number = undefined;
	/**
	 * Total number of shipments not rated with AMC included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	shipmentTariffCount:number = undefined;
	/**
	 * Current Operating Ratio percentage of shipments not rated with AMC included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	tariffOrPct:number = undefined;
	/**
	 * Averaged tariff profit amount of the shipments within the group
	 * Required: false
	 * Array: false
	 */
	tariffProfitAmt:MonetaryAmount = undefined;
	/**
	 * Current total cost of shipments rated with AMC included  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	totalAmcCostAmt:MonetaryAmount = undefined;
	/**
	 * Current total cost amount of shipmentsincluded  in the group for pricing analysis
	 * Required: false
	 * Array: false
	 */
	totalCostAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalTariffCostAmt:MonetaryAmount = undefined;
	/**
	 * (Net Revenue Amount/Avg. Weight * Shipment Count) * 100
	 * Required: false
	 * Array: false
	 */
	totalYieldAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	averageRatedClassCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	averageActualClassCd:string = undefined;
	/**
	 * Averaged  Net Revenue based on current pricing agreement versus Market Price output of the leaf model
	 * Required: false
	 * Array: false
	 */
	leafCurrentVsMarketCdi:number = undefined;
	/**
	 * Averaged  Net Revenue based on proposed pricing versus Market Price output of the leaf model
	 * Required: false
	 * Array: false
	 */
	leafNewVsMarketCdi:number = undefined;
}
/**
 * Shipment information used for the analysis that lead to the proposal. 
 */
export class PricingAnalysisShipment {
	/**
	 * Actual class code
	 * Required: false
	 * Array: false
	 */
	actualClassCd:string = undefined;
	/**
	 * Actual revenue amount
	 * Required: false
	 * Array: false
	 */
	actualRevAmt:MonetaryAmount = undefined;
	/**
	 * Absolute Minimum Charge indicator
	 * Required: false
	 * Array: false
	 */
	amcInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * True if the bil2 is the debtor
	 * Required: false
	 * Array: false
	 */
	bil2DebtorInd:boolean = undefined;
	/**
	 * Instance Id for the bil2
	 * Required: false
	 * Array: false
	 */
	bil2InstId:string = undefined;
	/**
	 * Mad code for the bil2
	 * Required: false
	 * Array: false
	 */
	bil2MadCd:string = undefined;
	/**
	 * Name of the bil2
	 * Required: false
	 * Array: false
	 */
	bil2Nm:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	bulkLiquidInd:boolean = undefined;
	/**
	 * CDI Percent Better Local
	 * Required: false
	 * Array: false
	 */
	cdiPctBetterLocal:number = undefined;
	/**
	 * CDI Percent Better National
	 * Required: false
	 * Array: false
	 */
	cdiPctBetterNational:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cdiPctHighValue:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cdiPctLowValue:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	clusterCdiPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	clusterDefinition:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	clusterDiscountPct:number = undefined;
	/**
	 * True if this is a collect shipment
	 * Required: false
	 * Array: false
	 */
	collectShipmentInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	commentNote:string = undefined;
	/**
	 * Country code for the consignee location
	 * Required: false
	 * Array: false
	 */
	consigneeCountryCd:string = undefined;
	/**
	 * True if the consignee is the debtor
	 * Required: false
	 * Array: false
	 */
	consigneeDebtorInd:boolean = undefined;
	/**
	 * Instance Id for the consignee
	 * Required: false
	 * Array: false
	 */
	consigneeInstId:string = undefined;
	/**
	 * Name of the consignee
	 * Required: false
	 * Array: false
	 */
	consigneeNm:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	costCdiPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	currentAmcAmt:MonetaryAmount = undefined;
	/**
	 * Corporate discount impact for the current pricing agreement
	 * Required: false
	 * Array: false
	 */
	currentCts599Impact:number = undefined;
	/**
	 * Discount Amount based on Discount Percentage from current pricing agreement
	 * Required: false
	 * Array: false
	 */
	currentDiscountAmt:MonetaryAmount = undefined;
	/**
	 * Discount Percentage from current pricing agreement
	 * Required: false
	 * Array: false
	 */
	currentDiscountPct:number = undefined;
	/**
	 * freight charge amount from current pricing agreement
	 * Required: false
	 * Array: false
	 */
	currentFreightChargeAmt:MonetaryAmount = undefined;
	/**
	 * FALSE	Fuel Surcharge amount based on fsc percentage and freight charge amount from current pricing agreement
	 * Required: false
	 * Array: false
	 */
	currentFscAmt:MonetaryAmount = undefined;
	/**
	 * Total charges based on discounted linehaul charges, FSC, accessorials , OBI from current pricing agreement
	 * Required: false
	 * Array: false
	 */
	currentNetRevWithObi:MonetaryAmount = undefined;
	/**
	 * Operating Ratio (cost over net revenue) from current pricing agreement
	 * Required: false
	 * Array: false
	 */
	currentOrWithObi:number = undefined;
	/**
	 * Indicates if rated with AMC  for the current pricing agreement
	 * Required: false
	 * Array: false
	 */
	currentRatedWithAmcInd:boolean = undefined;
	/**
	 * source of the shipment data
	 * Required: false
	 * Array: false
	 */
	dataSourceCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destGeoPrcGrpLaneMbrId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destPrcGeoGrpLaneMbrNm:object = undefined;
	/**
	 * Discount Amount
	 * Required: false
	 * Array: false
	 */
	discountAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	discountSourceCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	division:string = undefined;
	/**
	 * FFN Lane Service Level Identifier
	 * Required: false
	 * Array: false
	 */
	ffnLaneServiceLevelId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneId:string = undefined;
	/**
	 * This shipment used Highway sub-service
	 * Required: false
	 * Array: false
	 */
	hssInd:boolean = undefined;
	/**
	 * HSS Lane Score Percent
	 * Required: false
	 * Array: false
	 */
	hssLaneScorePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	inboundInd:boolean = undefined;
	/**
	 * Include this shipment in the analysis
	 * Required: false
	 * Array: false
	 */
	includeForAnalysisInd:boolean = undefined;
	/**
	 * True if rated with Lineal Foot Pricing
	 * Required: false
	 * Array: false
	 */
	linealFtShpmtInd:boolean = undefined;
	/**
	 * Linehaul impact percent
	 * Required: false
	 * Array: false
	 */
	lnhImpactPct:number = undefined;
	/**
	 * Macro score percent
	 * Required: false
	 * Array: false
	 */
	macroScorePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	manualRatingReqdInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	marketCdiPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	marketDiscount:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	modelQualityCategory:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	modelQualityScore:number = undefined;
	/**
	 * Net revenue amount
	 * Required: false
	 * Array: false
	 */
	netRevAmt:MonetaryAmount = undefined;
	/**
	 * New AMC Amount
	 * Required: false
	 * Array: false
	 */
	newAmcAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origGeoPrcGrpLaneMbrId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origPrcGeoGrpLaneMbrNm:string = undefined;
	/**
	 * OR Percent Better Local
	 * Required: false
	 * Array: false
	 */
	orPctBetterLocal:number = undefined;
	/**
	 * OR Percent Better National
	 * Required: false
	 * Array: false
	 */
	orPctBetterNational:number = undefined;
	/**
	 * OR Percent Score
	 * Required: false
	 * Array: false
	 */
	orPctScore:number = undefined;
	/**
	 * OR with OBI
	 * Required: false
	 * Array: false
	 */
	orWithObi:MonetaryAmount = undefined;
	/**
	 * Other revenue amount
	 * Required: false
	 * Array: false
	 */
	otherRevAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	outboundInd:boolean = undefined;
	/**
	 * Overall Increase Percent
	 * Required: false
	 * Array: false
	 */
	overallIncreasePct:number = undefined;
	/**
	 * Overall Increase Proposed Percent
	 * Required: false
	 * Array: false
	 */
	overallIncrProposedPct:number = undefined;
	/**
	 * Overall Lane Score Percent
	 * Required: false
	 * Array: false
	 */
	overallLaneScorePct:number = undefined;
	/**
	 * The account instance id for the main customer on the pricing agreement in effect for this shipment
	 * Required: false
	 * Array: false
	 */
	paAcctInstId:string = undefined;
	/**
	 * The account mad cd for the main customer on the pricing agreement in effect for this shipment
	 * Required: false
	 * Array: false
	 */
	paAcctMadCd:string = undefined;
	/**
	 * The account name for the main customer on the pricing agreement in effect for this shipment
	 * Required: false
	 * Array: false
	 */
	paAcctNm:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	palletCnt:number = undefined;
	/**
	 * calendar month of the pickup
	 * Required: false
	 * Array: false
	 */
	pickupMonthName:string = undefined;
	/**
	 * The text month and numeric year of the pickup date - June 2015
	 * Required: false
	 * Array: false
	 */
	pickupMonthYear:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnalysisShipmentId:string = undefined;
	/**
	 * Unique identifier for Proposal that is being analyzed.
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnlysShipmentBase:PricingAnalysisShipmentBase = undefined;
	/**
	 * Proposed Accessorial Amount
	 * Required: false
	 * Array: false
	 */
	proposedAccessorialAmt:MonetaryAmount = undefined;
	/**
	 * Proposed AMC Amount
	 * Required: false
	 * Array: false
	 */
	proposedAmcAmt:MonetaryAmount = undefined;
	/**
	 * Proposed CDI Percent Better National
	 * Required: false
	 * Array: false
	 */
	proposedCdiPctBetterNation:number = undefined;
	/**
	 * Proposed Discount Amount
	 * Required: false
	 * Array: false
	 */
	proposedDiscountAmt:MonetaryAmount = undefined;
	/**
	 * Proposed Freight Charge Amount
	 * Required: false
	 * Array: false
	 */
	proposedFreightChgAmt:MonetaryAmount = undefined;
	/**
	 * Proposed FSC Amount
	 * Required: false
	 * Array: false
	 */
	proposedFscAmt:MonetaryAmount = undefined;
	/**
	 * Proposed Impact Type Code
	 * Required: false
	 * Array: false
	 */
	proposedImpactTypeCd:string = undefined;
	/**
	 * Proposed Increase Percent High Value
	 * Required: false
	 * Array: false
	 */
	proposedIncrPctHighValue:number = undefined;
	/**
	 * Proposed Increase Percent Low Value
	 * Required: false
	 * Array: false
	 */
	proposedIncrPctLowValue:number = undefined;
	/**
	 * Proposed OR Percent Better National
	 * Required: false
	 * Array: false
	 */
	proposedOrPctBetterNationa:number = undefined;
	/**
	 * Proposed OR Percent Score
	 * Required: false
	 * Array: false
	 */
	proposedOrPctScore:number = undefined;
	/**
	 * Proposed Overall Lane Score Percent
	 * Required: false
	 * Array: false
	 */
	proposedOvrllLaneScorePct:number = undefined;
	/**
	 * Indicates if rated with AMC  for the new pricing
	 * Required: false
	 * Array: false
	 */
	proposedRatedWithAmcInd:boolean = undefined;
	/**
	 * Rating Absolute Minimum Charge indicator
	 * Required: false
	 * Array: false
	 */
	ratingShipmentAmcInd:boolean = undefined;
	/**
	 * rating tariff description
	 * Required: false
	 * Array: false
	 */
	ratingTariffDesc:string = undefined;
	/**
	 * Identifies the actual rating tariff (RTAR) used to rate the shipment.
	 * Required: false
	 * Array: false
	 */
	ratingTariffId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	segmentId:string = undefined;
	/**
	 * Unique identifier for Pricing Analysis Shipment-Level record
	 * Required: false
	 * Array: false
	 */
	shipInstId:string = undefined;
	/**
	 * Country code for the shipper location
	 * Required: false
	 * Array: false
	 */
	shipperCountryCd:string = undefined;
	/**
	 * True if the shipper is the debtor
	 * Required: false
	 * Array: false
	 */
	shipperDebtorInd:boolean = undefined;
	/**
	 * Instance ID for the shipper
	 * Required: false
	 * Array: false
	 */
	shipperInstId:string = undefined;
	/**
	 * Name of the shipper
	 * Required: false
	 * Array: false
	 */
	shipperNm:string = undefined;
	/**
	 * Applicable Discount Percentage
	 * Required: false
	 * Array: false
	 */
	shpmtAplcblDiscntPct:number = undefined;
	/**
	 * Actual commodity class
	 * Required: false
	 * Array: false
	 */
	shpmtAsEntrdAvgNmfcAdj:number = undefined;
	/**
	 * As Rated commodity class
	 * Required: false
	 * Array: false
	 */
	shpmtAsRtdAvgNmfcAdj:number = undefined;
	/**
	 * Average Cost Model Density
	 * Required: false
	 * Array: false
	 */
	shpmtAvgCostModelDensity:number = undefined;
	/**
	 * Total claim amount of the shipment
	 * Required: false
	 * Array: false
	 */
	shpmtClaimsAmt:MonetaryAmount = undefined;
	/**
	 * Total undiscounted linehaul charges, which is the commodity lines only (excludes ACs, FSC, discounts, etc.) when rated using corporate tariff CTS599
	 * Required: false
	 * Array: false
	 */
	shpmtCorpRtgPassLnhChg:MonetaryAmount = undefined;
	/**
	 * Cost Density Source Code
	 * Required: false
	 * Array: false
	 */
	shpmtCostDnstySrcCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shpmtDeliveryDate:Date = undefined;
	/**
	 * Shipper to Consignee Miles
	 * Required: false
	 * Array: false
	 */
	shpmtDistance:Distance = undefined;
	/**
	 * Fuel Surcharge Percent used to calculate FSC amount
	 * Required: false
	 * Array: false
	 */
	shpmtEffFscPct:number = undefined;
	/**
	 * Total amount of Accessorials (without FSC) applied to the shipment
	 * Required: false
	 * Array: false
	 */
	shpmtInvcRtgPassAccsrlAmt:MonetaryAmount = undefined;
	/**
	 * Total Discount Amount of the shipment
	 * Required: false
	 * Array: false
	 */
	shpmtInvcRtgPassDiscnt:MonetaryAmount = undefined;
	/**
	 * Total amount of Fuel Surcharge applied to the shipment
	 * Required: false
	 * Array: false
	 */
	shpmtInvcRtgPassFsc:MonetaryAmount = undefined;
	/**
	 * Total undiscounted linehaul charges, which is the commodity lines only (excludes ACs, FSC, discounts, etc.) when rated using actual tariff from the applied pricing agreement ruleset
	 * Required: false
	 * Array: false
	 */
	shpmtInvcRtgPassLnhChg:MonetaryAmount = undefined;
	/**
	 * OBI Incentive Discount Adjustment Amount
	 * Required: false
	 * Array: false
	 */
	shpmtObiDiscntAdjAmt:MonetaryAmount = undefined;
	/**
	 * Total paid claim amount of the shipment
	 * Required: false
	 * Array: false
	 */
	shpmtPaidClaimsAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shpmtPallets:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shpmtPctOfBusiness:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shpmtPieces:number = undefined;
	/**
	 * Total cost amount of the shipment
	 * Required: false
	 * Array: false
	 */
	shpmtTotCostAmt:MonetaryAmount = undefined;
	/**
	 * Total weight of the shipment
	 * Required: false
	 * Array: false
	 */
	shpmtWeight:Weight = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	statusCd:string = undefined;
	/**
	 * Standard number of days it should take to move a shipment from origin to destination
	 * Required: false
	 * Array: false
	 */
	stdTransitDays:number = undefined;
	/**
	 * Strategy Proposed
	 * Aggressive Growth (Less than 0%)
	 * Grow (0-2.9)
	 * Maintain (3-6.9)
	 * At Risk (7-10.9)
	 * Lose (11+)
	 * Required: false
	 * Array: false
	 */
	strategyProposed:string = undefined;
	/**
	 * Tariff Revenue amount
	 * Required: false
	 * Array: false
	 */
	tariffRevAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	thirdPartyInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalVolume:Volume = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	tripMiles:string = undefined;
	/**
	 * True if rated under a Volume Shipment Program
	 * Required: false
	 * Array: false
	 */
	vspShpmtInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	willingnessToPay:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnlysGeoGroupHdrBase:PricingAnalysisGeoGroupHdrBase = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	geoGroupDiscountAmcBase:GeoGroupDiscountAmcBase = undefined;
	/**
	 * Proposed Fuel Surcharge Percent
	 * Required: false
	 * Array: false
	 */
	proposedFscPct:number = undefined;
}
export class PricingAnalysisShipmentAccessorial {
	/**
	 * unique identifir of pricing analysis shipment accessorial record
	 * Required: false
	 * Array: false
	 */
	prcAnlysShipmentAccsrlId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pronbr:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shipmentWeight:Weight = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	increasePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shipInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialInfo:PricingAccessorial = undefined;
}
/**
 * Shipment information used for the analysis that lead to the proposal. 
 */
export class PricingAnalysisShipmentBase {
	/**
	 * Actual class code
	 * Required: false
	 * Array: false
	 */
	actualClassCd:string = undefined;
	/**
	 * Mad code for the bil2
	 * Required: false
	 * Array: false
	 */
	bil2MadCd:string = undefined;
	/**
	 * If PRC_ANALYSIS_SHIPMENT.INBOUND_IND = Y then Inbound
	 * If PRC_ANALYSIS_SHIPMENT.OUTBOUND_IND = Y then Outbound
	 * If PRC_ANALYSIS_SHIPMENT.THIRD_PARTY_IND = Y then Third Party
	 * Required: false
	 * Array: false
	 */
	billingTypeCd:string = undefined;
	/**
	 * If PRC_ANALYSIS_SHIPMENT.COLLEC_SHIPMENT_ID = 'Y' then COL else PPD
	 * Required: false
	 * Array: false
	 */
	chargeToCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	clusterId:string = undefined;
	/**
	 * Mad code for the consignee
	 * Required: false
	 * Array: false
	 */
	consigneeMadCd:string = undefined;
	/**
	 * State code for the consignee location
	 * Required: false
	 * Array: false
	 */
	consigneeStateCd:string = undefined;
	/**
	 * If PRC_ANALYSIS_SHIPMENT.CONSIGNEE_DEBTOR_IND = 'Y' then 'CONSIGNEE'.
	 * If PRC_ANALYSIS_SHIPMENT.SHIPPER_DEBTOR_IND = 'Y' then 'SHIPPER'
	 * If PRC_ANALYSIS_SHIPMENT.BIL_2_DEBTOR_IND = 'Y' then 'BILL-TO')
	 * Required: false
	 * Array: false
	 */
	debtorCd:string = undefined;
	/**
	 * destination geographic area that shares common pricing elements, including tariffs, discounts, AMCs, FAK - keep
	 * Required: false
	 * Array: false
	 */
	destGeoPrcGrpLaneMbrNm:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	geoPricingGroupLaneNm:string = undefined;
	/**
	 * origin geographic area that shares common pricing elements, including tariffs, discounts, AMCs, FAK
	 * Required: false
	 * Array: false
	 */
	origGeoPrcGrpLaneMbrNm:string = undefined;
	/**
	 * The pickup date of the shipment - 6/1/2015
	 * Required: false
	 * Array: false
	 */
	pickupDate:Date = undefined;
	/**
	 * The business key for the shipment-
	 * Required: false
	 * Array: false
	 */
	proNbr:string = undefined;
	/**
	 * Rated class code
	 * Required: false
	 * Array: false
	 */
	ratedClassCd:string = undefined;
	/**
	 * rating tariff short description
	 * Required: false
	 * Array: false
	 */
	ratingTariff:string = undefined;
	/**
	 * Mad code for the shipper
	 * Required: false
	 * Array: false
	 */
	shipperMadCd:string = undefined;
	/**
	 * State code of the shipper location
	 * Required: false
	 * Array: false
	 */
	shipperStateCd:string = undefined;
	/**
	 * traffic type code
	 * Required: false
	 * Array: false
	 */
	trafficTypeCd:string = undefined;
	/**
	 * weight break code
	 * Required: false
	 * Array: false
	 */
	weightBreakCd:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	shipmentInstIds:string[] = undefined;
	/**
	 * Origin sic for the shipment
	 * Required: false
	 * Array: false
	 */
	origSic:string = undefined;
	/**
	 * destination sic for the shipment
	 * Required: false
	 * Array: false
	 */
	destSic:string = undefined;
	/**
	 * Postal code for the consignee location
	 * Required: false
	 * Array: false
	 */
	consigneePostalCd:string = undefined;
	/**
	 * Postal code for the shipper location
	 * Required: false
	 * Array: false
	 */
	shipperPostalCd:string = undefined;
}
/**
 * Minimum Shipment information used for creating the Analysis Shipment records for a Proposal
 */
export class PricingAnalysisShipmentHdr {
	/**
	 * Unique identifier for Proposal that is being analyzed.
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Unique identifier for Pricing Analysis Shipment-Level record
	 * Required: false
	 * Array: false
	 */
	shipInstId:string = undefined;
	/**
	 * The pickup date of the shipment - 6/1/2015
	 * Required: false
	 * Array: false
	 */
	pickupDate:Date = undefined;
	/**
	 * Postal code for the shipper location
	 * Required: false
	 * Array: false
	 */
	shipperPostalCd:string = undefined;
	/**
	 * Country code for the shipper location
	 * Required: false
	 * Array: false
	 */
	shipperCountryCd:string = undefined;
	/**
	 * Postal code for the consignee location
	 * Required: false
	 * Array: false
	 */
	consigneePostalCd:string = undefined;
	/**
	 * Country code for the consignee location
	 * Required: false
	 * Array: false
	 */
	consigneeCountryCd:string = undefined;
	/**
	 * Total weight of the shipment
	 * Required: false
	 * Array: false
	 */
	shpmtWeight:Weight = undefined;
	/**
	 * Tariff Revenue amount
	 * Required: false
	 * Array: false
	 */
	tariffRevAmt:MonetaryAmount = undefined;
	/**
	 * Actual class code
	 * Required: false
	 * Array: false
	 */
	actualClassCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shpmtPieces:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shpmtPallets:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shpmtDeliveryDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shpmtPctOfBusiness:number = undefined;
}
/**
 * A pricing analyst and the manager to whiom they report
 */
export class PricingAnalyst {
	/**
	 * Pricing analyst employee ID
	 * Required: false
	 * Array: false
	 */
	pricingAnalystEmplId:string = undefined;
	/**
	 * First and Last name of the pricing analyst
	 * Required: false
	 * Array: false
	 */
	pricingAnalystFullName:string = undefined;
	/**
	 * The employee id of the analyst's manager
	 * Required: false
	 * Array: false
	 */
	pricingMgrEmplId:string = undefined;
	/**
	 * The full name of the analyst's manager
	 * Required: false
	 * Array: false
	 */
	pricingMgrFullName:string = undefined;
}
/**
 * Stores the assignment for each Pricing Analyst by AE/SAM, District (formerly Sales Region), Region (formerly Area), Regional (formerly Local), Strategic (formerly National)
 */
export class PricingAnalystAssignment {
	/**
	 * Unique identifier for the analyst assignment record
	 * Required: false
	 * Array: false
	 */
	analystAssignmentId:string = undefined;
	/**
	 * Identifies the type of assignment for the pricing analyst.
	 * Valid Values:
	 * 1 - AE/SAM
	 * 2 - District (formerly Sales Region)
	 * 3 - Region (formerly Area )
	 * 4  - Regional (formerly Local)
	 * 5  - Strategic (formerly National)
	 * 6 -  Regional 3PL
	 * 7 -  Strategic 3PL
	 * Required: false
	 * Array: false
	 */
	assignmentTypeCd:string = undefined;
	/**
	 * The employee ID of the Account Executive (Local or National)
	 * Required: false
	 * Array: false
	 */
	acctExecEmployeeId:string = undefined;
	/**
	 * First Name and Last Name of the Account Executive (Local or National)
	 * Required: false
	 * Array: false
	 */
	acctExecFullName:string = undefined;
	/**
	 * The employee ID of the National Account Sales Supervisor
	 * Required: false
	 * Array: false
	 */
	nasSupervisorEmployeeId:string = undefined;
	/**
	 * First Name and Last Name of the National Account Sales Supervisor
	 * Required: false
	 * Array: false
	 */
	nasSupervisorFullName:string = undefined;
	/**
	 * The employee ID of the National Account Sales Specialist
	 * Required: false
	 * Array: false
	 */
	nasSpecialistEmployeeId:string = undefined;
	/**
	 * First Name and Last Name of the National Account Sales Specialist
	 * Required: false
	 * Array: false
	 */
	nasSpecialistFullName:string = undefined;
	/**
	 * A geographical region (now called District) for which an account executive is responsible of (i.e. RCA)
	 * Required: false
	 * Array: false
	 */
	salesRegionCode:string = undefined;
	/**
	 * A geographical area for a location (now called Region) (i.e. CCA)
	 * Required: false
	 * Array: false
	 */
	areaCode:string = undefined;
	/**
	 * The type of pricing agreement (i.e. P360, In-Cycle)
	 * Required: false
	 * Array: false
	 */
	agreementTypeCd:string = undefined;
	/**
	 * The type of request for pricing (i.e. Renegotiation, New Opportunity)
	 * Required: false
	 * Array: false
	 */
	rfpTypeCd:string = undefined;
	/**
	 * The employee ID of the Pricing Analyst
	 * Required: false
	 * Array: false
	 */
	analystEmployeeId:string = undefined;
	/**
	 * First Name and Last Name of the Pricing Analyst
	 * Required: false
	 * Array: false
	 */
	analystFullName:string = undefined;
	/**
	 * A value of 'Y' on this field will indicate that this is the primary analyst assigned to this assignment record. There could be more than one Pricing Analyst for a unique AE or a  Sales Region or an Area
	 * Required: false
	 * Array: false
	 */
	primaryAssignmentInd:boolean = undefined;
	/**
	 * The date the assignment record was effective
	 * Required: false
	 * Array: false
	 */
	effectiveDate:Date = undefined;
	/**
	 * The date when the code is expired
	 * Required: false
	 * Array: false
	 */
	expirationDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	currentAnnualRevenue:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Factors that influence the best grouping that will be proposed by the Pricing Analyst. For example: Can't accept discounts by zip 3 only state to state.
 */
export class PricingBestGroupingConstraints {
	/**
	 * A type code for the type of constraint
	 * Required: false
	 * Array: true
	 */
	constraintTypeCd:string[] = undefined;
	/**
	 * The value for this constraint
	 * Required: false
	 * Array: false
	 */
	constraintValue:string = undefined;
}
/**
 * Basic information about the commodity that is being shipped with class and percentage of business
 */
export class PricingCommodity {
	/**
	 * unique identifier of Pricing Commodity
	 * Required: false
	 * Array: false
	 */
	commodityId:string = undefined;
	/**
	 * The actual class for the commodity
	 * Required: false
	 * Array: false
	 */
	commodityNmfcClass:string = undefined;
	/**
	 * The NMFC item for the commodity
	 * Required: false
	 * Array: false
	 */
	commodityNmfcItem:string = undefined;
	/**
	 * Description of the commodity
	 * Required: false
	 * Array: false
	 */
	commodityDesc:string = undefined;
	/**
	 * The percentage of total business for the commodity
	 * Required: false
	 * Array: false
	 */
	commodityPctOfBusiness:number = undefined;
}
/**
 * Factors that justify the sales proposal. Other factors might be added not just competitor information, like proximity to service center
 */
export class PricingCompetingCarrier {
	/**
	 * The Standard Carrier Alpha Code (SCAC) of the competing carrier
	 * Required: false
	 * Array: false
	 */
	competingCarrierScac:string = undefined;
	/**
	 * Name of the competing carrier that currently does business with the customer
	 * Required: false
	 * Array: false
	 */
	competingCarrierName:string = undefined;
	/**
	 * This is the percentage of customer's total business the competing carrier gets.
	 * Required: false
	 * Array: false
	 */
	competingCarrierMarketShare:number = undefined;
	/**
	 * This is the discount the customer receives from the competing carrier.
	 * Required: false
	 * Array: false
	 */
	competingCarrierDiscount:MonetaryAmount = undefined;
	/**
	 * This describes the rate base the competing carrier offers the customer.
	 * Required: false
	 * Array: false
	 */
	competingCarrierRatebase:string = undefined;
	/**
	 * This describes the FAK if any offered by the competing carrier to the customer
	 * Required: false
	 * Array: false
	 */
	competingCarrierFak:number = undefined;
	/**
	 * This is the AMC amount the competing carrier offers the customer
	 * Required: false
	 * Array: false
	 */
	competingCarrierAmc:MonetaryAmount = undefined;
	/**
	 * This text identifies the lanes the competing carrier moves freight for the customer.
	 * Required: false
	 * Array: false
	 */
	competingCarrierLanes:Lane = undefined;
}
/**
 * The discount and scope of the discount
 */
export class PricingDiscount {
	/**
	 * unique id of the discount record
	 * Required: false
	 * Array: false
	 */
	pricingDiscountId:string = undefined;
	/**
	 * Type of Range: Eg. Pallet, Weight break or Piece Count, etc.
	 * Required: false
	 * Array: false
	 */
	rangeTypeCode:string = undefined;
	/**
	 * Unit of measure for the range type. Unit of Measure for the Range Type
	 * if Range type Code = Weight then UOM code = LB or KG
	 * Required: false
	 * Array: false
	 */
	rangeUom:string = undefined;
	/**
	 * If range type is Pallet then this is the Minimum Pallet count value
	 * 
	 * If range type is Weight Break then this is the Minimum Weight value
	 * 
	 * If range type is Piece Count then this is the Minimum Piece Count value
	 * Required: false
	 * Array: false
	 */
	beginRangeValue:number = undefined;
	/**
	 * If range type is Pallet then this is the Maximum Pallet count value
	 * 
	 * If range type is Weight Break then this is the Maximum Weight value
	 * 
	 * If range type is Piece Count then this is the Maximum Piece Count value
	 * Required: false
	 * Array: false
	 */
	endRangeValue:number = undefined;
	/**
	 * The discount amount for this   range
	 * Required: false
	 * Array: false
	 */
	discountAmt:MonetaryAmount = undefined;
	/**
	 * Discount Percent as requested by the Sales Person
	 * Required: false
	 * Array: false
	 */
	discountPct:number = undefined;
	/**
	 * The discount percentage off the amc
	 * Required: false
	 * Array: false
	 */
	amcDiscountPct:number = undefined;
	/**
	 * Applicable discount in %, when minimum charges are applied
	 * Required: false
	 * Array: false
	 */
	minChargeDiscountPct:number = undefined;
	/**
	 * The scope for this amc. Interstate, Intrastate, International
	 * Required: false
	 * Array: false
	 */
	discountScopeIndicators:PricingScope = undefined;
}
/**
 * FAK information for a Geo Pricing Group
 */
export class PricingFak {
	/**
	 * unique identifier of Pricing Proposal FAK Header record
	 * Required: false
	 * Array: false
	 */
	prcProposalFakHdrId:string = undefined;
	/**
	 * unique identifier of Sales Rfp requeste Fak record
	 * Required: false
	 * Array: false
	 */
	requestedFakId:string = undefined;
	/**
	 * Identifies the type (class) of commodity being shipped along with the FAK that overrides it.
	 * Required: false
	 * Array: true
	 */
	classFakOverrides:ClassOverride[] = undefined;
	/**
	 * Indicators that show the scope of the rates or discounts
	 * Required: false
	 * Array: false
	 */
	pricingScopeIndicators:PricingScope = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fakType:string = undefined;
}
export class PricingGroupShipmentAccsrl {
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialChgAmt:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialOverrideChgAmt:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialOverrideChgPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialUom:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	increasePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shipmentCount:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalShipmentWeight:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalAccessorialAmt:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	avgAccessorialAmt:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposedTotalAccessorialAmt:number = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	shipmentAccsrlInstId:string[] = undefined;
}
export class PricingGroupShipmentAmc {
	/**
	 * Required: false
	 * Array: false
	 */
	trafficTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	newAmcAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalProposedRevAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	avgProposedOrPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shipmentCount:number = undefined;
}
export class PricingGroupSummaryShipment {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnlysShipmentBase:PricingAnalysisShipmentBase = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnlysShipmentAccsrl:PricingAnalysisShipmentAccessorial = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupDiscAmc:GeoGroupDiscountAmc = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnlysGeoGroupHdrBase:PricingAnalysisGeoGroupHdrBase = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupBiAmc:GeoGroupBiAmc = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupBiTariff:GeoGroupBiTariff = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupCmAmc:GeoGroupCmAmc = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcAnlysGeoGroupCmTariff:GeoGroupCmTariff = undefined;
}
/**
 * Factors that justify the sales proposal. Other factors might be added not just competitor information, like proximity to service center
 */
export class PricingJustification {
	/**
	 * The customers reasons for seeking a different carrier than the one they are currently using. Example: Damages, Shortages, Billing or Invoicing Issues
	 * Required: false
	 * Array: true
	 */
	custExplicitNeedsCd:string[] = undefined;
	/**
	 * Various elements known about competing carrier's relationship with the customer
	 * Required: false
	 * Array: true
	 */
	competingCarrierInfo:PricingCompetingCarrier[] = undefined;
	/**
	 * links to documents or key values to retrieve documents
	 * Required: false
	 * Array: true
	 */
	supportingDocuments:Document[] = undefined;
}
/**
 * Information about the Lading unit dimension
 */
export class PricingLadingDimension {
	/**
	 * Unique identifier of the Lading dimension
	 * Required: false
	 * Array: false
	 */
	ladingDimensionId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ladingLength:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ladingWidth:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ladingHeight:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ladingPctOfBusiness:number = undefined;
	/**
	 * The average weight for this lading unit
	 * Required: false
	 * Array: false
	 */
	ladingAvgWeight:Weight = undefined;
	/**
	 * Density of lading unit
	 * Required: false
	 * Array: false
	 */
	ladingAvgDensity:number = undefined;
}
/**
 * Lading Unit characteristics
 */
export class PricingLadingUnit {
	/**
	 * An Id that identifies this set of lading unit characteristics to be used in LadingUnitPricing
	 * Required: false
	 * Array: false
	 */
	ladingUnitId:string = undefined;
	/**
	 * The type of lading unit if known
	 * Required: false
	 * Array: false
	 */
	ladingUnitPackageTypeCd:string = undefined;
	/**
	 * Length, width and height of the lading unit
	 * Required: false
	 * Array: false
	 */
	ladingDimension:Dimensions = undefined;
	/**
	 * The average weight for this lading unit
	 * Required: false
	 * Array: false
	 */
	ladingAvgWeight:Weight = undefined;
	/**
	 * Density of lading unit
	 * Required: false
	 * Array: false
	 */
	ladingAvgDensity:number = undefined;
	/**
	 * average number of pieces of this lading unit
	 * Required: false
	 * Array: false
	 */
	ladingAvgPieceCount:number = undefined;
	/**
	 * The percentage of total business for the lading unit of these characteristics
	 * Required: false
	 * Array: false
	 */
	ladingUnitPctOfBusiness:number = undefined;
}
/**
 * Sets of lanes with commodity information for that set of lanes and the percentage of the customer's shipping business on that lane.
 */
export class PricingLane {
	/**
	 * an identifier for the lane
	 * Required: false
	 * Array: false
	 */
	laneId:string = undefined;
	/**
	 * defined by origin and destination areas
	 * Required: false
	 * Array: true
	 */
	lane:PricingLane_Lane[] = undefined;
	/**
	 * The actual commodity classes for the group of origin/destination lanes. The index of this array goes with the NMFCItem array
	 * Required: false
	 * Array: true
	 */
	laneExpectedNmfcClass:string[] = undefined;
	/**
	 * The actual commodity NMFC items for the group of origin/destination lanes.
	 * Required: false
	 * Array: true
	 */
	laneExpectedNmfcItem:string[] = undefined;
	/**
	 * The average freight class for the group of origin/destination lanes.
	 * Required: false
	 * Array: false
	 */
	laneExpectedAvgClass:number = undefined;
	/**
	 * The average density for the group of origin/destination lanes.
	 * Required: false
	 * Array: false
	 */
	laneExpectedAvgDensity:Density = undefined;
	/**
	 * The average weight for the group of origin/destination lanes
	 * Required: false
	 * Array: false
	 */
	laneExpectedAvgWeight:Weight = undefined;
	/**
	 * The average piece count for the group of origin/destination lanes
	 * Required: false
	 * Array: false
	 */
	laneExpectedAvgPieceCount:number = undefined;
	/**
	 * The percentage of total business for the group of origin/destination lanes
	 * Required: false
	 * Array: false
	 */
	laneExpectedPctOfBusiness:number = undefined;
}
/**
 * This is a view of shipment with factors used by the Pricing Statistical models
 */
export class PricingLeafShipment {
	/**
	 * Indicates if shipment is rated with Absolute Minimum Charge
	 * Required: false
	 * Array: false
	 */
	amcInd:boolean = undefined;
	/**
	 * Unique identifier for Pricing Analysis Shipment-Level record
	 * Required: false
	 * Array: false
	 */
	analysisShipInstId:string = undefined;
	/**
	 * The average NMFC commodity class
	 * Required: false
	 * Array: false
	 */
	avgClassCd:string = undefined;
	/**
	 * Indicates if shipment is bulk liquid
	 * Required: false
	 * Array: false
	 */
	bulkLiquidInd:boolean = undefined;
	/**
	 * Corporate Discount Impact output of the segmentation model
	 * Required: false
	 * Array: false
	 */
	cdiPct:number = undefined;
	/**
	 * Higher Corporate Discount Impact bound output of the segmentation model
	 * Required: false
	 * Array: false
	 */
	cdiPctHighValue:number = undefined;
	/**
	 * Lower Corporate Discount Impact bound output of the segmentation model
	 * Required: false
	 * Array: false
	 */
	cdiPctLowValue:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	classWeighted:string = undefined;
	/**
	 * Corporate Discount Impact to cluster output of the leaf model
	 * Required: false
	 * Array: false
	 */
	clusterCdiPct:number = undefined;
	/**
	 * Discount to cluster output of the leaf model
	 * Required: false
	 * Array: false
	 */
	clusterDiscountPct:number = undefined;
	/**
	 * Other notes
	 * Required: false
	 * Array: false
	 */
	comments:Comment = undefined;
	/**
	 * Cost Corporate Discount Impact
	 * Required: false
	 * Array: false
	 */
	costCdiPct:number = undefined;
	/**
	 * Total undiscounted linehaul charges, which is the commodity lines only (excludes ACs, FSC, discounts, etc.) when rated using corporate tariff CTS599
	 * Required: false
	 * Array: false
	 */
	cts599Amt:MonetaryAmount = undefined;
	/**
	 * Annual Customer Revenue
	 * Required: false
	 * Array: false
	 */
	custRevAmt:MonetaryAmount = undefined;
	/**
	 * Destination operational region
	 * Required: false
	 * Array: false
	 */
	destOpsRegionCd:string = undefined;
	/**
	 * unique id of the destination ops region
	 * Required: false
	 * Array: false
	 */
	destOpsRegionId:string = undefined;
	/**
	 * destinaton sic code
	 * Required: false
	 * Array: false
	 */
	destSicCd:string = undefined;
	/**
	 * Number of shipments with the same destination SIC
	 * Required: false
	 * Array: false
	 */
	destSicCnt:number = undefined;
	/**
	 * State where the  destination is located
	 * Required: false
	 * Array: false
	 */
	destStateCd:string = undefined;
	/**
	 * 1st character of destination zip code
	 * Required: false
	 * Array: false
	 */
	destZip1:string = undefined;
	/**
	 * Number of distinct first charater of the destination zip code
	 * Required: false
	 * Array: false
	 */
	destZip1Cnt:number = undefined;
	/**
	 * 1st two chars of dest zip code
	 * Required: false
	 * Array: false
	 */
	destZip2:string = undefined;
	/**
	 * Number of distinct first 2 charaters of the destination zip code
	 * Required: false
	 * Array: false
	 */
	destZip2Cnt:number = undefined;
	/**
	 * First 3 characters of the zip code for a US address or first 6 characters of the Canadian zip code of the address where the consignee is located
	 * Required: false
	 * Array: false
	 */
	destZip3Cd:string = undefined;
	/**
	 * First 5 characters of the zip code for a US address or first 6 characters of the Canadian zip code of the address where the consignee is located
	 * Required: false
	 * Array: false
	 */
	destZipCd:string = undefined;
	/**
	 * Number of distinct destination zip
	 * Required: false
	 * Array: false
	 */
	destZipCnt:number = undefined;
	/**
	 * XPO inbound revenue from NTD (National Traffic Db) for this destination zip
	 * Required: false
	 * Array: false
	 */
	destZipNtdCnwInbRevAmt:MonetaryAmount = undefined;
	/**
	 * Other company (non-XPO) outbound weight from NTD
	 * Required: false
	 * Array: false
	 */
	destZipNtdLtlInbWeight:Weight = undefined;
	/**
	 * Employee Range of the customer
	 * Required: false
	 * Array: false
	 */
	empRangeNm:string = undefined;
	/**
	 * Shipper to Consignee Miles
	 * Required: false
	 * Array: false
	 */
	laneDistance:Distance = undefined;
	/**
	 * Number of distinct regions
	 * Required: false
	 * Array: false
	 */
	laneRegionCnt:number = undefined;
	/**
	 * Region Name range
	 * Required: false
	 * Array: false
	 */
	laneRegionRangeNm:string = undefined;
	/**
	 * Origin and destination SIC
	 * Required: false
	 * Array: false
	 */
	laneSic:string = undefined;
	/**
	 * Number of distinct SIC
	 * Required: false
	 * Array: false
	 */
	laneSicCnt:number = undefined;
	/**
	 * Number of distinct states
	 * Required: false
	 * Array: false
	 */
	laneStateCnt:number = undefined;
	/**
	 * State Code range
	 * Required: false
	 * Array: false
	 */
	laneStateRangeNm:string = undefined;
	/**
	 * First character of the origin zip code and first character of the destination zip code range
	 * Required: false
	 * Array: false
	 */
	laneZip1:string = undefined;
	/**
	 * First 2 characters of the origin zip code and first 2 characters of the destination zip code range
	 * Required: false
	 * Array: false
	 */
	laneZip2:string = undefined;
	/**
	 * 1st 3 chars of origin and destination zip codes
	 * Required: false
	 * Array: false
	 */
	laneZip3:string = undefined;
	/**
	 * Number of distinct first 3 characters of the origin zip code and first 3 characters of the destination zip code range
	 * Required: false
	 * Array: false
	 */
	laneZip3Cnt:number = undefined;
	/**
	 * First 3 characters of the origin zip code and first 3 characters of the destination zip code range
	 * Required: false
	 * Array: false
	 */
	laneZip3LaneRangeNm:string = undefined;
	/**
	 * Origin zip code and destination zip code range
	 * Required: false
	 * Array: false
	 */
	laneZipRangeNm:string = undefined;
	/**
	 * Indicates if shipment is manually rated
	 * Required: false
	 * Array: false
	 */
	manualRatingRqrdInd:boolean = undefined;
	/**
	 * Market Discount Impact output of the leaf model
	 * Required: false
	 * Array: false
	 */
	marketCdiPct:number = undefined;
	/**
	 * Number of shipments per month of the customer
	 * Required: false
	 * Array: false
	 */
	monthlyShpmtCnt:number = undefined;
	/**
	 * XPO lane revenue from NTD (National Traffic Db)
	 * Required: false
	 * Array: false
	 */
	ntdCnwLaneRev:MonetaryAmount = undefined;
	/**
	 * Origin operational region
	 * Required: false
	 * Array: false
	 */
	origOpsRegionCd:string = undefined;
	/**
	 * unique id of the origin region
	 * Required: false
	 * Array: false
	 */
	origRegionId:string = undefined;
	/**
	 * Orgin sic code
	 * Required: false
	 * Array: false
	 */
	origSicCd:string = undefined;
	/**
	 * State where the origin is located
	 * Required: false
	 * Array: false
	 */
	origStateCd:string = undefined;
	/**
	 * First 5 characters of the zip code for a US address or first 6 characters of the Canadian zip code of the address where the shipper is located
	 * Required: false
	 * Array: false
	 */
	origZip:string = undefined;
	/**
	 * 1st char of origin zip code
	 * Required: false
	 * Array: false
	 */
	origZip1:string = undefined;
	/**
	 * Number of distinct first charater of the origin zip code
	 * Required: false
	 * Array: false
	 */
	origZip1Cnt:number = undefined;
	/**
	 * 1st 2 chars of origin zip code
	 * Required: false
	 * Array: false
	 */
	origZip2:string = undefined;
	/**
	 * Number of distinct first 2 charaters of the origin zip code
	 * Required: false
	 * Array: false
	 */
	origZip2Cnt:number = undefined;
	/**
	 * 1st 3 chars of origin zip code
	 * Required: false
	 * Array: false
	 */
	origZip3:string = undefined;
	/**
	 * Number of distinct first 3 charaters of the origin zip code
	 * Required: false
	 * Array: false
	 */
	origZip3Cnt:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origZipNtdCnwOtbRevAmt:MonetaryAmount = undefined;
	/**
	 * XPO outbound shipment count from NTD (National Traffic Db) for this origin zip
	 * Required: false
	 * Array: false
	 */
	origZipNtdCnwOtbShpCnt:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origZipNtdCnwOtbWeight:Weight = undefined;
	/**
	 * Other com[any (non-XPO) inbound revenue from NTD (National Traffic Db)
	 * Required: false
	 * Array: false
	 */
	origZipNtdInbRevAmt:MonetaryAmount = undefined;
	/**
	 * XPO inbound shipment count from NTD (National Traffic Db)
	 * Required: false
	 * Array: false
	 */
	origZipNtdInbShpmtCnt:number = undefined;
	/**
	 * XPO inbound shipment weight from NTD (National Traffic Db)
	 * Required: false
	 * Array: false
	 */
	origZipNtdInbWeight:Weight = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origZipNtdLtlCnwInbRevAmt:MonetaryAmount = undefined;
	/**
	 * Other com[any (non-XPO) inbound shipment count from NTD (National Traffic Db)
	 * Required: false
	 * Array: false
	 */
	origZipNtdLtlInbShpCnt:number = undefined;
	/**
	 * Other company (non-XPO) outbound weight from NTD
	 * Required: false
	 * Array: false
	 */
	origZipNtdLtlInbWeight:Weight = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origZipNtdLtlOtbRevAmt:MonetaryAmount = undefined;
	/**
	 * XPO outbound shipment count from NTD (National Traffic Db)
	 * Required: false
	 * Array: false
	 */
	origZipNtdOtbShpmtCnt:number = undefined;
	/**
	 * XPO outbound shipment weight from NTD (National Traffic Db)
	 * Required: false
	 * Array: false
	 */
	origZipNtdOtbWeight:Weight = undefined;
	/**
	 * Unique identifier for Pricing Leaf shipment record
	 * Required: false
	 * Array: false
	 */
	prcLeafShipmentId:string = undefined;
	/**
	 * Unique identifier for Proposal that is being analyzed.
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Indicates if customer is public
	 * Required: false
	 * Array: false
	 */
	publicCompanyInd:boolean = undefined;
	/**
	 * Sales Volume Range of the customer
	 * Required: false
	 * Array: false
	 */
	salesVolumeRangeNm:string = undefined;
	/**
	 * Representative customer on this SEGMENT output of the segmentation model
	 * Required: false
	 * Array: false
	 */
	sampleCust:string = undefined;
	/**
	 * This is the SEGMENT a particular shipment would be assigned to. This is also an input to the LEAF MODELS because it is the unique id that tells you which Leaf Model to run
	 * Required: false
	 * Array: false
	 */
	segmentId:string = undefined;
	/**
	 * Identifier of the shipment (i.e PRO, unique proxy shipment key)
	 * Required: false
	 * Array: false
	 */
	shipmentId:string = undefined;
	/**
	 * Indicates if customer size is small
	 * Required: false
	 * Array: false
	 */
	smallBusinessInd:boolean = undefined;
	/**
	 * Status code
	 * Required: false
	 * Array: false
	 */
	statusCd:string = undefined;
	/**
	 * Tariff Revenue Amount of the shipment
	 * Required: false
	 * Array: false
	 */
	tariffRevAmt:MonetaryAmount = undefined;
	/**
	 * Total volume  (weight/density)
	 * Required: false
	 * Array: false
	 */
	totalVolume:Volume = undefined;
	/**
	 * Actual shipment weight
	 * Required: false
	 * Array: false
	 */
	weight:Weight = undefined;
	/**
	 * XPO inbound revenue from NTD (National Traffic Db) for this origin zip
	 * Required: false
	 * Array: false
	 */
	origZipNtdCnwInbRevAmt:MonetaryAmount = undefined;
}
/**
 * A definition of a particular workflow
 */
export class PricingModelSegment {
	/**
	 * unique identifier of Pricing Segment Model record
	 * Required: false
	 * Array: false
	 */
	prcSegmentModelId:string = undefined;
	/**
	 * This is the SEGMENT a particular shipment would be assigned to. This is also an input to the LEAF MODELS because it is the unique id that tells you which Leaf Model to run
	 * Required: false
	 * Array: false
	 */
	segmentId:string = undefined;
	/**
	 * Corporate Discount Impact
	 * Required: false
	 * Array: false
	 */
	cdiPct:number = undefined;
	/**
	 * Lower weight bound for a shipment to be assigned to this SEGMENT
	 * Required: false
	 * Array: false
	 */
	minWeight:Weight = undefined;
	/**
	 * Upper weight bound for a shipment to be assigned to this SEGMENT
	 * Required: false
	 * Array: false
	 */
	maxWeight:Weight = undefined;
	/**
	 * Lower class bound for a shipment to be assigned to this SEGMENT
	 * Required: false
	 * Array: false
	 */
	minNmfcClass:string = undefined;
	/**
	 * Upper class bound for a shipment to be assigned to this SEGMENT
	 * Required: false
	 * Array: false
	 */
	maxNmfcClass:string = undefined;
	/**
	 * Lower sic miles bound for a shipment to be assigned to this SEGMENT
	 * Required: false
	 * Array: false
	 */
	minSicDistance:Distance = undefined;
	/**
	 * Upper sic miles bound for a shipment to be assigned to this SEGMENT
	 * Required: false
	 * Array: false
	 */
	maxSicDistance:Distance = undefined;
	/**
	 * Lower annual customer revenue bound for all business to be assigned to this SEGMENT
	 * Required: false
	 * Array: false
	 */
	minCustRevAmt:MonetaryAmount = undefined;
	/**
	 * Lower annual customer revenue bound for all business to be assigned to this SEGMENT
	 * Required: false
	 * Array: false
	 */
	maxCustRevAmt:MonetaryAmount = undefined;
	/**
	 * Shipment variables used to run the segmentation model
	 * Required: false
	 * Array: false
	 */
	modelQualityScore:number = undefined;
	/**
	 * Quality category code of the segmentation model
	 * Required: false
	 * Array: false
	 */
	modelQualityCategory:string = undefined;
	/**
	 * Shipment variables used to run the segmentation model
	 * Required: false
	 * Array: false
	 */
	modelInputs:string = undefined;
}
/**
 * Pricing Proposal details
 */
export class PricingProposal {
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpHdr:SalesRfpHdr = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposalHeader:PricingProposalHdr = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	expectations:ProposalExpecatations = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	custHierarchy:PricingProposalCustomerHierarchy[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	geoDiscountAmcSummary:GeoDiscountAmcSummary[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	accessorial:PricingAccessorial[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	geoAreaPricingLane:GeoAreaPricingLane[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	rulesetMatrix:PricingProposalRulesetMatrix[] = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	tmsInfo:PricingProposalTms[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agrmntPerformance:RfpAgrmntPerformance = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rfpAuditHdr:SalesRfpAuditHeader = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	liabilityDetails:PricingAgreementDocLiabHdr = undefined;
}
/**
 * Accessorial information for Pricing Proposal
 */
export class PricingProposalAccessorial {
	/**
	 * unique identifier of Pricing Proposal Accessorial
	 * Required: false
	 * Array: false
	 */
	pricingProposalAccsrlId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	accessorialInfo:PricingAccessorial = undefined;
}
export class PricingProposalCustomerHierarchy {
	/**
	 * unique identifier of the Pricing Proposal Customer Hierarchy record
	 * Required: false
	 * Array: false
	 */
	prcProposalCustHierarchyId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	acctBasicInfo:AcctId = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	acctAddress:Address = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Value of ï¿½Yï¿½ if customer location is to be included in the analysis/proposal
	 * Required: false
	 * Array: false
	 */
	includeLocationInd:boolean = undefined;
}
/**
 * Pricing Analysis header information and summary of Pricing Analysis results
 */
export class PricingProposalHdr {
	/**
	 * Required: false
	 * Array: false
	 */
	aggrModelTemplateId:string = undefined;
	/**
	 * Identifier of the Current Pricing Agreement . The first qualifier in the customer agreement ID (e.g. 636714494-02-003)
	 * Required: false
	 * Array: false
	 */
	agreementAcctInstId:string = undefined;
	/**
	 * Current Pricing Agreement version number. The middle qualifier in the customer agreement ID (e.g. 636714494-02-003)
	 * Required: false
	 * Array: false
	 */
	agreementAcctVersion:string = undefined;
	/**
	 * Indicates when the new pricing agreement is candidate for monitor and adjust as set by the Pricing Analyst
	 * Required: false
	 * Array: false
	 */
	agreementMonitorRecordInd:string = undefined;
	/**
	 * Current Pricing Agreement suffix number. The last qualifier in the customer agreement ID e.g. (636714494-02-003)
	 * Required: false
	 * Array: false
	 */
	agreementSeqNbr:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	analysisCompleteDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	analysisDueDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	analysisStartDate:Date = undefined;
	/**
	 * Identifies the type of pricing analysis (i.e. IYM , Lane-Based )
	 * Required: false
	 * Array: false
	 */
	analysisTypeCd:string = undefined;
	/**
	 * identifies the type of pricing analysis work (i.e. New Opportunity, Renegotiate,Update Locations, Cancel Pricing, Additional Business, Monitor and Adjust, Revenue Neutral, In-Cycle Renegotiation, Out-of-Cycle Renegotiaton, Adhoc)
	 * Required: false
	 * Array: false
	 */
	analysisWorkTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Current CTS599 Discount Impact percentage. Averaged based on period type (monthly, 6-month, annual)
	 * Required: false
	 * Array: false
	 */
	currentDiscountImpactPct:number = undefined;
	/**
	 * Current Operating Ratio percentage. Averaged based on period type (monthly, 6-month, annual)
	 * Required: false
	 * Array: false
	 */
	currentOrPct:number = undefined;
	/**
	 * current potential shipment revenue. Averaged based on period type (monthly, 6-month, annual)
	 * Required: false
	 * Array: false
	 */
	currentPotentialRevAmt:MonetaryAmount = undefined;
	/**
	 * current shipment revenue. Averaged based on period type (monthly, 6-month, annual)
	 * Required: false
	 * Array: false
	 */
	currentRevAmt:MonetaryAmount = undefined;
	/**
	 * Overall CTS599 Discount Impact for shipments with FAK and without FSC
	 * Required: false
	 * Array: false
	 */
	fakDiscImpactNoFsc:number = undefined;
	/**
	 * CTS599 Discount Impact for shipments with FAK and FSC
	 * Required: false
	 * Array: false
	 */
	fakDiscImpactWithFsc:number = undefined;
	/**
	 * Captures how many months worth of shipment data is used for analysis
	 * Required: false
	 * Array: false
	 */
	nbrOfPeriods:number = undefined;
	/**
	 * Identifier of the Customer with whom the agreement is being or was negotiated. This will uniquely identify the customer in the CIS component.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustInstId:string = undefined;
	/**
	 * This is the business key at the time of negotiation for this customer. Note that it will not be kept in synch with the master customer database.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustMadCd:string = undefined;
	/**
	 * Name and Address of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	negotiatingCustNmAddr:Address = undefined;
	/**
	 * Specifies whether customer account is Pickup/Delivery, BillTo, Corporate Hierarchy
	 * Required: false
	 * Array: false
	 */
	negotiatingCustTypeCd:string = undefined;
	/**
	 * Overall CTS599 Discount Impact for shipments with no FAK and without FSC
	 * Required: false
	 * Array: false
	 */
	nonFakDiscImpactNoFsc:number = undefined;
	/**
	 * CTS599 Discount Impact for shipments with no FAK and FSC
	 * Required: false
	 * Array: false
	 */
	nonFakDiscImpactWithFsc:number = undefined;
	/**
	 * Identifies if the new pricing agreement has OBI Program
	 * Required: false
	 * Array: false
	 */
	obiInd:boolean = undefined;
	/**
	 * Offered discount percentage based on pricing analysis
	 * Required: false
	 * Array: false
	 */
	offeredDiscountPct:number = undefined;
	/**
	 * Overall CTS599 Discount Impact for shipments without FSC
	 * Required: false
	 * Array: false
	 */
	overallDiscImpactNoFsc:number = undefined;
	/**
	 * Overall CTS599 Discount Impact for shipments with FSC
	 * Required: false
	 * Array: false
	 */
	overallDiscImpactWithFsc:number = undefined;
	/**
	 * Overall percent of business of shipments with FAK
	 * Required: false
	 * Array: false
	 */
	overallFakPctOfBusiness:number = undefined;
	/**
	 * Overall percent of business of shipments with no  FAK
	 * Required: false
	 * Array: false
	 */
	overallNoFakPctOfBusiness:number = undefined;
	/**
	 * Describes how the amounts are calculated/averaged (i.e. monthly, 6-month, yearly)
	 * Required: false
	 * Array: false
	 */
	periodTypeCd:string = undefined;
	/**
	 * Unique id for the proposal
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnalystId:string = undefined;
	/**
	 * Identifies the type of pricing contract (i.e. Regional, Strategic, Regional 3PL Broker, Strategic 3PL Customer-Specific)
	 * Required: false
	 * Array: false
	 */
	pricingTypeCd:string = undefined;
	/**
	 * Profit improvement reasons
	 * Required: false
	 * Array: false
	 */
	profitImprvmntDiscrReason:string = undefined;
	/**
	 * Profit improvement comments
	 * Required: false
	 * Array: false
	 */
	profitImprvmntDiscrComment:string = undefined;
	/**
	 * Profit Improvement Target Percentage
	 * Required: false
	 * Array: false
	 */
	profitImprovementTargetPct:number = undefined;
	/**
	 * Projected OR Change based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedChangeOrPct:number = undefined;
	/**
	 * Projected Cost based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedCostAmt:MonetaryAmount = undefined;
	/**
	 * Projected CTS599 Discount Impact based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedDiscountImpactPct:number = undefined;
	/**
	 * Projected freight revenue increase percentage. Averaged based on period type (monthly, 6-month, annual)
	 * Required: false
	 * Array: false
	 */
	projectedFrghtRevIncrPct:number = undefined;
	/**
	 * Projected operating income improvement percentage. Averaged based on period type (monthly, 6-month, annual)
	 * Required: false
	 * Array: false
	 */
	projectedOpIncImprvmntPct:number = undefined;
	/**
	 * Projected Operating Ratio based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedOrPct:number = undefined;
	/**
	 * Projected Price Change based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedPriceChgPct:number = undefined;
	/**
	 * Projected Profit Amount on based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedProfitAmt:MonetaryAmount = undefined;
	/**
	 * Projected Profit Improvement on based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedProfitImprvmntAmt:MonetaryAmount = undefined;
	/**
	 * Projected Profit Improvement percentage on based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedProfitImprvmntPct:number = undefined;
	/**
	 * Projected profit increase percentage. Averaged based on period type (monthly, 6-month, annual)
	 * Required: false
	 * Array: false
	 */
	projectedProfitIncrPct:number = undefined;
	/**
	 * Projected Revenue based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedRevAmt:MonetaryAmount = undefined;
	/**
	 * Projected Revenue Change based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedRevChgPct:number = undefined;
	/**
	 * Projected total revenue increase percentage. Averaged based on period type (monthly, 6-month, annual)
	 * Required: false
	 * Array: false
	 */
	projectedTotRevIncrPct:number = undefined;
	/**
	 * Projected Yield without FSC based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedYieldNoFsc:number = undefined;
	/**
	 * Projected Yield with FSC based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedYieldWithFsc:number = undefined;
	/**
	 * Average NMFC Freight class assumption for  this proposal
	 * Required: false
	 * Array: false
	 */
	proposalAvgClass:string = undefined;
	/**
	 * The average density assumption for  the proposal
	 * Required: false
	 * Array: false
	 */
	proposalAvgDensity:Density = undefined;
	/**
	 * The average weight assumption for  the proposal
	 * Required: false
	 * Array: false
	 */
	proposalAvgWeight:Weight = undefined;
	/**
	 * Current status of the pricing proposal (from 'not started' thru 'accepted'. Sample values (not complete): Not Started, Analysis in Progress, Under Review, Submitted to Sales, On-Hold, Cancelled, Accepted, Rejected, Countered
	 * Required: false
	 * Array: false
	 */
	proposalStatCd:string = undefined;
	/**
	 * Proposed discount percentage upper bound. Mainly for IYM simple analysis
	 * Required: false
	 * Array: false
	 */
	proposedDiscountHighPct:number = undefined;
	/**
	 * Proposed discount percentage lower bound. Mainly for IYM simple analysis
	 * Required: false
	 * Array: false
	 */
	proposedDiscountLowPct:number = undefined;
	/**
	 * Proposed discount percentage min. Mainly for lane-based analysis
	 * Required: false
	 * Array: false
	 */
	proposedDiscountMaxPct:number = undefined;
	/**
	 * Proposed discount percentage min. Mainly for lane-based analysis
	 * Required: false
	 * Array: false
	 */
	proposedDiscountMidPct:number = undefined;
	/**
	 * Proposed discount percentage min. Mainly for lane-based analysis
	 * Required: false
	 * Array: false
	 */
	proposedDiscountMinPct:number = undefined;
	/**
	 * Proposed discount percentageoptimal. Mainly for IYM simple analysis
	 * Required: false
	 * Array: false
	 */
	proposedDiscountOptimalPct:number = undefined;
	/**
	 * Unique identifier for Request for Pricing record
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Shipment Revenue Percentage
	 * Required: false
	 * Array: false
	 */
	shipmentRevPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	thirdPartyLogisticsInd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	thirdPartyLogisticsTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	targetOrPct:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	manualCalcInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	analystMgrId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	analystMgrName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnalystName:string = undefined;
	/**
	 * valid values are Y or N
	 * Required: false
	 * Array: false
	 */
	adminUserInd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	dataSourceCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	startMoRevAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	startMoOrPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	recmdSingleDiscPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposedAmcShpmtPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	requestedDiscImpactPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	startMoCostAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	startMoProfitAmt:MonetaryAmount = undefined;
	/**
	 * Identifier for Pricing Analyst to send renegotiation letter at 40 days prior to Reneg Date
	 * Required: false
	 * Array: false
	 */
	sendRenegLetterTypeCode:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	forcedLetterDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fastPassNoAnalysisInd:string = undefined;
	/**
	 * Indicates that there is no further analysis for the pricing analyst to perform.
	 * Required: false
	 * Array: false
	 */
	approvedAsRequested:string = undefined;
	/**
	 * The date on which offer sent first time
	 * Required: false
	 * Array: false
	 */
	firstOfferSentDate:Date = undefined;
	/**
	 * Approved As requested except AC
	 * Required: false
	 * Array: false
	 */
	approvedAsRequestedExceptAc:string = undefined;
	/**
	 * Contract Info Requeseted
	 * Required: false
	 * Array: false
	 */
	contractInfoRequested:string = undefined;
	/**
	 * Contract Terms For Letter
	 * Required: false
	 * Array: false
	 */
	contractTermsForLetter:number = undefined;
	/**
	 * Letter Implementation Date
	 * Required: false
	 * Array: false
	 */
	letterImplementationDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	creatorMgrName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	incrementalGrowth:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	incrementalGrowthInd:string = undefined;
}
/**
 * Contains information about ruleset creation from Pricing Proposal data
 */
export class PricingProposalRulesetMatrix {
	/**
	 * Unique identifier for Pricing Proposal record
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Unique identifier for Pricing Proposal Rule-Set Matrix record
	 * Required: false
	 * Array: false
	 */
	prcProposalRulesetMatrixId:string = undefined;
	/**
	 * Origin State Code
	 * Required: false
	 * Array: false
	 */
	origStateCd:string = undefined;
	/**
	 * Origin Country Code
	 * Required: false
	 * Array: false
	 */
	origCountryCd:string = undefined;
	/**
	 * Defines the low end of the Origin zip range for the lane
	 * Required: false
	 * Array: false
	 */
	origLowPostalCd:string = undefined;
	/**
	 * Defines the high end of the Origin zip range for the lane
	 * Required: false
	 * Array: false
	 */
	origHighPostalCd:string = undefined;
	/**
	 * Destination Country Code
	 * Required: false
	 * Array: false
	 */
	destCountryCd:string = undefined;
	/**
	 * Destination State Code
	 * Required: false
	 * Array: false
	 */
	destStateCd:string = undefined;
	/**
	 * Defines the low end of the Destination zip range for the lane
	 * Required: false
	 * Array: false
	 */
	destLowPostalCd:string = undefined;
	/**
	 * Defines the high end of the Destination zip range for the lane
	 * Required: false
	 * Array: false
	 */
	destHighPostalCd:string = undefined;
	/**
	 * Applicable Discount Percentage
	 * Required: false
	 * Array: false
	 */
	discountPct:number = undefined;
	/**
	 * Applicable discount in %, when minimum charges are applied
	 * Required: false
	 * Array: false
	 */
	minChargeDiscountPct:number = undefined;
	/**
	 * Absolute Minimum Charge Amount
	 * Required: false
	 * Array: false
	 */
	amcAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingIndicators:PricingScope = undefined;
	/**
	 * Type of uom for the begin and end range value (i.e. WEIGHT)
	 * Required: false
	 * Array: false
	 */
	tierType:string = undefined;
	/**
	 * The lower limit of the range of weight (depends on Tier Type) that this discount record is applicable
	 * Required: false
	 * Array: false
	 */
	beginRangeValue:number = undefined;
	/**
	 * The upper limit of the range of weight (depends on Tier Type) that this discount record is applicable
	 * Required: false
	 * Array: false
	 */
	endRangeValue:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Contains information about the data created for Customer TMS from Pricing Proposal data
 */
export class PricingProposalTms {
	/**
	 * unique identifier of Pricing Proposal TMS file record
	 * Required: false
	 * Array: false
	 */
	prcProposalTmsFileId:string = undefined;
	/**
	 * Origin State Code
	 * Required: false
	 * Array: false
	 */
	origStateCd:string = undefined;
	/**
	 * Destination State Code
	 * Required: false
	 * Array: false
	 */
	destStateCd:string = undefined;
	/**
	 * Discount in decimal format
	 * Required: false
	 * Array: false
	 */
	discountPct:number = undefined;
	/**
	 * Minimum Charge Amount
	 * Required: false
	 * Array: false
	 */
	minChargeAmt:MonetaryAmount = undefined;
	/**
	 * SCAC code
	 * Required: false
	 * Array: false
	 */
	scacCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
export class PricingProposalWorkQueue {
	/**
	 * Required: false
	 * Array: false
	 */
	pricingProposalHdr:PricingProposalHdr = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	crmAccountOwner:CrmAccountOwner = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfp:SalesRfp = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposalOverallNote:SalesRfpComment = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposalComment:SalesRfpComment = undefined;
}
/**
 * The definition by indicator of the scope that a particular pricing element will apply to.
 */
export class PricingScope {
	/**
	 * Indicator if the rates and discounts apply to intrastate shipments
	 * Required: false
	 * Array: false
	 */
	intrastateInd:boolean = undefined;
	/**
	 * Indicator if the rates and discounts apply to interstate shipments
	 * Required: false
	 * Array: false
	 */
	interstateInd:boolean = undefined;
	/**
	 * Indicator if the rates and discounts apply to international shipments
	 * Required: false
	 * Array: false
	 */
	internationalInd:boolean = undefined;
	/**
	 * Indicator if the rates and discounts apply to outbound prepaid shipments
	 * Required: false
	 * Array: false
	 */
	outboundPrepaidInd:boolean = undefined;
	/**
	 * Indicator if the rates and discounts apply to inbound collect shipments
	 * Required: false
	 * Array: false
	 */
	inboundCollectInd:boolean = undefined;
	/**
	 * Indicator if the rates and discounts apply to outbound collect shipments
	 * Required: false
	 * Array: false
	 */
	outboundCollectInd:boolean = undefined;
	/**
	 * Indicator if the rates and discounts apply to inbound prepaid shipments
	 * Required: false
	 * Array: false
	 */
	inboundPrepaidInd:boolean = undefined;
	/**
	 * Indicator if the rates and discounts apply to third party shipments
	 * Required: false
	 * Array: false
	 */
	thirdPartyInd:boolean = undefined;
}
/**
 * Information about State-State AMCs and Discount
 */
export class ProposalAmcDiscount {
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcProposalAmcDiscountId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origGroupTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origGroupName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origGroupId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origStateCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	origCountryCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destGroupTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destGroupName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destGroupId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destStateCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	destCountryCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	currentDiscountPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	newDiscountPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	currentAmcAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	newAmcAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
/**
 * Discount Information for a Pricing Proposal
 */
export class ProposalDiscount {
	/**
	 * unique db identifier of the geo pricing lane discount record
	 * Required: false
	 * Array: false
	 */
	geoPricingLaneDiscountId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingDiscount:PricingDiscount = undefined;
}
/**
 *  Proposal expectation details
 */
export class ProposalExpecatations {
	/**
	 * Required: false
	 * Array: false
	 */
	currentMonthlyCostAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	currentMonthlyProfitAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	monthlyRevenueReductionAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	monthlyCostReductionAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	monthlyOperatingIncomeImprovementAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	monthlyOperatingIncomeAmt:MonetaryAmount = undefined;
	/**
	 * Required: true
	 * Array: true
	 */
	strategy:StrategyDetails[] = undefined;
}
/**
 * Allowable weight break for Rating tariff Code
 */
export class RatingTariffWgtBreak {
	/**
	 * This is the tariff code or the RTAR for the default tariff that uses a specific set of rates described by the description text. For example the code CTS599 identifies the current Current CTS Corporate Tariff
	 * Required: false
	 * Array: false
	 */
	tariffCd:string = undefined;
	/**
	 * This describes the rates used by this tariff - for example Current CTS Corporrate Tariff or Yellow 1999 Rates etc.
	 * Required: false
	 * Array: false
	 */
	tariffDesc:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	baseTariff:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agrmtPrintDesc:string = undefined;
	/**
	 * commodity class code
	 * Required: false
	 * Array: false
	 */
	classCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rateSection:number = undefined;
	/**
	 * Indicates that the tariff is most commonly used
	 * Required: false
	 * Array: false
	 */
	commonlyUsedInd:boolean = undefined;
	/**
	 * number of weight breaks
	 * Required: false
	 * Array: false
	 */
	nbrOfEntries:number = undefined;
	/**
	 * 1st weight break
	 * Required: false
	 * Array: false
	 */
	wgtText1:string = undefined;
	/**
	 * 1st weight break low value
	 * Required: false
	 * Array: false
	 */
	wgtLow1:number = undefined;
	/**
	 * 2nd weight break
	 * Required: false
	 * Array: false
	 */
	wgtText2:string = undefined;
	/**
	 * 2nd weight break low value
	 * Required: false
	 * Array: false
	 */
	wgtLow2:number = undefined;
	/**
	 * 3rd weight break
	 * Required: false
	 * Array: false
	 */
	wgtText3:string = undefined;
	/**
	 * 3rd weight break low value
	 * Required: false
	 * Array: false
	 */
	wgtLow3:number = undefined;
	/**
	 * 4th weight break
	 * Required: false
	 * Array: false
	 */
	wgtText4:string = undefined;
	/**
	 * 4th weight break low value
	 * Required: false
	 * Array: false
	 */
	wgtLow4:number = undefined;
	/**
	 * 5th weight break
	 * Required: false
	 * Array: false
	 */
	wgtText5:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow5:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText6:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow6:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText7:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow7:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText8:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow8:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText9:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow9:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText10:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow10:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText11:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow11:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText12:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow12:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText13:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow13:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText14:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow14:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtText15:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	wgtLow15:number = undefined;
}
/**
 * Unmapped document record type
 */
export class Record {
	/**
	 * Required: false
	 * Array: true
	 */
	data:string[] = undefined;
}
/**
 * Information about Sales Rfp and Its owner
 */
export class RfpOwner {
	/**
	 * The database identifier of this SalesRFP
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Employee ID of current owner of sales rfp
	 * Required: false
	 * Array: false
	 */
	fromOwner:string = undefined;
}
/**
 * Basic customer account info
 */
export class RfpAcct {
	/**
	 * Required: false
	 * Array: false
	 */
	acctId:AcctId = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	address:Address = undefined;
	/**
	 * Effective date of the record
	 * Required: false
	 * Array: false
	 */
	effDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	prcSourceAcctId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingSourceAcctMadCd:string = undefined;
}
/**
 * Information about Accounts and list of rulesets
 */
export class RfpAcctRuleset {
	/**
	 * Required: false
	 * Array: false
	 */
	account:RfpAcct = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	rulesets:Ruleset[] = undefined;
}
/**
 * Various factors used by or determined by the Pricing Analysis
 */
export class RfpAgrmntPerformance {
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	avgCostModelDensity:Density = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	avgFreightClass:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	avgRatedAsFreightClass:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cnwy190FscRetenPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cts599DiscImpactPctNetFsc:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	cts599DiscImpactPctNoObi:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	custMadCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	custName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	distanceMilesPerShipment:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	effFscPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	netRevAmount:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	netRevAmtNoObi:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	netRevAmtNoObiPerShpmnt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	onTimeShipmentPct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	orPctNoObi:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	paidClaimAmount:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	paidClaimRatio:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	profitAmtNoObi:MonetaryAmount = undefined;
	/**
	 * unique identifier of the Rfp Agreement Performance record
	 * Required: false
	 * Array: false
	 */
	rfpAgrmntPerformanceId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rfpEndDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rfpStartDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shipmentCount:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	weightLbsPerShipment:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	yieldAmtNetFsc:MonetaryAmount = undefined;
	/**
	 * 
	 * Required: false
	 * Array: false
	 */
	yieldAmtNoObi:MonetaryAmount = undefined;
}
/**
 * The variable parameters that are included in the standard Pricing Agreement
 */
export class RfpAuditLog {
	/**
	 * Required: false
	 * Array: false
	 */
	eventTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventSubTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventStatus:string = undefined;
	/**
	 * Any other comments to be included in the contract
	 * Required: false
	 * Array: false
	 */
	eventOccurredTimestamp:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventTranCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventCreatedBy:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	eventData:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	statusReasonCd:string = undefined;
}
/**
 * Commodity class overrides for the RFP
 */
export class RfpClassOverride {
	/**
	 * Commodity class and an overriding FAK
	 * Required: false
	 * Array: false
	 */
	commodityClass:ClassOverride = undefined;
	/**
	 * The percentage of business for this FAK
	 * Required: false
	 * Array: false
	 */
	FakPctOfBusiness:string = undefined;
}
/**
 * Off Bill Incentive program participation info
 */
export class RfpObi {
	/**
	 * unique id of the sales rfp obi plan
	 * Required: false
	 * Array: false
	 */
	rfpObiPlanId:string = undefined;
	/**
	 * Identifies the type of Off Bill discount program. End of Month, Volume Incentive Program, Load Allowance, Unload Allowance, Pallet
	 * Required: false
	 * Array: false
	 */
	obiPgm:string = undefined;
	/**
	 * Indicates whether to calculate incentive amounts based on gross or net revenue
	 * Required: false
	 * Array: false
	 */
	payOnGrossNetInd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	grossNetAmt:MonetaryAmount = undefined;
	/**
	 * Indicates whether to qualify a shipment based on gross or net revenue
	 * Required: false
	 * Array: false
	 */
	qualifyOnGrossNetInd:string = undefined;
	/**
	 * A rate offered based on a unit of measure such as CWT
	 * Required: false
	 * Array: false
	 */
	incentiveRateAmt:MonetaryAmount = undefined;
	/**
	 * The unit of measure that the incentive rate is based on
	 * Required: false
	 * Array: false
	 */
	incentiveRateUom:string = undefined;
	/**
	 * Identifies if a single or multiple discount structure is associated with the OBI program
	 * Required: false
	 * Array: false
	 */
	discountTypeCd:string = undefined;
	/**
	 * Identifies the type of units for the range of discount rates
	 * Required: false
	 * Array: false
	 */
	discountUom:string = undefined;
	/**
	 * Discount percent when the discount type is Single
	 * Required: false
	 * Array: false
	 */
	singleDiscountPct:number = undefined;
	/**
	 * If the discountTypeCd is single, the amt will be here for some programs
	 * Required: false
	 * Array: false
	 */
	singleDiscountAmt:MonetaryAmount = undefined;
	/**
	 * The discount percentage per tier if the type is multiple
	 * Required: false
	 * Array: true
	 */
	discountTiers:RfpObiDiscTier[] = undefined;
	/**
	 * The origin location that a shipment must be pickup from to qualify for a pallet allowance
	 * Required: false
	 * Array: false
	 */
	origLoc:string = undefined;
}
/**
 * Off Bill Incentive program participation info
 */
export class RfpObiDiscTier {
	/**
	 * The minimum units, in a range, for which this discount rate applies
	 * Required: false
	 * Array: false
	 */
	discountTierMin:number = undefined;
	/**
	 * The maximum units, in a range, for which this discount rate applies
	 * Required: false
	 * Array: false
	 */
	discountTierMax:number = undefined;
	/**
	 * The program discount percentage to apply on qualified shipments
	 * Required: false
	 * Array: false
	 */
	discountPct:number = undefined;
	/**
	 * The program discount to apply on qualified shipments
	 * Required: false
	 * Array: false
	 */
	discount:MonetaryAmount = undefined;
}
export class Ruleset {
	/**
	 * billTo/3rd party or PnD
	 * Required: false
	 * Array: false
	 */
	rulesetTypeCd:string = undefined;
	/**
	 * indicates whether the ruleset  can be used as third party ruleset
	 * Required: false
	 * Array: false
	 */
	thirdPartyInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	agreementId:string = undefined;
	/**
	 * Ruleset Sequence Number
	 * Required: false
	 * Array: false
	 */
	rulesetNbr:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ratingTariffName:string = undefined;
	/**
	 * An Accessorial charge may be overridden to be included in all shipments rated using the rule set or its rate or percentage may be overridden or it may be waived. Sometimes an accessorial may not be applicable to the rule set because this accessorial is not present in the customers rules tariff or was not negotiated for the modified 199 rules tariff.
	 * If the rules tariff is Customer specific and the AC service is included, it must exist on this table. If the Rules tariff is CNWY199, it will only exist on this table if AC service is overriden, waived or always applied."
	 * Required: false
	 * Array: true
	 */
	accessorialOverrides:AccessorialOverride[] = undefined;
	/**
	 * Desc: Stores the pricing class structures for rule sets. The class structure indicates if any class needs to be overridden and treated as another class for pricing purposes.
	 * Required: false
	 * Array: true
	 */
	classOverrides:ClassOverride[] = undefined;
	/**
	 * Desc: NMFC numbers for which the class is overridden to a different class value.
	 * Required: false
	 * Array: true
	 */
	nmfcOverrides:NmfcItemOverride[] = undefined;
}
/**
 * Shipment data to retrieve Rulesets 
 */
export class RulesetShipment {
	/**
	 * Second part of the unique identifier to a shipment. This is the date that the shipment was picked up.
	 * Required: false
	 * Array: false
	 */
	pkupDate:Date = undefined;
	/**
	 * Conditions agreed upon between parties to a transportation transaction regarding the type and payment of freight.
	 * Required: false
	 * Array: false
	 */
	chargeToCd:string = undefined;
	/**
	 * Total Piece count on the shipment
	 * Required: false
	 * Array: false
	 */
	totPiecesCnt:number = undefined;
	/**
	 * Total weight of the shipment
	 * Required: false
	 * Array: false
	 */
	totWeight:Weight = undefined;
	/**
	 * Shipment Origin details
	 * Required: false
	 * Array: false
	 */
	origin:Address = undefined;
	/**
	 * Shipment destination details
	 * Required: false
	 * Array: false
	 */
	destination:Address = undefined;
	/**
	 * Shipper Customer Account Instance Id
	 * Required: false
	 * Array: false
	 */
	shipperInstId:string = undefined;
	/**
	 * Consignee Customer Account Instance Id
	 * Required: false
	 * Array: false
	 */
	consigneeInstId:string = undefined;
	/**
	 * Bill 2 party Account Instance Id
	 * Required: false
	 * Array: false
	 */
	bill2InstId:string = undefined;
	/**
	 * Total number of pallets for the shipment
	 * Required: false
	 * Array: false
	 */
	palletCnt:number = undefined;
	/**
	 * Carrier information that we used for Advance Service
	 * Required: false
	 * Array: false
	 */
	advCarrierScac:string = undefined;
	/**
	 * Carrier information that we used for Beyond Service
	 * Required: false
	 * Array: false
	 */
	bydCarrierScac:string = undefined;
	/**
	 * Commodity Package code
	 * Required: false
	 * Array: false
	 */
	cmdtyPackageCd:string = undefined;
}
/**
 * Sales RFP is a solicitation made by a Sales agent to the pricing department for pricing that can be offered to a customer.
 */
export class SalesRfp {
	/**
	 * shipping related factors to be considered in the analysis
	 * Required: false
	 * Array: false
	 */
	agrmntPerformance:RfpAgrmntPerformance = undefined;
	/**
	 * The variable parameters that are included in the standard Pricing Agreement
	 * Required: false
	 * Array: false
	 */
	contractBoilerPlateParms:ContractBoilerPlate = undefined;
	/**
	 * File of representative shipments composed of lanes, commodities, weights, density for a customer, maybe based on actual historical shipments or derived shipments.
	 * Required: false
	 * Array: true
	 */
	custShipmentFile:CustomerShipmentsFile[] = undefined;
	/**
	 * geographic area that shares common pricing elements, including tariffs, discounts, AMCs, FAK
	 * Required: false
	 * Array: true
	 */
	geoPricingGroups:GeoAreaPricingLane[] = undefined;
	/**
	 * accessorials being considered in this RFP
	 * Required: false
	 * Array: true
	 */
	pricingAccessorials:PricingAccessorial[] = undefined;
	/**
	 * commodities for this customer with their percentage of business
	 * Required: false
	 * Array: true
	 */
	pricingCommodities:PricingCommodity[] = undefined;
	/**
	 * lanes grouped by shared commodity characteristics. There may be several such groupings
	 * Required: false
	 * Array: true
	 */
	pricingCommodityLanes:PricingLane[] = undefined;
	/**
	 * Factors that are influencing the proposal. Other factors might be added not just competitor information, like proximity to service center
	 * Required: false
	 * Array: false
	 */
	pricingJustification:PricingJustification = undefined;
	/**
	 * various lading unit dimensions
	 * Required: false
	 * Array: true
	 */
	pricingLadingUnits:PricingLadingDimension[] = undefined;
	/**
	 * Customer account locations in the scope of this rfp with basic info
	 * Required: false
	 * Array: true
	 */
	rfpAccts:RfpAcct[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rfpAuditHdr:SalesRfpAuditHeader = undefined;
	/**
	 * A record of all actions taken on this RFP
	 * Required: false
	 * Array: true
	 */
	rfpAuditLog:RfpAuditLog[] = undefined;
	/**
	 * OBI plan participation  factors
	 * Required: false
	 * Array: true
	 */
	rfpObi:RfpObi[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpContact:SalesRfpContact = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	salesRfpConstraint:SalesRfpConstraint[] = undefined;
	/**
	 * Key information for a SalesRFP
	 * Required: false
	 * Array: false
	 */
	salesRfpHdr:SalesRfpHdr = undefined;
	/**
	 * comments and exceptions during the RPF process including sales, pricing analyst, system
	 * Required: false
	 * Array: true
	 */
	workingComments:Comment[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingProposalHdr:PricingProposalHdr = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAgreement:PricingAgreement = undefined;
}
/**
 * Contains information about the User assigned to work on Rfp
 */
export class SalesRfpAuditHeader {
	/**
	 * Required: false
	 * Array: false
	 */
	assignToRsBuilderInd:boolean = undefined;
	/**
	 * audit status for the sales rfp once agreement is created
	 * Required: false
	 * Array: false
	 */
	auditStatus:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * unique identifier of the Rfp Audit Header record
	 * Required: false
	 * Array: false
	 */
	rfpAuditHeaderId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rsBuilderNotifyDate:Date = undefined;
	/**
	 * Date when the ruleset builder was assigned to the sales rfp
	 * Required: false
	 * Array: false
	 */
	rulesetBuilderAssignDate:Date = undefined;
	/**
	 * This is the date when the ruleset build is completed
	 * Required: false
	 * Array: false
	 */
	rsBuilderCompletedDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rulesetValidateDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rulesetBuildStatus:string = undefined;
	/**
	 * Employee Id of the user who is assigned to work on building ruleset for the Sales Rfp
	 * Required: false
	 * Array: false
	 */
	rulesetBuilderEmpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rulesetBuilderName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	sourcePrcRfpId:string = undefined;
}
/**
 * Comments related to Sales Rfp
 */
export class SalesRfpComment {
	/**
	 * unique db identifier of a comment
	 * Required: false
	 * Array: false
	 */
	commentId:string = undefined;
	/**
	 * unique id of sales rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique id of pricing proposal
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	comment:Comment = undefined;
}
/**
 * Captures the customer constraints related to a sales rfp
 */
export class SalesRfpConstraint {
	/**
	 * Type of AMC requested. 'SingAmc' - Single AMC or
	 * 'VarAmcByTraffic'
	 * Required: false
	 * Array: false
	 */
	amcTypeCode:string = undefined;
	/**
	 * The RFP average class of freight provided by sales, value zero indicates unknown value
	 * Required: false
	 * Array: false
	 */
	avgFreightClass:string = undefined;
	/**
	 * The RFP average density provided by sales, value zero indicates unknown value.
	 * Required: false
	 * Array: false
	 */
	avgShipmentDensity:Density = undefined;
	/**
	 * The RFP average shipment weight provided by sales, value zero indicates unknown value.
	 * Required: false
	 * Array: false
	 */
	avgShipmentWeight:Weight = undefined;
	/**
	 * Can customer accept different discounts for classes
	 * Required: false
	 * Array: false
	 */
	classDiscountInd:boolean = undefined;
	/**
	 * The number of divisions the customer has
	 * Required: false
	 * Array: false
	 */
	custDivisionsCount:number = undefined;
	/**
	 * Destination acceptable discounts that the customer can accept (Location, Zip1, Zip3, Zip5, State, Country, XPOBest)
	 * Required: false
	 * Array: false
	 */
	destAcceptableDiscounts:string = undefined;
	/**
	 * Where does the customer Ship to Zips/States/Regions
	 * Required: false
	 * Array: false
	 */
	destGroupId:string = undefined;
	/**
	 * Identifies if the customer has a limit to the number of discounts
	 * Required: false
	 * Array: false
	 */
	discountLimitInd:boolean = undefined;
	/**
	 * Type of Discount requested - values  are:
	 * simple     -- Speficic _Discount
	 * manual    --   Lane_Discount
	 * recommended -- LTL_Recommended_Discount  (RD template) -- RFP screen Lane Based Discounts or Lane Discount in WB
	 * Required: false
	 * Array: false
	 */
	discountType:string = undefined;
	/**
	 * 'VarDiscByWeight'
	 * 'VarDiscByTraffic'
	 * 'SingDiscount'
	 * 'VarDiscByApplication'
	 * Required: false
	 * Array: false
	 */
	discountSubType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	fakInd:boolean = undefined;
	/**
	 * Identifies if the customer has geo grouping constraints
	 * Required: false
	 * Array: false
	 */
	groupingConstraintInd:boolean = undefined;
	/**
	 * Identifies if inbound pricing must match either outbound pricing or third party pricing
	 * Required: false
	 * Array: false
	 */
	inboundPricingMatchInd:boolean = undefined;
	/**
	 * Identifies if the customer can accept different discount for different lanes
	 * Required: false
	 * Array: false
	 */
	laneDifferentDiscountInd:boolean = undefined;
	/**
	 * The maximum number of discounts a customer can accept
	 * Required: false
	 * Array: false
	 */
	maxDiscountNbr:number = undefined;
	/**
	 * What are the maximum weight bands a customer can accept
	 * Required: false
	 * Array: false
	 */
	maxNbrOfWeightBands:number = undefined;
	/**
	 * The number of shipments per month shipped by the customer, value zero indicates unknown value
	 * Required: false
	 * Array: false
	 */
	monthlyShipmentCount:number = undefined;
	/**
	 * The number of inbound locations for the customer
	 * Required: false
	 * Array: false
	 */
	nbrOfInboundLocations:number = undefined;
	/**
	 * The number of outbound locations for the customer
	 * Required: false
	 * Array: false
	 */
	nbrOfOutboundLocations:number = undefined;
	/**
	 * Origin acceptable discounts that the customer can accept (Location, Zip1, Zip3, Zip5, State, Country, XPOBest)
	 * Required: false
	 * Array: false
	 */
	origAcceptableDiscounts:string = undefined;
	/**
	 * Where does the customer Ship from Zips/States/Regions
	 * Required: false
	 * Array: false
	 */
	origGroupId:string = undefined;
	/**
	 * Identifies if outbound pricing must match either inbound pricing or third party pricing
	 * Required: false
	 * Array: false
	 */
	outboundPricingMatchInd:boolean = undefined;
	/**
	 * The primary shipment application type outbound, inbound/both or unknown
	 * Required: false
	 * Array: false
	 */
	primShipmentApplType:string = undefined;
	/**
	 * Required: false
	 * Array: true
	 */
	rfpConstraintEndPoint:SalesRfpConstraintEndPoint[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	rfpConstraintId:string = undefined;
	/**
	 * Requested shipment data source to use for analysis either historical shipment, shipment file or none.
	 * Required: false
	 * Array: false
	 */
	rqstdAnlysDataSource:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	shipmentFileSrc:string = undefined;
	/**
	 * Number of shipments in the shipment file
	 * Required: false
	 * Array: false
	 */
	shipmentFileCount:number = undefined;
	/**
	 * The valid values are ZIP1, STATE, ZIP
	 * Required: false
	 * Array: false
	 */
	shippingFromType:string = undefined;
	/**
	 * The valid values are ZIP1, STATE, ZIP
	 * Required: false
	 * Array: false
	 */
	shippingToType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	sourceType:string = undefined;
	/**
	 * Special Pricing Weight groups/Pallet/Pup
	 * Required: false
	 * Array: false
	 */
	specialPricingInd:boolean = undefined;
	/**
	 * Identifies if third party pricing must match either outbound pricing or inbound pricing
	 * Required: false
	 * Array: false
	 */
	thirdPartyPricingMatchInd:boolean = undefined;
	/**
	 * Can customer accept different discounts for weight bands
	 * Required: false
	 * Array: false
	 */
	wghtBandDiscountInd:boolean = undefined;
}
export class SalesRfpConstraintEndPoint {
	/**
	 * shipping from or shipping to
	 * Required: false
	 * Array: false
	 */
	applicationType:string = undefined;
	/**
	 * zip, zip1 or state
	 * Required: false
	 * Array: false
	 */
	type:string = undefined;
	/**
	 * either zip code or state code depending upon the type
	 * Required: false
	 * Array: false
	 */
	value:string = undefined;
}
/**
 * Contact Information for Sales Rfp in case of Cancellation of Pricing Agreement
 */
export class SalesRfpContact {
	/**
	 * unique id of sales rfp contact record
	 * Required: false
	 * Array: false
	 */
	rfpContactId:string = undefined;
	/**
	 * Audit Info details
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contactInfo:ContactInfo = undefined;
	/**
	 * Company name of the Contact Person
	 * Required: false
	 * Array: false
	 */
	companyName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contactAddress:Address = undefined;
	/**
	 * Customer location SIC code
	 * Required: false
	 * Array: false
	 */
	locationSic:string = undefined;
	/**
	 * Customer location SIC phone number
	 * Required: false
	 * Array: false
	 */
	locationSicPhone:string = undefined;
	/**
	 * cancellation letter status, printed or not printed
	 * Required: false
	 * Array: false
	 */
	cancellationLetterStatus:string = undefined;
	/**
	 * Cancellation letter notice date
	 * Required: false
	 * Array: false
	 */
	cancellationNoticeDate:Date = undefined;
	/**
	 * Cancellation Letter Document ID to view the document
	 * Required: false
	 * Array: false
	 */
	cancellationLetterDocId:string = undefined;
	/**
	 * Cancellation letter Date
	 * Required: false
	 * Array: false
	 */
	cancellationLetterDate:Date = undefined;
	/**
	 * Number of days Notice to be given
	 * Required: false
	 * Array: false
	 */
	noOfDaysNotice:number = undefined;
	/**
	 * Sales Agent Name
	 * Required: false
	 * Array: false
	 */
	salesAgentName:string = undefined;
	/**
	 * Sales Agent Phone number
	 * Required: false
	 * Array: false
	 */
	salesAgentPhone:string = undefined;
	/**
	 * Sales Agent Email Id
	 * Required: false
	 * Array: false
	 */
	salesAgentEmail:string = undefined;
	/**
	 * Sales Agent Title
	 * Required: false
	 * Array: false
	 */
	salesAgentTitle:string = undefined;
	/**
	 * Forced Increased Percent
	 * Required: false
	 * Array: false
	 */
	forcedIncreasePct:number = undefined;
	/**
	 * First Other Contact person
	 * Required: false
	 * Array: false
	 */
	firstOtherContact:string = undefined;
	/**
	 * Second Other Contact person
	 * Required: false
	 * Array: false
	 */
	secondOtherContact:string = undefined;
	/**
	 * Third Other Contact person
	 * Required: false
	 * Array: false
	 */
	thirdOtherContact:string = undefined;
	/**
	 * Fourth Other Contact person
	 * Required: false
	 * Array: false
	 */
	fourthOtherContact:string = undefined;
	/**
	 * Fifth Other Contact person
	 * Required: false
	 * Array: false
	 */
	fifthOtherContact:string = undefined;
}
/**
 * Key information for a SalesRFP
 */
export class SalesRfpHdr {
	/**
	 * Area Code of the sales rfp owner
	 * Required: false
	 * Array: false
	 */
	areaCode:string = undefined;
	/**
	 * Type of Pricing Agreement
	 * Required: false
	 * Array: false
	 */
	agreementTypeCd:string = undefined;
	/**
	 * Date when the RFP is approved
	 * Required: false
	 * Array: false
	 */
	approvedDate:Date = undefined;
	/**
	 * Audit info details
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * This indicates that the RFP is for a Bid.
	 * Required: false
	 * Array: false
	 */
	bidInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	contractStatus:string = undefined;
	/**
	 * The ID of the individual or system who creates the Sales RFP
	 * Required: false
	 * Array: false
	 */
	creatorId:string = undefined;
	/**
	 * The ID of the owner of sales RFP, the owner transfers to the party that is actively working on the RFP.
	 * Required: false
	 * Array: false
	 */
	currentOwnerId:string = undefined;
	/**
	 * The customers total sales revenue from there NAICS code. ï¿½
	 * Required: false
	 * Array: false
	 */
	custAnnualSalesRev:MonetaryAmount = undefined;
	/**
	 * The TMS system a customer uses
	 * Required: false
	 * Array: false
	 */
	customerTms:string = undefined;
	/**
	 * The TMS version the customer uses
	 * Required: false
	 * Array: false
	 */
	customerTmsVersion:number = undefined;
	/**
	 * The date the Sales RFP is due from Pricing back to Sales
	 * Required: false
	 * Array: false
	 */
	dueDate:Date = undefined;
	/**
	 * This indicates this is in cycle sales rfp or not
	 * Required: false
	 * Array: false
	 */
	inCycleInd:boolean = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	mappedShipmentFileInd:boolean = undefined;
	/**
	 * This is the monthly promise amount for the customer
	 * Required: false
	 * Array: false
	 */
	monthlyPotentialAmt:MonetaryAmount = undefined;
	/**
	 * This is the monthly promise amount for the customer
	 * Required: false
	 * Array: false
	 */
	monthlyPromiseAmt:MonetaryAmount = undefined;
	/**
	 * ï¿½North American Industry Classification System (NAICS)ï¿½codesï¿½used to classify the primary business activity of a firm.
	 * Required: false
	 * Array: false
	 */
	naicsCd:string = undefined;
	/**
	 * Name of the Customer with whom the agreement is being or was negotiated.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustAcctNm:string = undefined;
	/**
	 * Identifier of the Customer with whom the agreement is being or was negotiated. This will uniquely identify the customer in the CIS component.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustInstId:string = undefined;
	/**
	 * This is the business key at the time of negotiation for this customer. Note that it will not be kept in synch with the master customer database.
	 * Required: false
	 * Array: false
	 */
	negotiatingCustMadCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	negotiatingCustTypeCd:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	negotiatingCustAddr:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	newPricingAgrmtCustId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	newPricingAgrmtCustVer:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	newPricingAgrmtSeqNbr:number = undefined;
	/**
	 * The instance ID of the customer that negotiated this pricing agreement
	 * Required: false
	 * Array: false
	 */
	prevPricingAgrmtCustId:string = undefined;
	/**
	 * The version of the  pricing agreement
	 * Required: false
	 * Array: false
	 */
	prevPricingAgrmtCustVer:number = undefined;
	/**
	 * Identifies if the previous pricing agreement has OBI Program
	 * Required: false
	 * Array: false
	 */
	prevPricingObiInd:boolean = undefined;
	/**
	 * The sequence number within the version of pricing agreement
	 * Required: false
	 * Array: false
	 */
	prevPricingAgrmtSeqNbr:number = undefined;
	/**
	 * The ID of the Pricing Analyst
	 * Required: false
	 * Array: false
	 */
	pricingAnalystId:string = undefined;
	/**
	 * This contains the code that describes the type of pricing described by this RFP. Sample values: Regional, Strategic
	 * Required: false
	 * Array: false
	 */
	pricingTypeCd:string = undefined;
	/**
	 * Identifier of the primary customer location frequently the negotiating location associated with the RFP
	 * Required: false
	 * Array: false
	 */
	primaryAcctInstId:string = undefined;
	/**
	 * Primary customer location code associated with the RFP
	 * Required: false
	 * Array: false
	 */
	primaryAcctCode:string = undefined;
	/**
	 * assigns a priority to sales rfp Low, Medum, High
	 * Required: false
	 * Array: false
	 */
	priority:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	proposalExistsInd:boolean = undefined;
	/**
	 * Requested Effective Date for the Agreement if the request is approved.
	 * Required: false
	 * Array: false
	 */
	proposedEffDate:Date = undefined;
	/**
	 * Proposed Expiration Date for the Agreement if the request is approved.
	 * Required: false
	 * Array: false
	 */
	proposedExpDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	reasonCd:string = undefined;
	/**
	 * This is the requested discount for the customer  (Enter Requested Discounts/XPO LTL pricing recommend discounts/Simple)
	 * Required: false
	 * Array: false
	 */
	requestedDiscountType:string = undefined;
	/**
	 * The SIC code based on the sales agent's SIC code at RFP creation time
	 * Required: false
	 * Array: false
	 */
	requestSic:string = undefined;
	/**
	 * The date the sales RFP is first submitted to Pricing
	 * Required: false
	 * Array: false
	 */
	rfpRequestDate:Date = undefined;
	/**
	 * Current status of the request (Sales or Pricing). Sample values: Approved, Canceled, NewRFP, InSales, InPricing, InPricingAnalysis
	 * Required: false
	 * Array: false
	 */
	rfpStatusCd:string = undefined;
	/**
	 * This contains the code that describes the type of RFP this will be. Sample values - NewOpportunity, Renegotiation, AdditionalBusiness, Updateocation, ChangeAC, CancelPricing
	 * Required: false
	 * Array: false
	 */
	rfpTypeCd:string = undefined;
	/**
	 * The ID of the Sales Agent
	 * Required: false
	 * Array: false
	 */
	salesAgentId:string = undefined;
	/**
	 * This identifies the sales program associated with the RFP.
	 * Required: false
	 * Array: false
	 */
	salesPgm:string = undefined;
	/**
	 * The sales region code based on the sales agent's region code at RFP creation time
	 * Required: false
	 * Array: false
	 */
	salesRegionCd:string = undefined;
	/**
	 * Sales District code of the Sales RFP
	 * Required: false
	 * Array: false
	 */
	salesDistrictCd:string = undefined;
	/**
	 * Unique Identifier of the opportunity in the CRM (salesforce) system
	 * Required: false
	 * Array: false
	 */
	sourceCrmOpportunityId:string = undefined;
	/**
	 * The database identifier of this SalesRFP
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * A code that qualifies a request by service of transportation.
	 * Required: false
	 * Array: false
	 */
	serviceTypeCd:string = undefined;
	/**
	 * Credit status of the negotiating customer
	 * Required: false
	 * Array: false
	 */
	negotiatingCustCredStatCd:string = undefined;
	/**
	 * Unique identifier of the Sales Rfp to create new Sales Rfp
	 * Required: false
	 * Array: false
	 */
	sourceSalesRfpId:string = undefined;
	/**
	 * true if RFP is for a 3PL
	 * Required: false
	 * Array: false
	 */
	thirdPartyLogisticsInd:boolean = undefined;
	/**
	 * Regional or Strategic
	 * Required: false
	 * Array: false
	 */
	thirdPartyLogisticsTypeCd:string = undefined;
	/**
	 * Indicates if the updates are allowed on this Sales Rfp
	 * Required: false
	 * Array: false
	 */
	updatesAllowedInd:boolean = undefined;
	/**
	 * Type of location update: Add or Delete
	 * Required: false
	 * Array: false
	 */
	updateLocationType:string = undefined;
	/**
	 * Tye of Action Allowed fo this sales rfp
	 * Required: false
	 * Array: false
	 */
	ActionAllowed:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	salesAgentName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	creatorName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	pricingAnalystName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	currentOwnerName:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	renegotiationDate:Date = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	attachmentExist:boolean = undefined;
	/**
	 * unique identifier of Pricing Agreement Monitor
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorId:string = undefined;
	/**
	 * This is the pricing agreement monitor name, if the rfp is created out of PAM
	 * Required: false
	 * Array: false
	 */
	pricingAgreementMonitorName:string = undefined;
	/**
	 * This is the promise amount before request for expectation adjustment
	 * Required: false
	 * Array: false
	 */
	originalPromiseAmt:MonetaryAmount = undefined;
	/**
	 * This is the potential amount before request for expectation adjustment
	 * Required: false
	 * Array: false
	 */
	originalPotentialAmt:MonetaryAmount = undefined;
	/**
	 * Current monthly Reveune Amout derived from shipment summary
	 * Required: false
	 * Array: false
	 */
	currentMonthlyRevAmt:MonetaryAmount = undefined;
	/**
	 * Employee Id of the First Approver
	 * Required: false
	 * Array: false
	 */
	firstApproverId:string = undefined;
	/**
	 * Employee Id of the second approver
	 * Required: false
	 * Array: false
	 */
	secondApproverId:string = undefined;
	/**
	 * contact validated Indicator
	 * Required: false
	 * Array: false
	 */
	contactValidatedInd:string = undefined;
	/**
	 * Rfp last status updated date
	 * Required: false
	 * Array: false
	 */
	lastStatusUpdated:Date = undefined;
	/**
	 * Third Party Logistics customer Instance Id
	 * Required: false
	 * Array: false
	 */
	thirdPlCustInstId:number = undefined;
	/**
	 * Third Party Logistic Customer Mad Code
	 * Required: false
	 * Array: false
	 */
	thirdPlCustMadCd:string = undefined;
	/**
	 * Special Pricing Request Ind
	 * Required: false
	 * Array: false
	 */
	specialPricingRequestInd:string = undefined;
	/**
	 * Auto Approved Ind
	 * Required: false
	 * Array: false
	 */
	autoApprovedInd:string = undefined;
}
/**
 * Information about Sales Rfp created from Auto General Rate Increase
 */
export class SalesRfpGri {
	/**
	 * The database identifier of this SalesRFP
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * % to increase the total charge
	 * Required: false
	 * Array: false
	 */
	rateIncrPct:string = undefined;
	/**
	 * The Employee Id of sales agent
	 * Required: false
	 * Array: false
	 */
	salesAgentId:string = undefined;
	/**
	 * The employee Id of the Requester
	 * Required: false
	 * Array: false
	 */
	requesterId:string = undefined;
	/**
	 * This is the monthly promise amount for the customer
	 * Required: false
	 * Array: false
	 */
	monthlyPromiseAmt:MonetaryAmount = undefined;
	/**
	 * This is the monthly promise amount for the customer
	 * Required: false
	 * Array: false
	 */
	monthlyPotentialAmt:MonetaryAmount = undefined;
	/**
	 * Projected Operating Ratio based on proposed pricing
	 * Required: false
	 * Array: false
	 */
	projectedOrPct:string = undefined;
	/**
	 * Information about the Pricing current Agreement for GRI
	 * Required: false
	 * Array: false
	 */
	pricingAgreement:PricingAgreementBasic = undefined;
}
/**
 * Information about the Supporting documents captured for a Sales Rfp
 */
export class SalesRfpSupportingDocument {
	/**
	 * unique identifier of the sales rfp supporting document record
	 * Required: false
	 * Array: false
	 */
	rfpSupportingDocId:string = undefined;
	/**
	 * unique identifier of sales rfp
	 * Required: false
	 * Array: false
	 */
	salesRfpId:string = undefined;
	/**
	 * unique identifier for Pricing Proposal
	 * Required: false
	 * Array: false
	 */
	prcProposalInstId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	documentInfo:SalesRfpSupportingDocument_Document = undefined;
	/**
	 * status code when adding the document to the DMS
	 * Required: false
	 * Array: false
	 */
	statusCd:string = undefined;
	/**
	 * status message when ading the document to the DMS
	 * Required: false
	 * Array: false
	 */
	statusMsg:string = undefined;
}
/**
 * Information about the Customer Shipment summary for a given Pricing Agreement
 */
export class ShipmentSummary {
	/**
	 * unique identifier of the Pricing Agreement Shipment Summary
	 * Required: false
	 * Array: false
	 */
	agreementShipmentSummId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	currentOR:number = undefined;
	/**
	 * Off Billing Incentive amount for the current year
	 * Required: false
	 * Array: false
	 */
	currentYearObi:number = undefined;
	/**
	 * Shipment count for the current year
	 * Required: false
	 * Array: false
	 */
	currentYearShipmentCount:number = undefined;
	/**
	 * Total weight of the shipments shipped by customer in current year
	 * Required: false
	 * Array: false
	 */
	currentYearWeight:Weight = undefined;
	/**
	 * Cost Amount for the current year
	 * Required: false
	 * Array: false
	 */
	currYearCostAmt:MonetaryAmount = undefined;
	/**
	 * Revenue amount for the current year
	 * Required: false
	 * Array: false
	 */
	currYearRevAmt:MonetaryAmount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	previousYearCostAmt:MonetaryAmount = undefined;
	/**
	 * off bill incentive amount from previous year
	 * Required: false
	 * Array: false
	 */
	previousYearObi:number = undefined;
	/**
	 * customers revenue amount from previous year
	 * Required: false
	 * Array: false
	 */
	previousYearRevAmt:MonetaryAmount = undefined;
	/**
	 * customers shipment count from previous year
	 * Required: false
	 * Array: false
	 */
	previousYearShipmentCount:number = undefined;
	/**
	 * Total weight of the customers shipment in previous year
	 * Required: false
	 * Array: false
	 */
	previousYearWeight:Weight = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	priorOR:number = undefined;
	/**
	 * Year and Month for the shipment summary
	 * Required: false
	 * Array: false
	 */
	yearMonth:Date = undefined;
	/**
	 * monthly revenue amount
	 * Required: false
	 * Array: false
	 */
	monthlyRevAmt:MonetaryAmount = undefined;
	/**
	 * Start date of shipment summary record used when calculating average revenue by 6 moths or 12 months
	 * Required: true
	 * Array: false
	 */
	startDate:Date = undefined;
	/**
	 * End date of shipment summary record used when calculating average revenue by 6 moths or 12 months
	 * Required: true
	 * Array: false
	 */
	endDate:Date = undefined;
	/**
	 * Sales Comp Revenue Amount
	 * Required: false
	 * Array: false
	 */
	salesCompRevAmt:MonetaryAmount = undefined;
}
/**
 *  Strategy Details from analysis shipments
 */
export class StrategyDetails {
	/**
	 * Required: false
	 * Array: false
	 */
	StrategyType:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalShipmentCount:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalWeight:Weight = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	averageWeight:Weight = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	averageMiles:Distance = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	freightRevenueIncreasePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	totalRevenueIncreasePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	profitIncreasePct:number = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	operatingIncomeImprovementPct:number = undefined;
}
/**
 * A definition of a User Preference
 */
export class UserPreference {
	/**
	 * The application this user preference is for
	 * Required: true
	 * Array: false
	 */
	appName:string = undefined;
	/**
	 * User Preference Data
	 * Required: true
	 * Array: false
	 */
	preference:string = undefined;
}
/**
 * A definition of a particular workflow
 */
export class Workflow {
	/**
	 * unique id of extract workflow
	 * Required: false
	 * Array: false
	 */
	prcExtractWorkflowId:string = undefined;
	/**
	 * Unique identifier of workflow  metadata
	 * Required: false
	 * Array: false
	 */
	workflowTypeCd:string = undefined;
	/**
	 * Name of workflow
	 * Required: false
	 * Array: false
	 */
	workflowNm:string = undefined;
	/**
	 * The steps that are part of this workflow
	 * Required: false
	 * Array: true
	 */
	workflowSteps:WorkflowStep[] = undefined;
}
/**
 * A definition of a particular workflow
 */
export class WorkflowStep {
	/**
	 * Order of workflow step
	 * Required: false
	 * Array: false
	 */
	stepSeqNbr:number = undefined;
	/**
	 * The type of extract for this step
	 * Required: false
	 * Array: false
	 */
	extractTypeCd:string = undefined;
	/**
	 * THe name of the extract type
	 * Required: false
	 * Array: false
	 */
	extractNm:string = undefined;
	/**
	 * Required: true
	 * Array: false
	 */
	prcExtractWorkflowId:string = undefined;
}
export class RfpFileRequest {
	/**
	 * Unique DB Identiifier
	 * Required: false
	 * Array: false
	 */
	requestInstId:number = undefined;
	/**
	 * The type of file being requested.
	 * 
	 * VALID VALUES
	 * TransitTime
	 * Bid
	 * RateFile
	 * DiscountAMC
	 * Required: false
	 * Array: false
	 */
	requestTypeCode:string = undefined;
	/**
	 * JSON request payload
	 * Required: false
	 * Array: false
	 */
	requestPayload:string = undefined;
	/**
	 * Email address of person making the request.
	 * Required: false
	 * Array: false
	 */
	requestorEmailId:string = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	auditInfo:AuditInfo = undefined;
}
export class ListSalesRfpSupportingDocsResp_SalesRfpSupportingDocument extends SalesRfpSupportingDocument {
}
export class ListCrmAccountInfoResp_CrmAccount extends CrmAccount {
	/**
	 * Required: false
	 * Array: true
	 */
	accountOwner:CrmAccountOwner[] = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	parentAccount:CrmAccount = undefined;
	/**
	 * Required: false
	 * Array: false
	 */
	ultimateAccountParent:CrmAccount = undefined;
}
export class PricingLane_Lane extends Lane {
}
export class SalesRfpSupportingDocument_Document extends Document {
}

/**
 * Query parameters for getSalesRfp
 */
export class GetSalesRfpQuery {
	levelOfDetail: string = undefined;
}

/**
 * Query parameters for listSalesRFPs
 */
export class ListSalesRFPsQuery {
	ownerId: string = undefined;
	currentOwnerId: string = undefined;
	pricingAnalystId: string = undefined;
	negotiatingCustInstId: string = undefined;
	negotiatingCustMadCd: string = undefined;
	proposedEffDate: string = undefined;
	proposedExpDate: string = undefined;
	dueDate: string = undefined;
	startDate: string = undefined;
	endDate: string = undefined;
	salesRegionCd: string = undefined;
	salesDistrictCd: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for getSalesRfpCounts
 */
export class GetSalesRfpCountsQuery {
	levelOfDetail: string = undefined;
	ownerId: string = undefined;
	currentOwnerId: string = undefined;
	pricingAnalystId: string = undefined;
	negotiatingCustInstId: string = undefined;
	negotiatingCustMadCd: string = undefined;
	proposedEffDate: string = undefined;
	proposedExpDate: string = undefined;
	dueDate: string = undefined;
	startDate: string = undefined;
	endDate: string = undefined;
	salesRegionCd: string = undefined;
}

/**
 * Query parameters for listSalesRfpLifeCycles
 */
export class ListSalesRfpLifeCyclesQuery {
	acctId: string = undefined;
	dateCriteria: string = undefined;
	startDate: string = undefined;
	endDate: string = undefined;
	salesRegionCd: string = undefined;
	areaCode: string = undefined;
	rfpStatusCd: string = undefined;
	auditStatus: string = undefined;
	rulesetBuildStatus: string = undefined;
	proposalStatCd: string = undefined;
	reasonCd: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for updateSalesRfp
 */
export class UpdateSalesRfpQuery {
	prcProposalInstId: string = undefined;
}

/**
 * Query parameters for updateSalesRfpAuditHeader
 */
export class UpdateSalesRfpAuditHeaderQuery {
	emailNotify: string = undefined;
}

/**
 * Query parameters for listSalesRfpAccts
 */
export class ListSalesRfpAcctsQuery {
	parentAcctInstId: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listSalesRfpAccessorials
 */
export class ListSalesRfpAccessorialsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listGlobalAccessorials
 */
export class ListGlobalAccessorialsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAccessorialsByType
 */
export class ListPricingAccessorialsByTypeQuery {
	typeCode: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAccessorialsApplInd
 */
export class ListPricingAccessorialsApplIndQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
	typeCode: string = undefined;
}

/**
 * Query parameters for listSalesRfpAuditLogs
 */
export class ListSalesRfpAuditLogsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for createSalesRfpSupportingDocs
 */
export class CreateSalesRfpSupportingDocsQuery {
	prcProposalInstId: string = undefined;
}

/**
 * Query parameters for listSalesRfpSupportingDocs
 */
export class ListSalesRfpSupportingDocsQuery {
	prcProposalInstId: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for getSalesRfpSupportingDocsFile
 */
export class GetSalesRfpSupportingDocsFileQuery {
	rfpSupportingDocId: string[] = undefined;
}

/**
 * Query parameters for createSalesRfpComments
 */
export class CreateSalesRfpCommentsQuery {
	prcProposalInstId: string = undefined;
}

/**
 * Query parameters for listSalesRfpComments
 */
export class ListSalesRfpCommentsQuery {
	prcProposalInstId: string = undefined;
	commentTypeCd: string = undefined;
	commentSubTypeCd: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deleteSalesRfpComments
 */
export class DeleteSalesRfpCommentsQuery {
	prcProposalInstId: string = undefined;
	commentId: string = undefined;
	commentTypeCd: string = undefined;
	commentSubTypeCd: string = undefined;
}

/**
 * Query parameters for listSalesRfpObiPlans
 */
export class ListSalesRfpObiPlansQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deleteSalesRfpObiPlans
 */
export class DeleteSalesRfpObiPlansQuery {
	rfpObiPlanId: string = undefined;
}

/**
 * Query parameters for listSalesRfpGeoAreaPricingLanes
 */
export class ListSalesRfpGeoAreaPricingLanesQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listSalesRfpCommodities
 */
export class ListSalesRfpCommoditiesQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listSalesRfpLadingDimensions
 */
export class ListSalesRfpLadingDimensionsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listSalesRfpConstraints
 */
export class ListSalesRfpConstraintsQuery {
	sourceType: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listSalesRfpCustomerShipments
 */
export class ListSalesRfpCustomerShipmentsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listCrmAccount
 */
export class ListCrmAccountQuery {
	accountId: string[] = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for createPricingProposalHeader
 */
export class CreatePricingProposalHeaderQuery {
	salesRfpId: string = undefined;
	triggerProposalAnlysFlow: string = undefined;
}

/**
 * Query parameters for listPricingProposalHeaders
 */
export class ListPricingProposalHeadersQuery {
	salesRfpId: string = undefined;
	analysisTypeCd: string = undefined;
	proposalStatCd: string = undefined;
	pricingAnalystId: string = undefined;
	employeeRoleType: string[] = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for updatePricingProposal
 */
export class UpdatePricingProposalQuery {
	overrideInd: string = undefined;
}

/**
 * Query parameters for getPricingProposal
 */
export class GetPricingProposalQuery {
	levelOfDetail: string[] = undefined;
	excludeHdr: string = undefined;
}

/**
 * Query parameters for listPricingProposalWorkQueue
 */
export class ListPricingProposalWorkQueueQuery {
	pricingAnalystId: string = undefined;
	proposalStatCd: string[] = undefined;
	analysisTypeCd: string = undefined;
	acctId: string = undefined;
	dateCriteria: string = undefined;
	startDate: string = undefined;
	endDate: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAnlysGeoGroupHeaders
 */
export class ListPricingAnlysGeoGroupHeadersQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listGeoPricingGroupLanes
 */
export class ListGeoPricingGroupLanesQuery {
	acctInstId: string = undefined;
	acctMadCd: string = undefined;
	geoLaneIds: string[] = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deleteGeoPricingGroupLanes
 */
export class DeleteGeoPricingGroupLanesQuery {
	geoPricingGroupLaneMbrId: string = undefined;
}

/**
 * Query parameters for listGeoPricingGroups
 */
export class ListGeoPricingGroupsQuery {
	geoPricingGroupId: string[] = undefined;
	custAcctId: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAnalysisWorkflowProgress
 */
export class ListPricingAnalysisWorkflowProgressQuery {
	extractTypeCd: string = undefined;
	extractStatusCd: string = undefined;
	latestFlow: string = undefined;
	active: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for getPricingExtractWorkflow
 */
export class GetPricingExtractWorkflowQuery {
	workflowNm: string = undefined;
}

/**
 * Query parameters for getPricingExtractWorkflowStep
 */
export class GetPricingExtractWorkflowStepQuery {
	stepSeqNbr: string = undefined;
}

/**
 * Query parameters for deletePricingAnalysisWorkFlow
 */
export class DeletePricingAnalysisWorkFlowQuery {
	analysisFlowInstId: string = undefined;
}

/**
 * Query parameters for listPricingAnalysts
 */
export class ListPricingAnalystsQuery {
	managerEmplId: string = undefined;
	employeeRoleType: string[] = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingAdminPersonnel
 */
export class DeletePricingAdminPersonnelQuery {
	adminPersonnelId: string[] = undefined;
}

/**
 * Query parameters for updatePricingAnalysisShipments
 */
export class UpdatePricingAnalysisShipmentsQuery {
	intraAmc: string = undefined;
	interAmc: string = undefined;
	intlAmc: string = undefined;
	enableAnalysis: string = undefined;
	adjustPricingType: string = undefined;
}

/**
 * Query parameters for listPricingAnalysisShipments
 */
export class ListPricingAnalysisShipmentsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingLeafShipments
 */
export class ListPricingLeafShipmentsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingModelSegments
 */
export class ListPricingModelSegmentsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingAnalysisShipments
 */
export class DeletePricingAnalysisShipmentsQuery {
	prcAnalysisShipmentId: string[] = undefined;
}

/**
 * Query parameters for listPricingAnlysGeoGroupBIAmcs
 */
export class ListPricingAnlysGeoGroupBIAmcsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingAnlysGeoGroupBIAmcs
 */
export class DeletePricingAnlysGeoGroupBIAmcsQuery {
	geoGroupBiAmcId: string = undefined;
}

/**
 * Query parameters for listGeoPricingDiscountAmcSummary
 */
export class ListGeoPricingDiscountAmcSummaryQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deleteGeoPricingDiscountAmcSummary
 */
export class DeleteGeoPricingDiscountAmcSummaryQuery {
	geoDiscAmcSummaryId: string = undefined;
}

/**
 * Query parameters for listPricingAnlysGeoGroupBiTariffs
 */
export class ListPricingAnlysGeoGroupBiTariffsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingAnlysGeoGroupBiTariff
 */
export class DeletePricingAnlysGeoGroupBiTariffQuery {
	geoGroupBiTariffId: string = undefined;
}

/**
 * Query parameters for listPricingAnlysGeoGroupCmTariffs
 */
export class ListPricingAnlysGeoGroupCmTariffsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingAnlysGeoGroupCmTariff
 */
export class DeletePricingAnlysGeoGroupCmTariffQuery {
	geoGroupCmTariffId: string = undefined;
}

/**
 * Query parameters for listPricingAnlysGeoGroupCmAmcs
 */
export class ListPricingAnlysGeoGroupCmAmcsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingAnlysGeoGroupCmAmcs
 */
export class DeletePricingAnlysGeoGroupCmAmcsQuery {
	geoGroupCmAmcId: string = undefined;
}

/**
 * Query parameters for listPricingProposalAccessorials
 */
export class ListPricingProposalAccessorialsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingProposalGeoPricingLanes
 */
export class ListPricingProposalGeoPricingLanesQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingProposalGeoPricingLanes
 */
export class DeletePricingProposalGeoPricingLanesQuery {
	geoAreaPrcLaneId: string = undefined;
}

/**
 * Query parameters for listGeoPricingLaneAmcs
 */
export class ListGeoPricingLaneAmcsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deleteGeoPricingLaneAmcs
 */
export class DeleteGeoPricingLaneAmcsQuery {
	geoPricingLaneAmcId: string = undefined;
}

/**
 * Query parameters for listGeoPricingLaneDiscounts
 */
export class ListGeoPricingLaneDiscountsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deleteGeoPricingLaneDiscounts
 */
export class DeleteGeoPricingLaneDiscountsQuery {
	geoPricingLaneDiscountId: string = undefined;
}

/**
 * Query parameters for createPricingAnalysisShipmentAccessorials
 */
export class CreatePricingAnalysisShipmentAccessorialsQuery {
	enableAnalysis: string = undefined;
}

/**
 * Query parameters for updatePricingAnalysisShipmentAccessorials
 */
export class UpdatePricingAnalysisShipmentAccessorialsQuery {
	enableAnalysis: string = undefined;
}

/**
 * Query parameters for listPricingAnalysisShipmentAccessorials
 */
export class ListPricingAnalysisShipmentAccessorialsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingAnalysisShipmentAccessorials
 */
export class DeletePricingAnalysisShipmentAccessorialsQuery {
	prcAnlysShipmentAccsrlId: string = undefined;
}

/**
 * Query parameters for listPricingAnalystAssignments
 */
export class ListPricingAnalystAssignmentsQuery {
	assignmentTypeCd: string = undefined;
	acctExecEmployeeId: string = undefined;
	nasSupervisorEmployeeId: string = undefined;
	nasSpecialistEmployeeId: string = undefined;
	salesRegionCode: string = undefined;
	areaCode: string = undefined;
	agreementTypeCode: string = undefined;
	rfpTypeCode: string = undefined;
	effectiveDate: string = undefined;
	expirationDate: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAnlysGeoGroupDiscAmcs
 */
export class ListPricingAnlysGeoGroupDiscAmcsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingAnlysGeoGroupDiscAmcs
 */
export class DeletePricingAnlysGeoGroupDiscAmcsQuery {
	geoGroupDiscAmcId: string = undefined;
}

/**
 * Query parameters for createPricingProposalCustHierarchy
 */
export class CreatePricingProposalCustHierarchyQuery {
	updateShipment: string = undefined;
}

/**
 * Query parameters for listPricingProposalCustHierarchy
 */
export class ListPricingProposalCustHierarchyQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for updatePricingProposalCustHierarchies
 */
export class UpdatePricingProposalCustHierarchiesQuery {
	updateShipment: string = undefined;
}

/**
 * Query parameters for calculatePricingGroupSummaryShipments
 */
export class CalculatePricingGroupSummaryShipmentsQuery {
	categoryType: string[] = undefined;
	discAdjustment: string = undefined;
	newAmc: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for calculatePricingGroupShipmentAmc
 */
export class CalculatePricingGroupShipmentAmcQuery {
	intraAmc: string = undefined;
	interAmc: string = undefined;
	intlAmc: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for calculatePricingGroupShipmentAccsrl
 */
export class CalculatePricingGroupShipmentAccsrlQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deleteGeoPricingLaneFaks
 */
export class DeleteGeoPricingLaneFaksQuery {
	pricingProposalFakId: string = undefined;
}

/**
 * Query parameters for deleteGeoPricingLaneFakOverrides
 */
export class DeleteGeoPricingLaneFakOverridesQuery {
	overrideIds: string[] = undefined;
}

/**
 * Query parameters for listPricingProposalRulesetMatrix
 */
export class ListPricingProposalRulesetMatrixQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingProposalRulesetMatrix
 */
export class DeletePricingProposalRulesetMatrixQuery {
	prcProposalRulesetMatrixId: string = undefined;
}

/**
 * Query parameters for listPricingProposalTmsFile
 */
export class ListPricingProposalTmsFileQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for deletePricingProposalTmsFile
 */
export class DeletePricingProposalTmsFileQuery {
	prcProposalTmsFileId: string = undefined;
}

/**
 * Query parameters for listAggregateModelConstraintTemplates
 */
export class ListAggregateModelConstraintTemplatesQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingProposalAggrModelGeoStagings
 */
export class ListPricingProposalAggrModelGeoStagingsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listGeoPricingGroupsForProposal
 */
export class ListGeoPricingGroupsForProposalQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listGeoPricingGroupLanesForProposal
 */
export class ListGeoPricingGroupLanesForProposalQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listGeoPricingGroupMembersForProposal
 */
export class ListGeoPricingGroupMembersForProposalQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listGeoPricingGroupExclMembersForProposal
 */
export class ListGeoPricingGroupExclMembersForProposalQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingProposalAmcDiscounts
 */
export class ListPricingProposalAmcDiscountsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingCodesByType
 */
export class ListPricingCodesByTypeQuery {
	categoryCode: string[] = undefined;
	subCategoryCode: string[] = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listSalesRfpAcctsRuleset
 */
export class ListSalesRfpAcctsRulesetQuery {
	listInfo: string = undefined;
}

/**
 * Query parameters for listAcctPricingAgreement
 */
export class ListAcctPricingAgreementQuery {
	acctInstId: string[] = undefined;
	leadAcctId: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAgreements
 */
export class ListPricingAgreementsQuery {
	acctInstId: string = undefined;
	agreementId: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAgreementAccts
 */
export class ListPricingAgreementAcctsQuery {
	leadAcctId: string = undefined;
	agreementAcctVersion: string = undefined;
	agreementSeqNbr: number = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAgreementExpectations
 */
export class ListPricingAgreementExpectationsQuery {
	leadAcctId: string = undefined;
	agreementSeqNbr: string = undefined;
	agreementCustVersion: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAgreementShipmentSumm
 */
export class ListPricingAgreementShipmentSummQuery {
	leadAcctId: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for getPricingAgreementShipmentSumm
 */
export class GetPricingAgreementShipmentSummQuery {
	leadAcctId: string = undefined;
	agreementSeqNbr: string = undefined;
	agreementCustVersion: string = undefined;
	prevMonthCount: string = undefined;
}

/**
 * Query parameters for getPricingAgreementLiabilityDetails
 */
export class GetPricingAgreementLiabilityDetailsQuery {
	acctInstId: string = undefined;
	seqNbr: string = undefined;
	versionNbr: string = undefined;
	agreementId: string = undefined;
}

/**
 * Query parameters for getPricingAgreementReportFile
 */
export class GetPricingAgreementReportFileQuery {
	leadAcctId: string = undefined;
	agreementAcctVersion: string = undefined;
	agreementSeqNbr: string = undefined;
	reportType: string = undefined;
}

/**
 * Query parameters for listRatingTariffWeightBreaks
 */
export class ListRatingTariffWeightBreaksQuery {
	tariffCd: string[] = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for getSalesRfpReportFile
 */
export class GetSalesRfpReportFileQuery {
	reportType: string = undefined;
}

/**
 * Query parameters for listPricingAdminProposedStrategy
 */
export class ListPricingAdminProposedStrategyQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAdminProposedIncreaseLevel
 */
export class ListPricingAdminProposedIncreaseLevelQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAdminFakAdjustPct
 */
export class ListPricingAdminFakAdjustPctQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAdminFscPctLookup
 */
export class ListPricingAdminFscPctLookupQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAgreementMonitors
 */
export class ListPricingAgreementMonitorsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
	startYearMonth: string = undefined;
	endYearMonth: string = undefined;
	pricingTypeCd: string = undefined;
	leadAcctId: string = undefined;
	agreementCustVersion: string = undefined;
	agreementSeqNbr: string = undefined;
	salesRegion: string = undefined;
	salesDistrict: string = undefined;
	salesOwnerEmployeeId: string = undefined;
	statusCd: string = undefined;
	typeCd: string = undefined;
}

/**
 * Query parameters for getPricingAgreementMonitorCounts
 */
export class GetPricingAgreementMonitorCountsQuery {
	levelOfDetail: string = undefined;
	ownerId: string = undefined;
}

/**
 * Query parameters for listPricingAgreementMonitorParameters
 */
export class ListPricingAgreementMonitorParametersQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for getPricingAgreementMonitorRegionRollupReport
 */
export class GetPricingAgreementMonitorRegionRollupReportQuery {
	yearMonth: string = undefined;
	statusCd: string = undefined;
}

/**
 * Query parameters for listPricingAgreementMonitorValidActions
 */
export class ListPricingAgreementMonitorValidActionsQuery {
	actionSource: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPraAgreementMonitorRunDate
 */
export class ListPraAgreementMonitorRunDateQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAgreementAudits
 */
export class ListPricingAgreementAuditsQuery {
	employeeId: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
	fromDate: string = undefined;
	toDate: string = undefined;
	custAcctId: string = undefined;
	auditStatus: string = undefined;
	salesRfpId: string = undefined;
}

/**
 * Query parameters for listPricingAgreementRecentAudits
 */
export class ListPricingAgreementRecentAuditsQuery {
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAgreementNextRevisionAudits
 */
export class ListPricingAgreementNextRevisionAuditsQuery {
	rfpReqDate: string = undefined;
	period: number = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for updatePricingAgreementAuditHeader
 */
export class UpdatePricingAgreementAuditHeaderQuery {
	updateType: string = undefined;
}

/**
 * Query parameters for updatePricingAgreementAuditIssue
 */
export class UpdatePricingAgreementAuditIssueQuery {
	agreementAuditEventLogId: string = undefined;
}

/**
 * Query parameters for createPricingAgreementAuditDocument
 */
export class CreatePricingAgreementAuditDocumentQuery {
	agreementAuditCommentId: string = undefined;
	agreementAuditIssueId: string = undefined;
}

/**
 * Query parameters for getPricingAgreementAuditDocument
 */
export class GetPricingAgreementAuditDocumentQuery {
	agreementAuditCommentId: string = undefined;
	agreementAuditIssueId: string = undefined;
}

/**
 * Query parameters for listPricingAgreementAuditParameters
 */
export class ListPricingAgreementAuditParametersQuery {
	startEffDate: string = undefined;
	endEffDate: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPrcNavigationBarCounts
 */
export class ListPrcNavigationBarCountsQuery {
	ownerId: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingAgreementDocHdrs
 */
export class ListPricingAgreementDocHdrsQuery {
	custAcctId: number = undefined;
	custAcctCd: string = undefined;
	pricingAgreementId: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for getPricingAgreementDocument
 */
export class GetPricingAgreementDocumentQuery {
	docTypeId: number = undefined;
	oracleDocId: number = undefined;
}

/**
 * Query parameters for getPricingAgreementDocuments
 */
export class GetPricingAgreementDocumentsQuery {
	docTypeId: number[] = undefined;
}

/**
 * Query parameters for searchPricingAgreementDocCustomer
 */
export class SearchPricingAgreementDocCustomerQuery {
	custAcctId: number = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for getPricingAgreementDocDetails
 */
export class GetPricingAgreementDocDetailsQuery {
	agreementMonitorId: number = undefined;
}

/**
 * Query parameters for listPricingCustomerHierarchy
 */
export class ListPricingCustomerHierarchyQuery {
	custAcctId: number = undefined;
	custAcctCd: string = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingCustomerAccount
 */
export class ListPricingCustomerAccountQuery {
	acctInstId: string[] = undefined;
	acctName: string = undefined;
	addressLine1: string = undefined;
	cityName: string = undefined;
	stateCd: string = undefined;
	countryCd: string = undefined;
	postalCd: string = undefined;
	acctPartyRole: string[] = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}

/**
 * Query parameters for listPricingCustomerDetails
 */
export class ListPricingCustomerDetailsQuery {
	acctId: number[] = undefined;
	acctCd: string[] = undefined;
	levelOfDetail: string = undefined;
	numberOfRows: number = undefined;
	startAt: number = undefined;
	fields: string[] = undefined;
	sortByFieldName: string[] = undefined;
	sortOrder: string[] = undefined;
}


/**
 * Path parameters for getSalesRfp
 */
export class GetSalesRfpPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfp
 */
export class UpdateSalesRfpPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfpStatus
 */
export class UpdateSalesRfpStatusPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for validateSalesRfp
 */
export class ValidateSalesRfpPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfpAgreementDetails
 */
export class UpdateSalesRfpAgreementDetailsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfpAuditHeader
 */
export class UpdateSalesRfpAuditHeaderPath {
	salesRfpId: string = undefined;
	rfpAuditHeaderId: string = undefined;
}

/**
 * Path parameters for getSalesRfpAuditHeader
 */
export class GetSalesRfpAuditHeaderPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpAccts
 */
export class CreateSalesRfpAcctsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for listSalesRfpAccts
 */
export class ListSalesRfpAcctsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpLanes
 */
export class CreateSalesRfpLanesPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for listSalesRfpAccessorials
 */
export class ListSalesRfpAccessorialsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpAccessorials
 */
export class CreateSalesRfpAccessorialsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpAuditLog
 */
export class CreateSalesRfpAuditLogPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for listSalesRfpAuditLogs
 */
export class ListSalesRfpAuditLogsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpSupportingDocs
 */
export class CreateSalesRfpSupportingDocsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for deleteSalesRfpSupportingDocs
 */
export class DeleteSalesRfpSupportingDocsPath {
	salesRfpId: string = undefined;
	rfpSupportingDocId: string = undefined;
}

/**
 * Path parameters for getSalesRfpSupportingDoc
 */
export class GetSalesRfpSupportingDocPath {
	salesRfpId: string = undefined;
	rfpSupportingDocId: string = undefined;
}

/**
 * Path parameters for listSalesRfpSupportingDocs
 */
export class ListSalesRfpSupportingDocsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for getUnmappedSupportingDocument
 */
export class GetUnmappedSupportingDocumentPath {
	salesRfpId: string = undefined;
	rfpSupportingDocId: string = undefined;
}

/**
 * Path parameters for createSalesRfpShipmentSupportingDocs
 */
export class CreateSalesRfpShipmentSupportingDocsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for getSalesRfpSupportingDocFile
 */
export class GetSalesRfpSupportingDocFilePath {
	salesRfpId: string = undefined;
	rfpSupportingDocId: string = undefined;
}

/**
 * Path parameters for getSalesRfpSupportingDocsFile
 */
export class GetSalesRfpSupportingDocsFilePath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpComments
 */
export class CreateSalesRfpCommentsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfpComments
 */
export class UpdateSalesRfpCommentsPath {
	salesRfpId: string = undefined;
	commentId: string = undefined;
}

/**
 * Path parameters for listSalesRfpComments
 */
export class ListSalesRfpCommentsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for deleteSalesRfpComments
 */
export class DeleteSalesRfpCommentsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for listSalesRfpObiPlans
 */
export class ListSalesRfpObiPlansPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfpObiPlans
 */
export class UpdateSalesRfpObiPlansPath {
	salesRfpId: string = undefined;
	rfpObiPlanId: string = undefined;
}

/**
 * Path parameters for deleteSalesRfpObiPlans
 */
export class DeleteSalesRfpObiPlansPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpObiPlans
 */
export class CreateSalesRfpObiPlansPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for listSalesRfpGeoAreaPricingLanes
 */
export class ListSalesRfpGeoAreaPricingLanesPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfpGeoAreaPricingLanes
 */
export class UpdateSalesRfpGeoAreaPricingLanesPath {
	salesRfpId: string = undefined;
	geoAreaPrcLaneId: string = undefined;
}

/**
 * Path parameters for createSalesRfpGeoAreaPricingLanes
 */
export class CreateSalesRfpGeoAreaPricingLanesPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for deleteSalesRfpGeoAreaPricingLanes
 */
export class DeleteSalesRfpGeoAreaPricingLanesPath {
	salesRfpId: string = undefined;
	geoAreaPrcLaneId: string = undefined;
}

/**
 * Path parameters for listSalesRfpCommodities
 */
export class ListSalesRfpCommoditiesPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpCommodity
 */
export class CreateSalesRfpCommodityPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for getSalesRfpAgrmntPerformance
 */
export class GetSalesRfpAgrmntPerformancePath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpAgrmntPerformance
 */
export class CreateSalesRfpAgrmntPerformancePath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for deleteSalesRfpAgrmntPerformance
 */
export class DeleteSalesRfpAgrmntPerformancePath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpLadingDimensions
 */
export class CreateSalesRfpLadingDimensionsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for listSalesRfpLadingDimensions
 */
export class ListSalesRfpLadingDimensionsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpContactDetails
 */
export class CreateSalesRfpContactDetailsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for getSalesRfpContactDetails
 */
export class GetSalesRfpContactDetailsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfpContactDetails
 */
export class UpdateSalesRfpContactDetailsPath {
	salesRfpId: string = undefined;
	rfpContactId: string = undefined;
}

/**
 * Path parameters for listSalesRfpConstraints
 */
export class ListSalesRfpConstraintsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createSalesRfpConstraint
 */
export class CreateSalesRfpConstraintPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfpConstraint
 */
export class UpdateSalesRfpConstraintPath {
	salesRfpId: string = undefined;
	rfpConstraintId: string = undefined;
}

/**
 * Path parameters for deleteSalesRfpConstraint
 */
export class DeleteSalesRfpConstraintPath {
	salesRfpId: string = undefined;
	rfpConstraintId: string = undefined;
}

/**
 * Path parameters for createSalesRfpCustomerShipments
 */
export class CreateSalesRfpCustomerShipmentsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for deleteSalesRfpCustomerShipments
 */
export class DeleteSalesRfpCustomerShipmentsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for listSalesRfpCustomerShipments
 */
export class ListSalesRfpCustomerShipmentsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for buildInputHierarchyShipmentFile
 */
export class BuildInputHierarchyShipmentFilePath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for buildSalesRfpCustomerShipments
 */
export class BuildSalesRfpCustomerShipmentsPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for updateSalesRfpCustomerShipments
 */
export class UpdateSalesRfpCustomerShipmentsPath {
	salesRfpId: string = undefined;
	custShipmentFileId: string = undefined;
}

/**
 * Path parameters for getCrmAccount
 */
export class GetCrmAccountPath {
	accountId: string = undefined;
}

/**
 * Path parameters for getPricingProposalHeader
 */
export class GetPricingProposalHeaderPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingProposalHeader
 */
export class UpdatePricingProposalHeaderPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingProposal
 */
export class UpdatePricingProposalPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for getPricingProposal
 */
export class GetPricingProposalPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for calculatePricingProposalFaks
 */
export class CalculatePricingProposalFaksPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for createPricingAnlysGeoGroupHeader
 */
export class CreatePricingAnlysGeoGroupHeaderPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingAnlysGeoGroupHeader
 */
export class UpdatePricingAnlysGeoGroupHeaderPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for listPricingAnlysGeoGroupHeaders
 */
export class ListPricingAnlysGeoGroupHeadersPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for deletePricingAnlysGeoGroupHeader
 */
export class DeletePricingAnlysGeoGroupHeaderPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for updateGeoPricingGroupLanes
 */
export class UpdateGeoPricingGroupLanesPath {
	geoPricingGroupLaneId: string = undefined;
}

/**
 * Path parameters for deleteGeoPricingGroupLanes
 */
export class DeleteGeoPricingGroupLanesPath {
	geoPricingGroupLaneId: string = undefined;
}

/**
 * Path parameters for updateGeoPricingGroup
 */
export class UpdateGeoPricingGroupPath {
	geoPricingGroupId: string = undefined;
}

/**
 * Path parameters for deleteGeoPricingGroup
 */
export class DeleteGeoPricingGroupPath {
	geoPricingGroupId: string = undefined;
}

/**
 * Path parameters for createPricingAnalysisWorkFlow
 */
export class CreatePricingAnalysisWorkFlowPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingAnalysisWorkFlow
 */
export class UpdatePricingAnalysisWorkFlowPath {
	prcProposalInstId: string = undefined;
	analysisFlowInstId: string = undefined;
}

/**
 * Path parameters for listPricingAnalysisWorkflowProgress
 */
export class ListPricingAnalysisWorkflowProgressPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for getPricingExtractWorkflowStep
 */
export class GetPricingExtractWorkflowStepPath {
	prcExtractWorkflowId: string = undefined;
}

/**
 * Path parameters for deletePricingAnalysisWorkFlow
 */
export class DeletePricingAnalysisWorkFlowPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for getPricingAnalystDetails
 */
export class GetPricingAnalystDetailsPath {
	pricingAnalystEmplId: string = undefined;
}

/**
 * Path parameters for createPricingAnalysisShipments
 */
export class CreatePricingAnalysisShipmentsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingAnalysisShipments
 */
export class UpdatePricingAnalysisShipmentsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingAnalysisShipmentsFromCSV
 */
export class UpdatePricingAnalysisShipmentsFromCSVPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingAnalysisShipments
 */
export class ListPricingAnalysisShipmentsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for segmentPricingLeafShipments
 */
export class SegmentPricingLeafShipmentsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingLeafShipments
 */
export class ListPricingLeafShipmentsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingLeafShipment
 */
export class UpdatePricingLeafShipmentPath {
	prcProposalInstId: string = undefined;
	prcLeafShipmentId: string = undefined;
}

/**
 * Path parameters for bulkUpdatePricingLeafShipments
 */
export class BulkUpdatePricingLeafShipmentsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for deletePricingAnalysisShipments
 */
export class DeletePricingAnalysisShipmentsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for createPricingAnlysGeoGroupBIAmcs
 */
export class CreatePricingAnlysGeoGroupBIAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for updatePricingAnlysGeoGroupBIAmcs
 */
export class UpdatePricingAnlysGeoGroupBIAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
	geoGroupBiAmcId: string = undefined;
}

/**
 * Path parameters for listPricingAnlysGeoGroupBIAmcs
 */
export class ListPricingAnlysGeoGroupBIAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for deletePricingAnlysGeoGroupBIAmcs
 */
export class DeletePricingAnlysGeoGroupBIAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for createGeoPricingDiscountAmcSummary
 */
export class CreateGeoPricingDiscountAmcSummaryPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updateGeoPricingDiscountAmcSummary
 */
export class UpdateGeoPricingDiscountAmcSummaryPath {
	prcProposalInstId: string = undefined;
	geoDiscAmcSummaryId: string = undefined;
}

/**
 * Path parameters for listGeoPricingDiscountAmcSummary
 */
export class ListGeoPricingDiscountAmcSummaryPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for deleteGeoPricingDiscountAmcSummary
 */
export class DeleteGeoPricingDiscountAmcSummaryPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for createPricingAnlysGeoGroupBiTariffs
 */
export class CreatePricingAnlysGeoGroupBiTariffsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for listPricingAnlysGeoGroupBiTariffs
 */
export class ListPricingAnlysGeoGroupBiTariffsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for updatePricingAnlysGeoGroupBiTariff
 */
export class UpdatePricingAnlysGeoGroupBiTariffPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
	geoGroupBiTariffId: string = undefined;
}

/**
 * Path parameters for deletePricingAnlysGeoGroupBiTariff
 */
export class DeletePricingAnlysGeoGroupBiTariffPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for createPricingAnlysGeoGroupCmTariffs
 */
export class CreatePricingAnlysGeoGroupCmTariffsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for listPricingAnlysGeoGroupCmTariffs
 */
export class ListPricingAnlysGeoGroupCmTariffsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for updatePricingAnlysGeoGroupCmTariff
 */
export class UpdatePricingAnlysGeoGroupCmTariffPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
	geoGroupCmTariffId: string = undefined;
}

/**
 * Path parameters for deletePricingAnlysGeoGroupCmTariff
 */
export class DeletePricingAnlysGeoGroupCmTariffPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for createPricingAnlysGeoGroupCmAmcs
 */
export class CreatePricingAnlysGeoGroupCmAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for listPricingAnlysGeoGroupCmAmcs
 */
export class ListPricingAnlysGeoGroupCmAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for updatePricingAnlysGeoGroupCmAmcs
 */
export class UpdatePricingAnlysGeoGroupCmAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
	geoGroupCmAmcId: string = undefined;
}

/**
 * Path parameters for deletePricingAnlysGeoGroupCmAmcs
 */
export class DeletePricingAnlysGeoGroupCmAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for createPricingProposalAccessorials
 */
export class CreatePricingProposalAccessorialsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingProposalAccessorials
 */
export class ListPricingProposalAccessorialsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for deletePricingProposalAccessorials
 */
export class DeletePricingProposalAccessorialsPath {
	prcProposalInstId: string = undefined;
	prcProposalAccsrlId: string = undefined;
}

/**
 * Path parameters for createPricingProposalGeoPricingLanes
 */
export class CreatePricingProposalGeoPricingLanesPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingProposalGeoPricingLanes
 */
export class ListPricingProposalGeoPricingLanesPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingProposalGeoPricingLanes
 */
export class UpdatePricingProposalGeoPricingLanesPath {
	prcProposalInstId: string = undefined;
	geoAreaPrcLaneId: string = undefined;
}

/**
 * Path parameters for deletePricingProposalGeoPricingLanes
 */
export class DeletePricingProposalGeoPricingLanesPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for createGeoPricingLaneAmcs
 */
export class CreateGeoPricingLaneAmcsPath {
	prcProposalInstId: string = undefined;
	geoPricingLaneId: string = undefined;
}

/**
 * Path parameters for listGeoPricingLaneAmcs
 */
export class ListGeoPricingLaneAmcsPath {
	prcProposalInstId: string = undefined;
	geoPricingLaneId: string = undefined;
}

/**
 * Path parameters for updateGeoPricingLaneAmcs
 */
export class UpdateGeoPricingLaneAmcsPath {
	prcProposalInstId: string = undefined;
	geoPricingLaneId: string = undefined;
	geoPricingLaneAmcId: string = undefined;
}

/**
 * Path parameters for deleteGeoPricingLaneAmcs
 */
export class DeleteGeoPricingLaneAmcsPath {
	prcProposalInstId: string = undefined;
	geoPricingLaneId: string = undefined;
}

/**
 * Path parameters for createGeoPricingLaneDiscounts
 */
export class CreateGeoPricingLaneDiscountsPath {
	prcProposalInstId: string = undefined;
	geoPricingLaneId: string = undefined;
}

/**
 * Path parameters for listGeoPricingLaneDiscounts
 */
export class ListGeoPricingLaneDiscountsPath {
	prcProposalInstId: string = undefined;
	geoPricingLaneId: string = undefined;
}

/**
 * Path parameters for updateGeoPricingLaneDiscounts
 */
export class UpdateGeoPricingLaneDiscountsPath {
	prcProposalInstId: string = undefined;
	geoPricingLaneId: string = undefined;
	geoPricingLaneDiscountId: string = undefined;
}

/**
 * Path parameters for deleteGeoPricingLaneDiscounts
 */
export class DeleteGeoPricingLaneDiscountsPath {
	prcProposalInstId: string = undefined;
	geoPricingLaneId: string = undefined;
}

/**
 * Path parameters for createPricingAnalysisShipmentAccessorials
 */
export class CreatePricingAnalysisShipmentAccessorialsPath {
	prcProposalInstId: string = undefined;
	prcAnlysShipmentId: string = undefined;
}

/**
 * Path parameters for updatePricingAnalysisShipmentAccessorials
 */
export class UpdatePricingAnalysisShipmentAccessorialsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingAnalysisShipmentAccessorials
 */
export class ListPricingAnalysisShipmentAccessorialsPath {
	prcProposalInstId: string = undefined;
	prcAnlysShipmentId: string = undefined;
}

/**
 * Path parameters for deletePricingAnalysisShipmentAccessorials
 */
export class DeletePricingAnalysisShipmentAccessorialsPath {
	prcProposalInstId: string = undefined;
	prcAnlysShipmentId: string = undefined;
}

/**
 * Path parameters for updatePricingAnalystAssignments
 */
export class UpdatePricingAnalystAssignmentsPath {
	analystAssignmentId: string = undefined;
}

/**
 * Path parameters for deletePricingAnalystAssignment
 */
export class DeletePricingAnalystAssignmentPath {
	analystAssignmentId: string = undefined;
}

/**
 * Path parameters for createPricingAnlysGeoGroupDiscAmcs
 */
export class CreatePricingAnlysGeoGroupDiscAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for listPricingAnlysGeoGroupDiscAmcs
 */
export class ListPricingAnlysGeoGroupDiscAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for updatePricingAnlysGeoGroupDiscAmcs
 */
export class UpdatePricingAnlysGeoGroupDiscAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
	geoGroupDiscAmcId: string = undefined;
}

/**
 * Path parameters for deletePricingAnlysGeoGroupDiscAmcs
 */
export class DeletePricingAnlysGeoGroupDiscAmcsPath {
	prcProposalInstId: string = undefined;
	prcAnalysGeoGroupId: string = undefined;
}

/**
 * Path parameters for createPricingProposalCustHierarchy
 */
export class CreatePricingProposalCustHierarchyPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingProposalCustHierarchy
 */
export class ListPricingProposalCustHierarchyPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingProposalCustHierarchy
 */
export class UpdatePricingProposalCustHierarchyPath {
	prcProposalInstId: string = undefined;
	prcProposalCustHierarchyId: string = undefined;
}

/**
 * Path parameters for updatePricingProposalCustHierarchies
 */
export class UpdatePricingProposalCustHierarchiesPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for deletePricingProposalCustHierarchy
 */
export class DeletePricingProposalCustHierarchyPath {
	prcProposalInstId: string = undefined;
	prcProposalCustHierarchyId: string = undefined;
}

/**
 * Path parameters for calculatePricingGroupSummaryShipments
 */
export class CalculatePricingGroupSummaryShipmentsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for calculatePricingGroupShipmentAmc
 */
export class CalculatePricingGroupShipmentAmcPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for calculatePricingGroupShipmentAccsrl
 */
export class CalculatePricingGroupShipmentAccsrlPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for createGeoPricingLaneFaks
 */
export class CreateGeoPricingLaneFaksPath {
	prcProposalInstId: string = undefined;
	geoPrcLaneId: string = undefined;
}

/**
 * Path parameters for getGeoPricingLaneFak
 */
export class GetGeoPricingLaneFakPath {
	prcProposalInstId: string = undefined;
	geoPrcLaneId: string = undefined;
}

/**
 * Path parameters for updateGeoPricingLaneFak
 */
export class UpdateGeoPricingLaneFakPath {
	prcProposalInstId: string = undefined;
	geoPrcLaneId: string = undefined;
	pricingProposalFakId: string = undefined;
}

/**
 * Path parameters for deleteGeoPricingLaneFaks
 */
export class DeleteGeoPricingLaneFaksPath {
	prcProposalInstId: string = undefined;
	geoPrcLaneId: string = undefined;
}

/**
 * Path parameters for deleteGeoPricingLaneFakOverrides
 */
export class DeleteGeoPricingLaneFakOverridesPath {
	prcProposalInstId: string = undefined;
	geoPrcLaneId: string = undefined;
	prcFakHdrId: string = undefined;
}

/**
 * Path parameters for createPricingProposalRulesetMatrix
 */
export class CreatePricingProposalRulesetMatrixPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingProposalRulesetMatrix
 */
export class ListPricingProposalRulesetMatrixPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingProposalRulesetMatrix
 */
export class UpdatePricingProposalRulesetMatrixPath {
	prcProposalInstId: string = undefined;
	prcProposalRulesetMatrixId: string = undefined;
}

/**
 * Path parameters for deletePricingProposalRulesetMatrix
 */
export class DeletePricingProposalRulesetMatrixPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for createPricingProposalTmsFile
 */
export class CreatePricingProposalTmsFilePath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingProposalTmsFile
 */
export class ListPricingProposalTmsFilePath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingProposalTmsFile
 */
export class UpdatePricingProposalTmsFilePath {
	prcProposalInstId: string = undefined;
	prcProposalTmsFileId: string = undefined;
}

/**
 * Path parameters for deletePricingProposalTmsFile
 */
export class DeletePricingProposalTmsFilePath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listAggregateModelConstraintTemplates
 */
export class ListAggregateModelConstraintTemplatesPath {
	prcProposalInstId: string = undefined;
	aggrModelTemplateId: string = undefined;
}

/**
 * Path parameters for createPricingProposalAggrModelGeoStagings
 */
export class CreatePricingProposalAggrModelGeoStagingsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingProposalAggrModelGeoStagings
 */
export class ListPricingProposalAggrModelGeoStagingsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for updatePricingProposalAggrModelGeoStagings
 */
export class UpdatePricingProposalAggrModelGeoStagingsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listGeoPricingGroupsForProposal
 */
export class ListGeoPricingGroupsForProposalPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listGeoPricingGroupLanesForProposal
 */
export class ListGeoPricingGroupLanesForProposalPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listGeoPricingGroupMembersForProposal
 */
export class ListGeoPricingGroupMembersForProposalPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listGeoPricingGroupExclMembersForProposal
 */
export class ListGeoPricingGroupExclMembersForProposalPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for listPricingProposalAmcDiscounts
 */
export class ListPricingProposalAmcDiscountsPath {
	prcProposalInstId: string = undefined;
}

/**
 * Path parameters for deletePricingCode
 */
export class DeletePricingCodePath {
	pricingCodeId: string = undefined;
}

/**
 * Path parameters for updatePricingCode
 */
export class UpdatePricingCodePath {
	pricingCodeId: string = undefined;
}

/**
 * Path parameters for createSalesRfpAcctsRuleset
 */
export class CreateSalesRfpAcctsRulesetPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for listSalesRfpAcctsRuleset
 */
export class ListSalesRfpAcctsRulesetPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for deleteSalesRfpAcctsRuleset
 */
export class DeleteSalesRfpAcctsRulesetPath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for createPricingAgreementExpectation
 */
export class CreatePricingAgreementExpectationPath {
	agreementId: string = undefined;
}

/**
 * Path parameters for listPricingAgreementExpectations
 */
export class ListPricingAgreementExpectationsPath {
	agreementId: string = undefined;
}

/**
 * Path parameters for getPricingAgreementExpectation
 */
export class GetPricingAgreementExpectationPath {
	agreementId: string = undefined;
	agreementExpectationId: string = undefined;
}

/**
 * Path parameters for listPricingAgreementShipmentSumm
 */
export class ListPricingAgreementShipmentSummPath {
	agreementId: string = undefined;
}

/**
 * Path parameters for updatePricingAgreement
 */
export class UpdatePricingAgreementPath {
	agreementId: string = undefined;
}

/**
 * Path parameters for getClassRatesByCustomerId
 */
export class GetClassRatesByCustomerIdPath {
	acctInstId: string = undefined;
	partyTypeCode: string = undefined;
}

/**
 * Path parameters for getClassRatesByPricingAgrmtAndRuleset
 */
export class GetClassRatesByPricingAgrmtAndRulesetPath {
	agreementId: string = undefined;
	rulesetNbr: number = undefined;
}

/**
 * Path parameters for getSalesRfpReportFile
 */
export class GetSalesRfpReportFilePath {
	salesRfpId: string = undefined;
}

/**
 * Path parameters for deletePricingAdminProposedStrategy
 */
export class DeletePricingAdminProposedStrategyPath {
	adminProposedStrategyPctId: string = undefined;
}

/**
 * Path parameters for updatePricingAdminProposedStrategy
 */
export class UpdatePricingAdminProposedStrategyPath {
	adminProposedStrategyPctId: string = undefined;
}

/**
 * Path parameters for deletePricingAdminProposedIncreaseLevel
 */
export class DeletePricingAdminProposedIncreaseLevelPath {
	adminProposedIncrLevelId: string = undefined;
}

/**
 * Path parameters for updatePricingAdminProposedIncreaseLevel
 */
export class UpdatePricingAdminProposedIncreaseLevelPath {
	adminProposedIncrLevelId: string = undefined;
}

/**
 * Path parameters for deletePricingAdminFakAdjustPct
 */
export class DeletePricingAdminFakAdjustPctPath {
	adminFakAdjustPctId: string = undefined;
}

/**
 * Path parameters for updatePricingAdminFakAdjustPct
 */
export class UpdatePricingAdminFakAdjustPctPath {
	adminFakAdjustPctId: string = undefined;
}

/**
 * Path parameters for deletePricingAdminFscPctLookup
 */
export class DeletePricingAdminFscPctLookupPath {
	adminFscPctLookupId: string = undefined;
}

/**
 * Path parameters for updatePricingAdminFscPctLookup
 */
export class UpdatePricingAdminFscPctLookupPath {
	adminFscPctLookupId: string = undefined;
}

/**
 * Path parameters for getUserPreference
 */
export class GetUserPreferencePath {
	employeeId: string = undefined;
	appKey: string = undefined;
}

/**
 * Path parameters for updateUserPreference
 */
export class UpdateUserPreferencePath {
	employeeId: string = undefined;
	appKey: string = undefined;
}

/**
 * Path parameters for getPricingAgreementMonitor
 */
export class GetPricingAgreementMonitorPath {
	pricingAgreementMonitorId: string = undefined;
}

/**
 * Path parameters for updatePricingAgreementMonitor
 */
export class UpdatePricingAgreementMonitorPath {
	pricingAgreementMonitorId: string = undefined;
}

/**
 * Path parameters for getPricingAgreementMonitorParameter
 */
export class GetPricingAgreementMonitorParameterPath {
	agreementMonitorParameterId: string = undefined;
}

/**
 * Path parameters for updatePricingAgreementMonitorParameter
 */
export class UpdatePricingAgreementMonitorParameterPath {
	agreementMonitorParameterId: string = undefined;
}

/**
 * Path parameters for deletePricingAgreementMonitorParameter
 */
export class DeletePricingAgreementMonitorParameterPath {
	agreementMonitorParameterId: string = undefined;
}

/**
 * Path parameters for createPricingAgreementMonitorAction
 */
export class CreatePricingAgreementMonitorActionPath {
	pricingAgreementMonitorId: string = undefined;
}

/**
 * Path parameters for getPricingAgreementMonitorRegionRollupReport
 */
export class GetPricingAgreementMonitorRegionRollupReportPath {
	requesterId: string = undefined;
}

/**
 * Path parameters for listPricingAgreementMonitorValidActions
 */
export class ListPricingAgreementMonitorValidActionsPath {
	agreementMonitorId: string = undefined;
}

/**
 * Path parameters for listPricingAgreementRecentAudits
 */
export class ListPricingAgreementRecentAuditsPath {
	loginUserId: string = undefined;
}

/**
 * Path parameters for listPricingAgreementNextRevisionAudits
 */
export class ListPricingAgreementNextRevisionAuditsPath {
	leadAcctId: number = undefined;
}

/**
 * Path parameters for getPricingAgreementAuditDetail
 */
export class GetPricingAgreementAuditDetailPath {
	agreementAuditHdrId: string = undefined;
}

/**
 * Path parameters for createPricingAgreementAuditComment
 */
export class CreatePricingAgreementAuditCommentPath {
	agreementAuditHdrId: string = undefined;
}

/**
 * Path parameters for createPricingAgreementAuditIssue
 */
export class CreatePricingAgreementAuditIssuePath {
	agreementAuditHdrId: string = undefined;
}

/**
 * Path parameters for updatePricingAgreementAuditIssue
 */
export class UpdatePricingAgreementAuditIssuePath {
	agreementAuditHdrId: string = undefined;
	agreementAuditIssueId: string = undefined;
}

/**
 * Path parameters for deletePricingAgreementAuditIssue
 */
export class DeletePricingAgreementAuditIssuePath {
	agreementAuditHdrId: string = undefined;
	agreementAuditIssueId: string = undefined;
}

/**
 * Path parameters for deletePricingAgreementAuditEventLog
 */
export class DeletePricingAgreementAuditEventLogPath {
	agreementAuditEventLogId: string = undefined;
}

/**
 * Path parameters for createPricingAgreementAuditDocument
 */
export class CreatePricingAgreementAuditDocumentPath {
	agreementAuditHdrId: string = undefined;
}

/**
 * Path parameters for getPricingAgreementAuditDocument
 */
export class GetPricingAgreementAuditDocumentPath {
	agreementAuditHdrId: string = undefined;
}

/**
 * Path parameters for deletePricingAgreementAuditParameter
 */
export class DeletePricingAgreementAuditParameterPath {
	agreementAuditParameterId: string = undefined;
}

/**
 * Path parameters for getPricingAgreementAuditParameter
 */
export class GetPricingAgreementAuditParameterPath {
	agreementAuditParameterId: string = undefined;
}

/**
 * Path parameters for createPricingAgreementDocument
 */
export class CreatePricingAgreementDocumentPath {
	agreementDocHdrId: number = undefined;
}

/**
 * Path parameters for getPricingAgreementDocument
 */
export class GetPricingAgreementDocumentPath {
	agreementDocHdrId: number = undefined;
}

/**
 * Path parameters for getPricingAgreementDocuments
 */
export class GetPricingAgreementDocumentsPath {
	agreementDocHdrId: number = undefined;
}

/**
 * Path parameters for getPricingAgreementDocDetails
 */
export class GetPricingAgreementDocDetailsPath {
	agreementDocHdrId: number = undefined;
}

/**
 * Path parameters for deletePricingAgreementDocument
 */
export class DeletePricingAgreementDocumentPath {
	agreementDocHdrId: number = undefined;
	docTypeId: number = undefined;
}

/**
 * Path parameters for deletePricingAgreementDocHeader
 */
export class DeletePricingAgreementDocHeaderPath {
	agreementDocHdrId: number = undefined;
}

